import { useEffect, useMemo, useState } from "react";

const APP_HOSTNAME = typeof window !== "undefined" ? window.location.hostname : "localhost";
const IS_LOCALHOST = APP_HOSTNAME === "localhost" || APP_HOSTNAME === "127.0.0.1";
const MARKETING_BASE_URL = IS_LOCALHOST ? "" : "https://getclipse.com";
const APP_BASE_URL = IS_LOCALHOST ? "" : "https://app.getclipse.com";
const TWITCH_PROXY = process.env.REACT_APP_TWITCH_PROXY_URL || "http://localhost:3001/api/twitch";
const BACKEND = process.env.REACT_APP_BACKEND_URL || "http://localhost:4100";

function marketingHref(path = "/") {
  return `${MARKETING_BASE_URL}${path}`;
}

function appHref(path = "/") {
  return `${APP_BASE_URL}${path}`;
}

const PLATFORMS = {
  tiktok: { label: "TikTok", color: "#ff5b79", icon: "T", priority: 1 },
  instagram: { label: "Instagram", color: "#f472b6", icon: "I", priority: 2 },
  youtube: { label: "YouTube", color: "#ef4444", icon: "Y", priority: 3 },
  twitch: { label: "Twitch", color: "#8b5cf6", icon: "Tw", priority: 4 },
};

function platformPriority(platform) {
  return PLATFORMS[platform]?.priority || 99;
}

const DEFAULT_STREAMERS = ["xQc", "pokimane", "shroud", "valkyrae", "hasanabi", "ludwig", "ninja", "timthetatman"];

const THEME = {
  bg: "#070711",
  panel: "rgba(18,18,31,0.86)",
  panelStrong: "rgba(24,24,39,0.92)",
  panelSoft: "rgba(255,255,255,0.045)",
  line: "rgba(255,255,255,0.1)",
  lineStrong: "rgba(139,92,246,0.48)",
  text: "#f8f7ff",
  muted: "#a29caf",
  dim: "#686174",
  purple: "#8b5cf6",
  purpleDeep: "#6d28d9",
  purpleSoft: "rgba(139,92,246,0.16)",
  green: "#2ee88b",
  danger: "#ff5b79",
  warning: "#facc15",
};

const BRAND_FULL_SRC = "/clipse-logo.png";
const BRAND_MARK_SRC = "/clipse-mark.png";

function buildAuthHandoffHash(accessToken, refreshToken) {
  if (!accessToken || !refreshToken) return "";
  const params = new URLSearchParams({
    accessToken,
    refreshToken,
  });
  return `#auth-handoff?${params.toString()}`;
}

function consumeAuthHandoffFromHash() {
  if (typeof window === "undefined") return false;
  const hash = window.location.hash || "";
  if (!hash.startsWith("#auth-handoff?")) return false;
  const params = new URLSearchParams(hash.slice("#auth-handoff?".length));
  const accessToken = params.get("accessToken") || "";
  const refreshToken = params.get("refreshToken") || "";
  if (accessToken) {
    localStorage.setItem("clipse-backend-token", accessToken);
    localStorage.removeItem("cliphive-backend-token");
  }
  if (refreshToken) {
    localStorage.setItem("clipse-backend-refresh-token", refreshToken);
    localStorage.removeItem("cliphive-backend-refresh-token");
  }
  window.history.replaceState({}, "", `${window.location.pathname}${window.location.search}`);
  return Boolean(accessToken);
}

function BrandLockup({ href, markSize = 38, textSize = 20, gap = 12, variant = "mark", style = {} }) {
  const content = variant === "full" ? (
    <img
      src={BRAND_FULL_SRC}
      alt="Clipse"
      style={{
        width: Math.round(markSize * 5.25),
        height: Math.round(markSize * 1.92),
        objectFit: "contain",
        borderRadius: Math.max(10, Math.round(markSize * 0.3)),
        boxShadow: "0 16px 42px rgba(168,85,247,0.22)",
        display: "block",
      }}
    />
  ) : (
    <>
      <img
        src={BRAND_MARK_SRC}
        alt=""
        aria-hidden="true"
        style={{
          width: markSize,
          height: markSize,
          borderRadius: Math.max(10, Math.round(markSize * 0.34)),
          objectFit: "cover",
          boxShadow: "0 16px 42px rgba(168,85,247,0.34)",
          flex: "0 0 auto",
        }}
      />
      <span style={{ color: "#fff", fontWeight: 950, letterSpacing: 2, fontSize: textSize, lineHeight: 1 }}>CLIPSE</span>
    </>
  );
  const baseStyle = { display: "flex", alignItems: "center", gap, textDecoration: "none", ...style };
  return href ? <a href={href} style={baseStyle}>{content}</a> : <div style={baseStyle}>{content}</div>;
}

const PUBLIC_PLANS = {
  free: {
    key: "free",
    label: "Free",
    priceMonthlyUsd: 0,
    monthlyUploadJobs: 10,
    postingAccounts: 1,
    tagline: "Perfect for trying Clipse with a real posting workflow.",
    features: ["1 account", "10 clips per month", "Basic scheduling", "Clipse watermark"],
    analytics: false,
    aiCaptions: false,
    autoMode: false,
  },
  starter: {
    key: "starter",
    label: "Starter",
    priceMonthlyUsd: 19,
    monthlyUploadJobs: 100,
    postingAccounts: 5,
    tagline: "Everything you need to grow your content and save time.",
    features: ["5 accounts", "100 clips per month", "Scheduling", "AI captions + hashtags"],
    analytics: false,
    aiCaptions: true,
    autoMode: false,
    featured: true,
  },
  pro: {
    key: "pro",
    label: "Pro",
    priceMonthlyUsd: 49,
    monthlyUploadJobs: 500,
    postingAccounts: 15,
    tagline: "Advanced automation for creators and growing teams.",
    features: ["15 accounts", "500 clips per month", "Auto-posting", "Advanced analytics", "No watermark"],
    analytics: true,
    aiCaptions: true,
    autoMode: true,
  },
};

const NAV_ICONS = {
  home: "⌂",
  clips: "▣",
  ops: "▣",
  accounts: "◎",
  queue: "◴",
  portal: "◌",
  admin: "⚙",
  analytics: "⌁",
  templates: "▧",
  settings: "⚙",
};

async function twitchGet(path) {
  const res = await fetch(`${TWITCH_PROXY}${path}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
  return data;
}

async function backendFetch(path, options = {}, token) {
  const headers = { ...(options.headers || {}) };
  if (!(options.body instanceof FormData)) headers["Content-Type"] = headers["Content-Type"] || "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${BACKEND}${path}`, { ...options, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(data.error || data.message || `HTTP ${res.status}`);
    error.status = res.status;
    Object.assign(error, data);
    throw error;
  }
  return data;
}

function isAuthTokenError(error) {
  return error?.status === 401 || error?.message === "Invalid token" || error?.message === "Missing bearer token" || error?.code === "TOKEN_INVALID";
}

async function getUser(login) {
  const data = await twitchGet(`/users?login=${encodeURIComponent(login)}`);
  return data.data?.[0] || null;
}

const CLIP_TIME_RANGES = [
  { key: "1h", label: "1H", description: "Fresh clips", hours: 1 },
  { key: "24h", label: "Day", description: "Last 24 hours", hours: 24 },
  { key: "7d", label: "Week", description: "Last 7 days", hours: 24 * 7 },
  { key: "30d", label: "Month", description: "Last 30 days", hours: 24 * 30 },
  { key: "all", label: "All time", description: "Top available clips", hours: null },
];

function clipTimeRangeQuery(rangeKey) {
  const range = CLIP_TIME_RANGES.find((entry) => entry.key === rangeKey);
  if (!range?.hours) return "";
  return `&started_at=${encodeURIComponent(new Date(Date.now() - range.hours * 60 * 60 * 1000).toISOString())}`;
}

async function getTopClips(broadcasterId, limit = 9, rangeKey = "7d") {
  const data = await twitchGet(`/clips?broadcaster_id=${broadcasterId}&first=${limit}${clipTimeRangeQuery(rangeKey)}`);
  return data.data || [];
}

function fmtViews(n) {
  const value = Number(n || 0);
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return String(value);
}

function fmtPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function fmtDur(secs) {
  const m = Math.floor((secs || 0) / 60);
  const s = (secs || 0) % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function viralScore(clip) {
  const viewScore = Math.min((clip.view_count || clip.viewCount || 0) / 5000, 50);
  const duration = clip.duration || clip.duration_sec || 0;
  const durBonus = duration >= 10 && duration <= 45 ? 20 : 5;
  return Math.min(99, Math.round(45 + viewScore + durBonus + ((clip.view_count || 0) % 9)));
}

function usagePercent(current, limit) {
  if (!limit) return 0;
  return Math.min(100, Math.round((Number(current || 0) / Number(limit)) * 100));
}

function statusText(status) {
  return String(status || "queued").replace(/_/g, " ").toUpperCase();
}

function queueReceiptTone(item) {
  const status = item?.receipt_status || item?.job_status || item?.upload_status || "queued";
  if (status === "failed") return { color: "#ef444418", text: "#ef4444", border: "#ef444433" };
  if (status === "posted") return { color: "#4ade8018", text: "#4ade80", border: "#4ade8040" };
  if (status === "ready_for_review") return { color: "#facc1518", text: "#facc15", border: "#facc1540" };
  if (status === "posting" || status === "submitted" || status === "running") return { color: "#38bdf818", text: "#38bdf8", border: "#38bdf840" };
  return { color: "#7c3aed18", text: "#a78bfa", border: "#7c3aed40" };
}

function queueReceiptLabel(item) {
  return item?.receipt_label || statusText(item?.job_status || item?.upload_status);
}

function queueReceiptDetail(item) {
  if (item?.receipt_detail) return item.receipt_detail;
  if (item?.last_error_message) return item.last_error_message;
  return "Clipse is tracking this upload through the queue.";
}

function shortId(value) {
  return value ? String(value).slice(0, 8) : null;
}

function formatDateTime(value) {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleString();
}

function titleCaseName(value) {
  return String(value || "")
    .replace(/[_.-]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function userDisplayName(user) {
  const directName = user?.display_name || user?.displayName || user?.name || user?.company_name || user?.companyName;
  if (directName) return titleCaseName(directName);
  const emailName = user?.email?.split("@")?.[0];
  return titleCaseName(emailName) || "Admin";
}

function userInitials(user) {
  const name = userDisplayName(user);
  const parts = name.split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : name.slice(0, 2)).toUpperCase();
}

function roleLabel(role) {
  return titleCaseName(String(role || "Admin").replace(/_/g, " "));
}

function localDateTimeToIso(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function Tag({ children, color = "#ffffff10", text = "#666" }) {
  return (
    <span style={{
      background: color,
      color: text,
      borderRadius: 999,
      padding: "3px 10px",
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 0.6,
      whiteSpace: "nowrap",
      display: "inline-flex",
      alignItems: "center",
    }}>
      {children}
    </span>
  );
}

function Spinner({ size = 18, color = "#8b5cf6" }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        display: "inline-block",
        borderRadius: "50%",
        border: `2px solid ${color}33`,
        borderTopColor: color,
        animation: "spin 0.7s linear infinite",
      }}
    />
  );
}

function LiveDot() {
  return (
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: "#ef4444",
        display: "inline-block",
        boxShadow: "0 0 0 0 #ef444488",
        animation: "livepulse 1.5s ease-out infinite",
      }}
    />
  );
}

function Panel({ children, style = {}, className }) {
  return (
    <div
      className={className}
      style={{
        background: THEME.panel,
        border: `1px solid ${THEME.line}`,
        borderRadius: 22,
        boxShadow: "0 18px 70px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.04)",
        backdropFilter: "blur(18px)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 12, color: THEME.muted, marginBottom: 8, fontWeight: 700 }}>{label}</div>
      {children}
    </label>
  );
}

function TextInput(props) {
  return (
    <input
      {...props}
      style={{
        width: "100%",
        background: "rgba(7,7,17,0.74)",
        border: `1px solid ${THEME.line}`,
        borderRadius: 12,
        color: THEME.text,
        padding: "12px 14px",
        fontSize: 14,
        fontFamily: "inherit",
        outline: "none",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
        ...(props.style || {}),
      }}
    />
  );
}

function TextArea(props) {
  return (
    <textarea
      {...props}
      style={{
        width: "100%",
        background: "rgba(7,7,17,0.74)",
        border: `1px solid ${THEME.line}`,
        borderRadius: 12,
        color: THEME.text,
        padding: "12px 14px",
        fontSize: 14,
        fontFamily: "inherit",
        outline: "none",
        resize: "vertical",
        ...(props.style || {}),
      }}
    />
  );
}

function Select(props) {
  return (
    <select
      {...props}
      style={{
        width: "100%",
        background: "rgba(7,7,17,0.74)",
        border: `1px solid ${THEME.line}`,
        borderRadius: 12,
        color: THEME.text,
        padding: "12px 14px",
        fontSize: 14,
        fontFamily: "inherit",
        outline: "none",
        ...(props.style || {}),
      }}
    />
  );
}

function ToggleSwitch({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange?.(!checked)}
      disabled={disabled}
      style={{
        width: 46,
        height: 24,
        borderRadius: 999,
        border: `1px solid ${checked ? "rgba(139,92,246,0.8)" : THEME.line}`,
        background: checked ? "linear-gradient(135deg,#8b5cf6,#6d28d9)" : "rgba(255,255,255,0.1)",
        cursor: disabled ? "not-allowed" : "pointer",
        padding: 2,
        transition: "all 0.18s ease",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <span
        style={{
          display: "block",
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#fff",
          transform: checked ? "translateX(20px)" : "translateX(0)",
          transition: "transform 0.18s ease",
          boxShadow: "0 6px 16px rgba(0,0,0,0.35)",
        }}
      />
    </button>
  );
}

function PrimaryButton({ children, style, ...props }) {
  return (
    <button
      {...props}
      style={{
        background: "linear-gradient(135deg,#9f5cff,#6d28d9)",
        border: "none",
        borderRadius: 12,
        color: "#fff",
        padding: "13px 16px",
        fontSize: 14,
        fontWeight: 800,
        letterSpacing: 0.1,
        cursor: "pointer",
        boxShadow: "0 16px 34px rgba(109,40,217,0.28)",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function GhostButton({ children, style, ...props }) {
  return (
    <button
      {...props}
      style={{
        background: "rgba(255,255,255,0.055)",
        border: `1px solid ${THEME.line}`,
        borderRadius: 12,
        color: "#ddd6fe",
        padding: "11px 14px",
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: 0,
        cursor: "pointer",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function EmptyState({ eyebrow = "EMPTY", title, body, primaryLabel, onPrimary, secondaryLabel, onSecondary }) {
  return (
    <Panel style={{ padding: "54px 22px", textAlign: "center", background: "radial-gradient(circle at 50% 0%,rgba(139,92,246,0.14),transparent 42%),rgba(255,255,255,0.035)" }}>
      <div style={{ width: 54, height: 54, borderRadius: 18, margin: "0 auto 16px", display: "grid", placeItems: "center", color: THEME.purple, background: THEME.purpleSoft, border: `1px solid ${THEME.lineStrong}`, fontSize: 24 }}>⌁</div>
      <div style={{ color: THEME.purple, fontSize: 11, letterSpacing: 2.5, fontWeight: 900, marginBottom: 8 }}>{eyebrow}</div>
      <div style={{ color: "#fff", fontWeight: 950, fontSize: 22, marginBottom: 8 }}>{title}</div>
      <div style={{ color: THEME.muted, maxWidth: 560, margin: "0 auto", lineHeight: 1.65 }}>{body}</div>
      {(primaryLabel || secondaryLabel) ? (
        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginTop: 20 }}>
          {primaryLabel ? <PrimaryButton onClick={onPrimary}>{primaryLabel}</PrimaryButton> : null}
          {secondaryLabel ? <GhostButton onClick={onSecondary}>{secondaryLabel}</GhostButton> : null}
        </div>
      ) : null}
    </Panel>
  );
}

function OnboardingChecklist({ steps }) {
  const completed = steps.filter((step) => step.done).length;
  const progress = Math.round((completed / steps.length) * 100);
  const nextStep = steps.find((step) => !step.done);
  return (
    <Panel style={{ padding: 22, background: "linear-gradient(135deg,rgba(139,92,246,0.16),rgba(255,255,255,0.04))" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 18, alignItems: "flex-start", marginBottom: 18, flexWrap: "wrap" }}>
        <div>
          <div style={{ color: "#fff", fontSize: 22, fontWeight: 950, marginBottom: 6 }}>Get your first clip posted</div>
          <div style={{ color: THEME.muted, lineHeight: 1.6 }}>Follow these steps to turn an empty account into a working posting pipeline.</div>
        </div>
        <div style={{ minWidth: 180 }}>
          <div style={{ display: "flex", justifyContent: "space-between", color: THEME.muted, fontSize: 12, fontWeight: 800, marginBottom: 8 }}>
            <span>{completed}/{steps.length} complete</span>
            <span>{progress}%</span>
          </div>
          <div style={{ height: 9, borderRadius: 999, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
            <div style={{ width: `${progress}%`, height: "100%", background: "linear-gradient(90deg,#8b5cf6,#2ee88b)" }} />
          </div>
        </div>
      </div>
      <div className="step-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12 }}>
        {steps.map((step, index) => (
          <button
            key={step.id}
            onClick={step.onClick}
            style={{ textAlign: "left", border: `1px solid ${step.done ? "rgba(46,232,139,0.28)" : nextStep?.id === step.id ? "rgba(139,92,246,0.7)" : THEME.line}`, background: step.done ? "rgba(46,232,139,0.08)" : nextStep?.id === step.id ? "rgba(139,92,246,0.14)" : "rgba(255,255,255,0.035)", borderRadius: 16, padding: 14, cursor: "pointer", minHeight: 138 }}
          >
            <div style={{ width: 28, height: 28, borderRadius: "50%", display: "grid", placeItems: "center", background: step.done ? "rgba(46,232,139,0.18)" : THEME.purpleSoft, color: step.done ? THEME.green : THEME.purple, fontWeight: 950, marginBottom: 10 }}>{step.done ? "✓" : index + 1}</div>
            <div style={{ color: "#fff", fontWeight: 900, marginBottom: 5 }}>{step.title}</div>
            <div style={{ color: THEME.muted, fontSize: 12, lineHeight: 1.5 }}>{step.body}</div>
          </button>
        ))}
      </div>
    </Panel>
  );
}

function ClipLifecycleTimeline({ clip, hasVideoAsset, hasPostingAccount, readiness }) {
  const state = String(clip?.state || "fetched").toLowerCase();
  const steps = [
    { key: "saved", label: "Saved", done: true },
    { key: "video", label: "Video", done: hasVideoAsset },
    { key: "account", label: "Account", done: hasPostingAccount },
    { key: "ready", label: "Ready", done: Boolean(readiness?.allowed) || ["scheduled", "posted"].includes(state) },
    { key: "queued", label: "Queued", done: ["scheduled", "posted"].includes(state) },
    { key: "posted", label: "Posted", done: state === "posted" },
  ];
  const firstOpenIndex = steps.findIndex((step) => !step.done);
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${steps.length},1fr)`, gap: 8, margin: "12px 0 14px" }}>
      {steps.map((step, index) => {
        const active = firstOpenIndex === index;
        return (
          <div key={step.key} style={{ position: "relative", minWidth: 0 }}>
            <div style={{ height: 4, borderRadius: 999, background: step.done ? "linear-gradient(90deg,#2ee88b,#8b5cf6)" : active ? "rgba(139,92,246,0.5)" : "rgba(255,255,255,0.08)", marginBottom: 8 }} />
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 18, height: 18, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 10, fontWeight: 950, color: step.done ? THEME.green : active ? "#fff" : THEME.dim, background: step.done ? "rgba(46,232,139,0.12)" : active ? THEME.purpleSoft : "rgba(255,255,255,0.06)", border: `1px solid ${step.done ? "rgba(46,232,139,0.32)" : active ? "rgba(139,92,246,0.62)" : THEME.line}` }}>
                {step.done ? "✓" : index + 1}
              </span>
              <span style={{ color: step.done ? "#fff" : active ? "#ddd6fe" : THEME.muted, fontSize: 11, fontWeight: 850, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{step.label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NextActionCard({ title, body, tone = "purple", children }) {
  const palette = {
    green: { bg: "rgba(46,232,139,0.08)", border: "rgba(46,232,139,0.24)", icon: THEME.green },
    yellow: { bg: "rgba(250,204,21,0.08)", border: "rgba(250,204,21,0.26)", icon: THEME.warning },
    red: { bg: "rgba(255,91,121,0.08)", border: "rgba(255,91,121,0.28)", icon: THEME.danger },
    purple: { bg: "rgba(139,92,246,0.1)", border: "rgba(139,92,246,0.28)", icon: THEME.purple },
  }[tone] || {};
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", background: palette.bg, border: `1px solid ${palette.border}`, borderRadius: 14, padding: 13, marginTop: 12, flexWrap: "wrap" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", minWidth: 220, flex: 1 }}>
        <div style={{ width: 28, height: 28, borderRadius: 10, display: "grid", placeItems: "center", background: "rgba(255,255,255,0.08)", color: palette.icon, fontWeight: 950 }}>→</div>
        <div>
          <div style={{ color: "#fff", fontWeight: 900, fontSize: 13, marginBottom: 3 }}>{title}</div>
          <div style={{ color: THEME.muted, fontSize: 12, lineHeight: 1.5 }}>{body}</div>
        </div>
      </div>
      {children ? <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{children}</div> : null}
    </div>
  );
}

function ClipThumb({ clip, playing, onClick }) {
  if (playing && clip.embed_url) {
    return (
      <div style={{ position: "relative", width: "100%", paddingBottom: "56.25%", borderRadius: 16, overflow: "hidden", background: "#000" }}>
        <iframe
          title={clip.title}
          src={`${clip.embed_url}&parent=${encodeURIComponent(APP_HOSTNAME)}&autoplay=true`}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      style={{ position: "relative", width: "100%", paddingBottom: "56.25%", borderRadius: 16, overflow: "hidden", cursor: "pointer", background: THEME.panelStrong, border: `1px solid ${THEME.line}` }}
    >
      {clip.thumbnail_url ? (
        <img src={clip.thumbnail_url} alt={clip.title} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg,#7c3aed44,#1d4ed844)" }} />
      )}
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#00000088", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>▶</div>
      </div>
      <div style={{ position: "absolute", bottom: 8, right: 8, background: "#000000cc", borderRadius: 4, padding: "2px 7px", fontSize: 11, fontWeight: 700, color: "#fff" }}>
        {fmtDur(clip.duration)}
      </div>
    </div>
  );
}

function PreviewModal({ clip, streamer, accounts, currentUser, onClose, onQueue, onImport, onRequireLogin }) {
  const [playing, setPlaying] = useState(false);
  const [caption, setCaption] = useState("");
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [scheduleTime, setScheduleTime] = useState("now");
  const [queued, setQueued] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const score = viralScore(clip);

  const toggleAccount = (id) => {
    setSelectedAccounts((prev) => (prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id]));
  };

  const handleQueue = async () => {
    if (!currentUser) {
      onRequireLogin();
      return;
    }
    if (!selectedAccounts.length) return;
    const selectedAccountEntries = accounts.filter((acc) => selectedAccounts.includes(acc.id));
    try {
      await onQueue({ clip, streamer, accounts: selectedAccountEntries, caption, scheduleTime, score });
      setQueued(true);
      setTimeout(onClose, 900);
    } catch (error) {
      setQueued(false);
    }
  };

  const handleImport = async () => {
    if (!currentUser) {
      onRequireLogin();
      return;
    }
    setPreparing(true);
    try {
      await onImport({ clip, streamer });
      setTimeout(onClose, 500);
    } catch (error) {
      // The caller shows the user-facing error.
      setPreparing(false);
    }
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(14px)" }}
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div style={{ background: "linear-gradient(145deg,rgba(20,20,34,0.98),rgba(10,10,20,0.98))", border: `1px solid ${THEME.line}`, borderRadius: 26, width: "100%", maxWidth: 920, maxHeight: "93vh", overflowY: "auto", boxShadow: "0 40px 120px rgba(0,0,0,0.72), 0 0 90px rgba(139,92,246,0.12)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px", borderBottom: `1px solid ${THEME.line}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {streamer?.profile_image_url ? (
              <img src={streamer.profile_image_url} alt={streamer.display_name} style={{ width: 34, height: 34, borderRadius: "50%", border: "2px solid #7c3aed44" }} />
            ) : (
              <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#7c3aed44", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: "#a78bfa" }}>
                {streamer?.display_name?.[0] || "?"}
              </div>
            )}
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: THEME.text }}>{streamer?.display_name}</div>
              {clip.game_name ? <div style={{ fontSize: 12, color: THEME.dim }}>{clip.game_name}</div> : null}
            </div>
          </div>
          <GhostButton onClick={onClose} style={{ padding: "8px 10px" }}>CLOSE</GhostButton>
        </div>

        <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <div>
            <ClipThumb clip={clip} playing={playing} onClick={() => setPlaying(true)} />
            <div style={{ marginTop: 14, fontSize: 14, fontWeight: 700, color: "#e8e8f0", lineHeight: 1.4, marginBottom: 10 }}>{clip.title}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
              <Tag color="#4ade8018" text="#4ade80">VIEWS {fmtViews(clip.view_count)}</Tag>
              <Tag color="#7c3aed18" text="#a78bfa">LENGTH {fmtDur(clip.duration)}</Tag>
              <Tag color="#ffffff08" text="#aaa">SCORE {score}</Tag>
            </div>
            <Field label="CAPTION">
              <TextArea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Write the posting caption..."
                style={{ minHeight: 88 }}
              />
            </Field>
          </div>

          <div>
            <div style={{ fontSize: 10, color: "#444", letterSpacing: 2, marginBottom: 12 }}>POST TO ACCOUNTS</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
              {!currentUser ? (
                <Panel style={{ padding: 14, background: "#0b0b16" }}>
                  <div style={{ color: "#facc15", fontSize: 11, lineHeight: 1.7 }}>Log in before queueing clips to posting accounts.</div>
                </Panel>
              ) : null}
              {currentUser && !accounts.length ? (
                <Panel style={{ padding: 14, background: "#0b0b16" }}>
                  <div style={{ color: "#facc15", fontSize: 11, lineHeight: 1.7 }}>Connect a posting account before queueing.</div>
                </Panel>
              ) : null}
              {accounts.filter((account) => account.status !== "maxed").map((acc) => {
                const platform = PLATFORMS[acc.platform] || PLATFORMS.tiktok;
                const selected = selectedAccounts.includes(acc.id);
                return (
                  <button
                    key={acc.id}
                    onClick={() => acc.status !== "cooldown" && toggleAccount(acc.id)}
                    style={{
                      background: selected ? "#7c3aed18" : "#111118",
                      border: `1px solid ${selected ? "#7c3aed66" : acc.status === "cooldown" ? "#facc1522" : "#222230"}`,
                      borderRadius: 10,
                      padding: "10px 14px",
                      cursor: acc.status === "cooldown" ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      opacity: acc.status === "cooldown" ? 0.5 : 1,
                      color: "#e8e8f0",
                    }}
                  >
                    <div style={{ width: 26, height: 26, borderRadius: "50%", background: `${platform.color}18`, border: `1.5px solid ${platform.color}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: platform.color, flexShrink: 0 }}>
                      {platform.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>{acc.handle}</div>
                      <div style={{ fontSize: 10, color: "#666" }}>{acc.postsToday}/{acc.postLimit} today</div>
                    </div>
                    <Tag color="#ffffff10" text="#aaa">{acc.status.toUpperCase()}</Tag>
                  </button>
                );
              })}
            </div>

            <div style={{ fontSize: 10, color: "#444", letterSpacing: 2, marginBottom: 10 }}>SCHEDULE</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
              {["now", "1h", "3h", "6h", "12h"].map((time) => (
                <GhostButton key={time} onClick={() => setScheduleTime(time)} style={{ flex: 1, color: scheduleTime === time ? "#fff" : "#999", background: scheduleTime === time ? "#7c3aed" : "#151526", borderColor: scheduleTime === time ? "#7c3aed" : "#27273a" }}>
                  {time === "now" ? "NOW" : `+${time}`}
                </GhostButton>
              ))}
            </div>

            <PrimaryButton onClick={handleQueue} disabled={(currentUser && !selectedAccounts.length) || queued} style={{ width: "100%", opacity: (currentUser && !selectedAccounts.length) || queued ? 0.6 : 1 }}>
              {!currentUser ? "LOG IN TO QUEUE" : queued ? "QUEUED" : selectedAccounts.length ? `QUEUE TO ${selectedAccounts.length} ACCOUNT${selectedAccounts.length > 1 ? "S" : ""}` : "SELECT ACCOUNTS"}
            </PrimaryButton>
            <GhostButton onClick={handleImport} disabled={preparing} style={{ width: "100%", marginTop: 10 }}>
              {!currentUser ? "LOG IN TO SAVE" : preparing ? "SAVING + PREPARING" : "SAVE + PREPARE"}
            </GhostButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClipCard({ clip, onSelect }) {
  const score = viralScore(clip);
  return (
    <div
      onClick={onSelect}
      className="clip-card"
      style={{ background: "linear-gradient(180deg,rgba(25,25,40,0.96),rgba(14,14,25,0.96))", border: `1px solid ${THEME.line}`, borderRadius: 16, overflow: "hidden", cursor: "pointer", transition: "transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease", boxShadow: "0 14px 40px rgba(0,0,0,0.22)" }}
    >
      <div style={{ position: "relative", width: "100%", paddingBottom: "64%", background: "#0a0a14", overflow: "hidden" }}>
        {clip.thumbnail_url ? (
          <img src={clip.thumbnail_url} alt={clip.title} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg,#7c3aed22,#1d4ed822)", display: "flex", alignItems: "center", justifyContent: "center", color: "#333", fontSize: 24 }}>◉</div>
        )}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(11,11,22,0.92),rgba(11,11,22,0.08) 62%,transparent)" }} />
        <div style={{ position: "absolute", top: 10, right: 10 }}><Tag color="rgba(0,0,0,0.62)" text="#fff">SCORE {score}</Tag></div>
        <div style={{ position: "absolute", bottom: 10, left: 10, background: "rgba(0,0,0,0.72)", borderRadius: 7, padding: "4px 8px", fontSize: 12, fontWeight: 800, color: "#fff" }}>{fmtDur(clip.duration)}</div>
      </div>
      <div style={{ padding: "12px 14px 14px" }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: THEME.text, marginBottom: 8, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{clip.title}</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Tag color="rgba(46,232,139,0.12)" text={THEME.green}>VIEWS {fmtViews(clip.view_count)}</Tag>
          <span style={{ fontSize: 11, color: THEME.dim }}>{clip.created_at ? new Date(clip.created_at).toLocaleDateString() : ""}</span>
        </div>
      </div>
    </div>
  );
}

function StreamerRow({ user, stream, selected, loading, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{ background: selected ? "#7c3aed18" : "#111118", border: `1px solid ${selected ? "#7c3aed55" : "#1a1a28"}`, borderRadius: 10, padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, marginBottom: 6, width: "100%", textAlign: "left", color: "#fff" }}
    >
      <div style={{ position: "relative", flexShrink: 0 }}>
        {user.profile_image_url ? (
          <img src={user.profile_image_url} alt={user.display_name} style={{ width: 34, height: 34, borderRadius: "50%", border: `2px solid ${selected ? "#7c3aed" : "#2a2a40"}` }} />
        ) : (
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: selected ? "#7c3aed44" : "#1a1a28", border: `2px solid ${selected ? "#7c3aed" : "#2a2a40"}`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, color: selected ? "#a78bfa" : "#555" }}>
            {user.display_name?.[0]}
          </div>
        )}
        {stream ? <div style={{ position: "absolute", bottom: 0, right: 0, width: 10, height: 10, borderRadius: "50%", background: "#ef4444", border: "2px solid #0a0a14", animation: "livepulse 1.5s ease-out infinite" }} /> : null}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: selected ? "#e8e8f0" : "#888", marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user.display_name}</div>
        {stream ? (
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <LiveDot />
            <span style={{ fontSize: 10, color: "#ef444499" }}>{parseInt(stream.viewer_count, 10).toLocaleString()}</span>
          </div>
        ) : (
          <div style={{ fontSize: 10, color: "#333" }}>offline</div>
        )}
      </div>
      {loading ? <Spinner size={14} /> : null}
    </button>
  );
}

function AuthPortal({ authMode, setAuthMode, loginForm, setLoginForm, registerForm, setRegisterForm, onLogin, onRegister, backendOk, currentUser, onLogout }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: currentUser ? "1fr" : "1fr 1fr", gap: 18 }}>
      {currentUser ? (
        <Panel style={{ padding: 22 }}>
          <div style={{ fontSize: 10, color: "#555", letterSpacing: 2, marginBottom: 8 }}>AUTH PORTAL</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#fff", marginBottom: 6 }}>{currentUser.email}</div>
          <div style={{ color: "#888", fontSize: 12, marginBottom: 14 }}>Role: {currentUser.role} • Tenant: {currentUser.tenant_id}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            <Tag color={backendOk ? "#4ade8018" : "#ef444418"} text={backendOk ? "#4ade80" : "#ef4444"}>{backendOk ? "BACKEND ONLINE" : "BACKEND OFFLINE"}</Tag>
            <Tag color="#7c3aed18" text="#a78bfa">LOGGED IN</Tag>
          </div>
          <GhostButton onClick={onLogout}>LOG OUT</GhostButton>
        </Panel>
      ) : (
        <>
          <Panel style={{ padding: 22 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <GhostButton onClick={() => setAuthMode("login")} style={{ background: authMode === "login" ? "#7c3aed22" : "#151526" }}>LOGIN</GhostButton>
              <GhostButton onClick={() => setAuthMode("register")} style={{ background: authMode === "register" ? "#7c3aed22" : "#151526" }}>REGISTER</GhostButton>
            </div>

            {authMode === "login" ? (
              <form onSubmit={onLogin} style={{ display: "grid", gap: 14 }}>
                <Field label="EMAIL">
                  <TextInput value={loginForm.email} onChange={(e) => setLoginForm((prev) => ({ ...prev, email: e.target.value }))} />
                </Field>
                <Field label="PASSWORD">
                  <TextInput type="password" value={loginForm.password} onChange={(e) => setLoginForm((prev) => ({ ...prev, password: e.target.value }))} />
                </Field>
                <PrimaryButton type="submit">LOG IN</PrimaryButton>
              </form>
            ) : (
              <form onSubmit={onRegister} style={{ display: "grid", gap: 14 }}>
                <Field label="COMPANY NAME">
                  <TextInput value={registerForm.companyName} onChange={(e) => setRegisterForm((prev) => ({ ...prev, companyName: e.target.value }))} />
                </Field>
                <Field label="EMAIL">
                  <TextInput value={registerForm.email} onChange={(e) => setRegisterForm((prev) => ({ ...prev, email: e.target.value }))} />
                </Field>
                <Field label="PASSWORD">
                  <TextInput type="password" value={registerForm.password} onChange={(e) => setRegisterForm((prev) => ({ ...prev, password: e.target.value }))} />
                </Field>
                <PrimaryButton type="submit">CREATE TENANT</PrimaryButton>
              </form>
            )}
          </Panel>

          <Panel style={{ padding: 22 }}>
            <div style={{ fontSize: 10, color: "#555", letterSpacing: 2, marginBottom: 8 }}>WHY THIS EXISTS</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: "#fff", marginBottom: 12 }}>Operations Portal</div>
            <div style={{ color: "#888", fontSize: 12, lineHeight: 1.8 }}>
              Log in to connect posting accounts, prepare clips, schedule posts, and manage admin-only controls.
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

function LegalPage({ page = "terms" }) {
  const isPrivacy = page === "privacy";
  const isRights = page === "rights";
  const isDeletion = page === "data-deletion";
  const isContact = page === "contact";
  const title = isPrivacy ? "PRIVACY POLICY" : isRights ? "CONTENT RIGHTS" : isDeletion ? "DATA DELETION" : isContact ? "CONTACT" : "TERMS OF SERVICE";
  const legalLinks = [
    ["Terms", marketingHref("/terms")],
    ["Privacy", marketingHref("/privacy")],
    ["Content Rights", marketingHref("/rights")],
    ["Data Deletion", marketingHref("/data-deletion")],
    ["Contact", marketingHref("/contact")],
  ];
  return (
    <div style={{ maxWidth: 920, margin: "0 auto", display: "grid", gap: 18 }}>
      <Panel style={{ padding: 14, display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
        {legalLinks.map(([label, href]) => {
          const active = href.replace("/", "") === page;
          return (
            <a
              key={href}
              href={href}
              style={{
                color: active ? "#fff" : THEME.muted,
                background: active ? "rgba(139,92,246,0.24)" : "rgba(255,255,255,0.035)",
                border: `1px solid ${active ? "rgba(139,92,246,0.62)" : THEME.line}`,
                borderRadius: 999,
                padding: "9px 12px",
                fontSize: 12,
                fontWeight: 850,
                textDecoration: "none",
              }}
            >
              {label}
            </a>
          );
        })}
      </Panel>
      <Panel style={{ padding: 26 }}>
        <div style={{ fontSize: 10, color: "#555", letterSpacing: 2, marginBottom: 10 }}>CLIPSE LEGAL</div>
        <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 40, letterSpacing: 4, color: "#fff", margin: 0 }}>{title}</h1>
        <div style={{ color: "#666", fontSize: 11, marginTop: 8 }}>Last updated: May 13, 2026</div>
      </Panel>

      <Panel style={{ padding: 26, color: "#aaa", fontSize: 12, lineHeight: 1.9 }}>
        {isPrivacy ? (
          <>
            <h3 style={{ color: "#fff" }}>What Clipse does</h3>
            <p>Clipse helps users import clips, prepare short-form video files, manage connected posting accounts, queue posts, and view posting receipts/status. We process data only to operate these workflows and improve service reliability.</p>
            <h3 style={{ color: "#fff" }}>Information we collect</h3>
            <p>We collect account information users provide, authentication/session records, connected platform account metadata, OAuth tokens needed to perform user-authorized actions, clip metadata, uploaded/imported video assets, posting queue records, posting receipts, and operational logs.</p>
            <h3 style={{ color: "#fff" }}>Connected accounts and OAuth tokens</h3>
            <p>When users connect TikTok, Twitch, YouTube, Instagram, or another platform, Clipse stores tokens securely on the backend and uses them only for account connection, readiness checks, and actions the user initiates or enables, such as queueing or uploading content.</p>
            <h3 style={{ color: "#fff" }}>How we use information</h3>
            <p>We use account and content data to authenticate users, maintain separate workspaces, connect platform accounts, prepare videos, run posting safety checks, schedule uploads, display queue status, debug failures, enforce plan limits, and improve reliability.</p>
            <h3 style={{ color: "#fff" }}>Storage and sharing</h3>
            <p>Imported and processed video assets may be stored in Cloudflare R2 or similar object storage so the queue and posting systems can access them. Clipse does not sell personal data. Data may be shared with connected platforms only as needed to perform requested actions.</p>
            <h3 style={{ color: "#fff" }}>Security</h3>
            <p>Clipse limits access to account data, uses backend-only storage for sensitive tokens, and relies on platform OAuth flows where available. Users can disconnect connected accounts from the Accounts page to stop future use of that platform account inside Clipse.</p>
            <h3 style={{ color: "#fff" }}>Deletion</h3>
            <p>Users can request deletion of their account data, connected accounts, queue records, and stored assets by emailing Admin@getclipse.com. We will verify account ownership before deleting data.</p>
            <h3 style={{ color: "#fff" }}>Contact</h3>
            <p>Privacy questions can be sent to <a href="mailto:Admin@getclipse.com" style={{ color: "#c4b5fd" }}>Admin@getclipse.com</a>.</p>
          </>
        ) : isRights ? (
          <>
            <h3 style={{ color: "#fff" }}>Creator-first clipping</h3>
            <p>Clipse is built for clippers, creators, editors, agencies, and teams that turn stream moments into short-form content. The product is designed to preserve creator context by carrying streamer/source attribution through the workflow and supporting visible streamer credit on generated clips.</p>
            <h3 style={{ color: "#fff" }}>Attribution and review</h3>
            <p>Users should review each clip before publishing and include accurate creator or streamer attribution when reposting. Clipse may add or preserve streamer/source credit in captions, overlays, metadata, or the bottom area of formatted clips.</p>
            <h3 style={{ color: "#fff" }}>Platform rules</h3>
            <p>Users are responsible for following Twitch, TikTok, YouTube, Instagram, and any other platform rules, including content, API, rate-limit, community, attribution, and monetization policies.</p>
            <h3 style={{ color: "#fff" }}>Account protection</h3>
            <p>Clipse may apply posting limits, cooldowns, duplicate-caption warnings, queue controls, and review states to reduce spam risk and protect user accounts.</p>
            <h3 style={{ color: "#fff" }}>Reports</h3>
            <p>To report misuse, missing attribution, or a content rights concern, contact Admin@getclipse.com with the relevant clip URL, account, and reason for the report.</p>
          </>
        ) : isDeletion ? (
          <>
            <h3 style={{ color: "#fff" }}>How to request deletion</h3>
            <p>Email Admin@getclipse.com from the email address connected to your Clipse account and include “Clipse data deletion request” in the subject line.</p>
            <h3 style={{ color: "#fff" }}>What can be deleted</h3>
            <p>Upon verified request, Clipse can delete user profile records, connected account records, OAuth tokens, clip metadata, stored video assets, queue records, posting attempts, and related workspace data where deletion is technically and legally available.</p>
            <h3 style={{ color: "#fff" }}>Connected platforms</h3>
            <p>Deleting Clipse data does not automatically delete content or account records held by TikTok, Twitch, YouTube, Instagram, or other connected platforms. Users should manage deletion on those platforms directly as needed.</p>
            <h3 style={{ color: "#fff" }}>Verification</h3>
            <p>We may ask for proof of account ownership before deleting data to prevent unauthorized deletion requests.</p>
          </>
        ) : isContact ? (
          <>
            <h3 style={{ color: "#fff" }}>Support</h3>
            <p>For support, account deletion, attribution reports, content rights reports, privacy questions, or platform review questions, contact:</p>
            <p><a href="mailto:Admin@getclipse.com" style={{ color: "#c4b5fd" }}>Admin@getclipse.com</a></p>
            <h3 style={{ color: "#fff" }}>Domains</h3>
            <p>Public site: getclipse.com. App: app.getclipse.com. API: api.getclipse.com. Assets: assets.getclipse.com.</p>
          </>
        ) : (
          <>
            <h3 style={{ color: "#fff" }}>Using Clipse</h3>
            <p>By using Clipse, users agree to review clips before publishing and to use the service for accounts, clips, videos, and content they are permitted to manage. Clipse is designed to help preserve creator attribution and posting context.</p>
            <h3 style={{ color: "#fff" }}>Service features</h3>
            <p>Clipse provides clip import, video preparation, object storage, connected account management, posting readiness checks, queue management, caption assistance, and upload orchestration. Features may change as the product evolves.</p>
            <h3 style={{ color: "#fff" }}>Prohibited use</h3>
            <p>Users may not use Clipse to post spam, fake engagement content, harmful content, impersonation, illegal content, intentionally misleading attribution, or content that violates TikTok, Instagram, YouTube, Twitch, or other platform policies.</p>
            <h3 style={{ color: "#fff" }}>Posting safety</h3>
            <p>Clipse may apply posting limits, cooldowns, duplicate-caption controls, queue restrictions, and review states to protect user accounts, platform integrity, and service reliability.</p>
            <h3 style={{ color: "#fff" }}>Billing</h3>
            <p>Paid plan terms, pricing, usage limits, renewals, refunds, and cancellation rules will be presented during checkout when billing is enabled. Until then, paid plan actions may be simulated or unavailable.</p>
            <h3 style={{ color: "#fff" }}>Contact</h3>
            <p>Questions about these terms can be sent to Admin@getclipse.com.</p>
          </>
        )}
      </Panel>
      <Panel style={{ padding: 16, display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", color: THEME.muted, fontSize: 12 }}>
        <span>© 2026 Clipse</span>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {legalLinks.map(([label, href]) => <a key={href} href={href} style={{ color: THEME.muted }}>{label}</a>)}
        </div>
      </Panel>
    </div>
  );
}

function PublicLandingPage() {
  const appUrl = IS_LOCALHOST ? "/?page=app" : appHref("/");
  const loginUrl = IS_LOCALHOST ? "/?page=login" : marketingHref("/login");
  const registerUrl = IS_LOCALHOST ? "/?page=register" : marketingHref("/register");
  const features = [
    ["Clip Import", "Find, save, and prepare stream clips from one clean workspace."],
    ["Post Queue", "Choose a posting account, generate captions, and queue clips with status receipts."],
    ["Account Management", "Manage TikTok, Instagram, YouTube, and creator posting workflows."],
    ["Growth Controls", "Use limits, cooldowns, and review states to protect posting accounts."],
  ];
  const steps = [
    ["1", "Import a clip", "Paste or browse clips and pull the video into your workspace."],
    ["2", "Choose account", "Pick the posting account and format for short-form platforms."],
    ["3", "Queue or post", "Schedule, post, and track the upload through clear receipts."],
  ];
  return (
    <div style={{ minHeight: "100vh", background: "radial-gradient(circle at 18% 8%,rgba(139,92,246,0.28),transparent 34%),radial-gradient(circle at 82% 12%,rgba(46,232,139,0.1),transparent 28%),#070711", color: THEME.text, fontFamily: "'Inter', 'Space Grotesk', sans-serif" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "26px 24px 70px" }}>
        <header style={{ display: "flex", justifyContent: "space-between", gap: 18, alignItems: "center", marginBottom: 76 }}>
          <BrandLockup variant="full" markSize={32} />
          <nav style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <a href="#features" style={{ color: THEME.muted, textDecoration: "none", fontWeight: 800, fontSize: 13 }}>Features</a>
            <a href="#pricing" style={{ color: THEME.muted, textDecoration: "none", fontWeight: 800, fontSize: 13 }}>Pricing</a>
            <a href={marketingHref("/terms")} style={{ color: THEME.muted, textDecoration: "none", fontWeight: 800, fontSize: 13 }}>Terms</a>
            <a href={marketingHref("/privacy")} style={{ color: THEME.muted, textDecoration: "none", fontWeight: 800, fontSize: 13 }}>Privacy</a>
            <a href={marketingHref("/rights")} style={{ color: THEME.muted, textDecoration: "none", fontWeight: 800, fontSize: 13 }}>Content Rights</a>
            <GhostButton onClick={() => { window.location.href = loginUrl; }} style={{ padding: "10px 14px" }}>Log in</GhostButton>
            <PrimaryButton onClick={() => { window.location.href = appUrl; }} style={{ padding: "10px 16px" }}>Open App</PrimaryButton>
          </nav>
        </header>

        <section style={{ display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: 34, alignItems: "center", marginBottom: 64 }}>
          <div>
            <Tag color="rgba(139,92,246,0.18)" text="#c4b5fd">BUILT FOR CLIPPERS AND CREATOR TEAMS</Tag>
            <h1 style={{ color: "#fff", fontFamily: "'Space Grotesk',sans-serif", fontSize: 64, lineHeight: 0.95, letterSpacing: -2.2, margin: "20px 0 18px" }}>
              Turn stream clips into scheduled short-form posts.
            </h1>
            <p style={{ color: THEME.muted, fontSize: 18, lineHeight: 1.75, maxWidth: 650, margin: "0 0 28px" }}>
              Clipse helps creators import clips, prepare vertical videos, manage posting accounts, and track every upload from queue to review.
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 28 }}>
              <PrimaryButton onClick={() => { window.location.href = registerUrl; }} style={{ padding: "15px 20px" }}>Start Free</PrimaryButton>
              <GhostButton onClick={() => { window.location.href = "#features"; }} style={{ padding: "15px 20px" }}>See How It Works</GhostButton>
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", color: THEME.muted, fontSize: 13 }}>
              <span>✓ TikTok-ready workflow</span>
              <span>✓ Account-level queues</span>
              <span>✓ Posting receipts</span>
            </div>
          </div>

          <Panel style={{ padding: 18, background: "linear-gradient(180deg,rgba(24,24,39,0.96),rgba(10,10,20,0.96))" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 16 }}>
              <div>
                <div style={{ color: "#fff", fontWeight: 950, fontSize: 18 }}>Today’s Queue</div>
                <div style={{ color: THEME.muted, fontSize: 12, marginTop: 4 }}>Ready, scheduled, and review states</div>
              </div>
              <Tag color="#4ade8018" text="#4ade80">LIVE</Tag>
            </div>
            <div style={{ display: "grid", gap: 12 }}>
              {[
                ["Ludwig debate clip", "Ready for review", "#facc15", "TikTok inbox upload accepted"],
                ["Clutch 1v4 moment", "Queued", "#a78bfa", "Scheduled for 7:30 PM"],
                ["Creator reaction", "Posted", "#4ade80", "Platform returned publish receipt"],
              ].map((item) => (
                <div key={item[0]} style={{ display: "grid", gridTemplateColumns: "76px 1fr", gap: 12, alignItems: "center", background: "rgba(255,255,255,0.045)", border: `1px solid ${THEME.line}`, borderRadius: 16, padding: 10 }}>
                  <div style={{ height: 50, borderRadius: 12, background: "linear-gradient(135deg,rgba(139,92,246,0.46),rgba(255,91,121,0.22))", position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 70% 30%,rgba(255,255,255,0.24),transparent 28%)" }} />
                  </div>
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                      <div style={{ color: "#fff", fontWeight: 900, fontSize: 13 }}>{item[0]}</div>
                      <span style={{ color: item[2], fontSize: 11, fontWeight: 900 }}>{item[1]}</span>
                    </div>
                    <div style={{ color: THEME.muted, fontSize: 11, marginTop: 5 }}>{item[3]}</div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </section>

        <section id="features" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 54 }}>
          {features.map((feature) => (
            <Panel key={feature[0]} style={{ padding: 20, background: "rgba(255,255,255,0.04)" }}>
              <div style={{ width: 38, height: 38, borderRadius: 14, display: "grid", placeItems: "center", background: THEME.purpleSoft, color: "#c4b5fd", marginBottom: 14 }}>✦</div>
              <div style={{ color: "#fff", fontWeight: 950, marginBottom: 8 }}>{feature[0]}</div>
              <div style={{ color: THEME.muted, fontSize: 13, lineHeight: 1.65 }}>{feature[1]}</div>
            </Panel>
          ))}
        </section>

        <section style={{ display: "grid", gridTemplateColumns: "0.8fr 1.2fr", gap: 22, alignItems: "start", marginBottom: 54 }}>
          <div>
            <div style={{ color: THEME.purple, fontWeight: 950, letterSpacing: 2, fontSize: 11, marginBottom: 10 }}>HOW IT WORKS</div>
            <h2 style={{ color: "#fff", fontFamily: "'Space Grotesk',sans-serif", fontSize: 38, letterSpacing: -1.2, margin: "0 0 10px" }}>A simple pipeline for clips.</h2>
            <p style={{ color: THEME.muted, lineHeight: 1.75 }}>Keep the messy parts behind the scenes and give users one focused flow from clip to post.</p>
          </div>
          <div style={{ display: "grid", gap: 12 }}>
            {steps.map((step) => (
              <Panel key={step[0]} style={{ padding: 18, display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{ width: 34, height: 34, borderRadius: "50%", display: "grid", placeItems: "center", background: "linear-gradient(135deg,#9f5cff,#6d28d9)", color: "#fff", fontWeight: 950, flexShrink: 0 }}>{step[0]}</div>
                <div>
                  <div style={{ color: "#fff", fontWeight: 950, marginBottom: 5 }}>{step[1]}</div>
                  <div style={{ color: THEME.muted, fontSize: 13, lineHeight: 1.6 }}>{step[2]}</div>
                </div>
              </Panel>
            ))}
          </div>
        </section>

        <section id="pricing" style={{ marginBottom: 54 }}>
          <div style={{ textAlign: "center", marginBottom: 22 }}>
            <h2 style={{ color: "#fff", fontFamily: "'Space Grotesk',sans-serif", fontSize: 38, letterSpacing: -1.2, margin: 0 }}>Pricing built for growth</h2>
            <div style={{ color: THEME.muted, marginTop: 8 }}>Start free, upgrade when posting volume grows.</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
            {Object.values(PUBLIC_PLANS).map((plan) => (
              <Panel key={plan.key} style={{ padding: 22, border: plan.featured ? "1px solid rgba(139,92,246,0.75)" : `1px solid ${THEME.line}`, background: plan.featured ? "linear-gradient(180deg,rgba(139,92,246,0.16),rgba(255,255,255,0.035))" : "rgba(255,255,255,0.035)" }}>
                <div style={{ color: plan.featured ? "#c4b5fd" : THEME.muted, fontWeight: 950, textTransform: "uppercase", marginBottom: 12 }}>{plan.label}</div>
                <div style={{ color: "#fff", fontSize: 42, fontWeight: 950, letterSpacing: -1 }}>${plan.priceMonthlyUsd}<span style={{ color: THEME.muted, fontSize: 14, fontWeight: 700 }}> / month</span></div>
                <div style={{ color: THEME.muted, lineHeight: 1.6, minHeight: 46, margin: "12px 0 18px" }}>{plan.tagline}</div>
                <div style={{ display: "grid", gap: 10, marginBottom: 20 }}>
                  {plan.features.map((feature) => <div key={feature} style={{ color: "#ddd6fe", fontSize: 13 }}>✓ {feature}</div>)}
                </div>
                <PrimaryButton onClick={() => { window.location.href = registerUrl; }} style={{ width: "100%" }}>{plan.priceMonthlyUsd ? `Choose ${plan.label}` : "Start Free"}</PrimaryButton>
              </Panel>
            ))}
          </div>
        </section>

        <footer style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", borderTop: `1px solid ${THEME.line}`, paddingTop: 22, color: THEME.muted, fontSize: 13, flexWrap: "wrap" }}>
          <div>© 2026 Clipse. Built for creator clip workflows.</div>
          <div style={{ display: "flex", gap: 14 }}>
            <a href={marketingHref("/terms")} style={{ color: THEME.muted }}>Terms</a>
            <a href={marketingHref("/privacy")} style={{ color: THEME.muted }}>Privacy</a>
            <a href={marketingHref("/rights")} style={{ color: THEME.muted }}>Content Rights</a>
            <a href={marketingHref("/data-deletion")} style={{ color: THEME.muted }}>Data Deletion</a>
            <a href={marketingHref("/contact")} style={{ color: THEME.muted }}>Contact</a>
            <a href={appUrl} style={{ color: "#c4b5fd" }}>Open App</a>
          </div>
        </footer>
      </div>
    </div>
  );
}

function clipImage(clip) {
  return clip?.thumbnail_url || clip?.thumbnailUrl || clip?.thumb || "";
}

function clipTitle(clip) {
  return clip?.title || "Untitled clip";
}

function clipCreator(clip) {
  return clip?.display_name || clip?.login || clip?.streamer_name || clip?.streamer || "clipse";
}

function generateQuickPostCaption(clip, platform = "tiktok") {
  const title = clipTitle(clip).replace(/\s+/g, " ").trim();
  const creator = clipCreator(clip).replace(/^@/, "").replace(/[^a-zA-Z0-9_ ]/g, "").trim() || "creator";
  const safeTitle = title.length > 82 ? `${title.slice(0, 79).trim()}...` : title;
  const angles = [
    "wild timing",
    "creator moment",
    "stream highlight",
    "must-see clip",
    "instant replay",
    "top reaction",
    "clutch moment",
    "clip-worthy play",
  ];
  const calls = [
    "This one deserves the replay",
    "Saved this from the stream",
    "The timing on this is unreal",
    "One of those clips you watch twice",
    "This moment had chat moving",
    "A clean highlight from the stream",
  ];
  const seedSource = `${clip?.id || ""}${clip?.source_clip_id || ""}${Date.now()}${Math.random()}`;
  let seed = 0;
  for (let index = 0; index < seedSource.length; index += 1) seed = (seed + seedSource.charCodeAt(index) * (index + 17)) % 9973;
  const uniqueCode = `${Math.floor(Date.now() / 1000).toString(36)}${seed.toString(36)}`.slice(-8);
  const angle = angles[seed % angles.length];
  const call = calls[(seed + 3) % calls.length];
  const tags = platform === "youtube" ? "#Shorts #Clipse" : platform === "instagram" ? "#Reels #Clipse" : "#TikTok #Clipse";
  return `${call}: ${safeTitle} — ${angle} from ${creator}. Clip note ${uniqueCode}. ${tags}`;
}

function clipState(clip) {
  const state = String(clip?.state || clip?.status || "").toLowerCase();
  if (state.includes("posted")) return "Posted";
  if (state.includes("scheduled")) return "Scheduled";
  if (state.includes("draft")) return "Draft";
  if (state.includes("ready")) return "Ready";
  if (Number(clip?.asset_count || 0) > 0) return "Ready";
  return "Draft";
}

function statusColors(status) {
  const key = String(status || "").toLowerCase();
  if (key === "ready") return { bg: "rgba(46,232,139,0.12)", text: THEME.green, border: "rgba(46,232,139,0.25)" };
  if (key === "scheduled") return { bg: "rgba(139,92,246,0.16)", text: "#c4b5fd", border: "rgba(139,92,246,0.32)" };
  if (key === "posted") return { bg: "rgba(59,130,246,0.14)", text: "#93c5fd", border: "rgba(59,130,246,0.26)" };
  return { bg: "rgba(255,255,255,0.08)", text: THEME.muted, border: "rgba(255,255,255,0.1)" };
}

function StatCard({ label, value, delta, accent = THEME.purple }) {
  return (
    <Panel style={{ padding: 20, minHeight: 128 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
        <div style={{ color: THEME.muted, fontSize: 13 }}>{label}</div>
        <div style={{ color: THEME.dim }}>✣</div>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <div style={{ color: THEME.text, fontSize: 30, fontWeight: 900, letterSpacing: -1 }}>{value}</div>
        {delta ? <div style={{ color: THEME.green, fontSize: 13, fontWeight: 800 }}>{delta}</div> : null}
      </div>
      <div style={{ marginTop: 16, height: 34, display: "flex", alignItems: "end", gap: 5 }}>
        {[18, 12, 17, 15, 24, 13, 30, 18, 34, 21, 28, 38].map((height, index) => (
          <div key={index} style={{ flex: 1, height, borderRadius: 999, background: `linear-gradient(180deg,${accent},rgba(139,92,246,0.1))`, opacity: 0.55 + (index % 4) * 0.1 }} />
        ))}
      </div>
    </Panel>
  );
}

function FilterBar({ filters = ["All", "Ready", "Scheduled", "Posted", "Drafts"], active = "All", onChange }) {
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
      {filters.map((filter) => (
        <button
          key={filter}
          onClick={() => onChange?.(filter)}
          style={{
            background: active === filter ? "linear-gradient(135deg,#8b5cf6,#6d28d9)" : "rgba(255,255,255,0.045)",
            border: `1px solid ${active === filter ? "rgba(139,92,246,0.85)" : THEME.line}`,
            color: active === filter ? "#fff" : THEME.muted,
            borderRadius: 10,
            padding: "10px 14px",
            fontWeight: 800,
            cursor: "pointer",
          }}
        >
          {filter}
        </button>
      ))}
    </div>
  );
}

function SaaSClipCard({ clip, compact = false, onPreview, onQuickPost, onSchedule }) {
  const status = clipState(clip);
  const colors = statusColors(status);
  const views = Number(clip?.view_count || clip?.viewCount || 0);
  const engagement = clip?.engagement || "0.0%";
  return (
    <div className="saas-clip-card" style={{ position: "relative", overflow: "hidden", borderRadius: 18, border: `1px solid ${THEME.line}`, background: "linear-gradient(180deg,rgba(21,21,35,0.96),rgba(12,12,24,0.98))", boxShadow: "0 18px 55px rgba(0,0,0,0.28)" }}>
      <div style={{ position: "relative", aspectRatio: compact ? "1.1 / 1" : "1.45 / 1", background: "#090915", overflow: "hidden" }}>
        {clipImage(clip) ? (
          <img src={clipImage(clip)} alt={clipTitle(clip)} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", background: "radial-gradient(circle at 30% 20%,rgba(139,92,246,0.42),transparent 28%),linear-gradient(135deg,#17172a,#070711)" }} />
        )}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(7,7,17,0.94),rgba(7,7,17,0.18) 60%,rgba(7,7,17,0.03))" }} />
        <div style={{ position: "absolute", left: 12, bottom: 12, background: "rgba(0,0,0,0.72)", borderRadius: 8, color: "#fff", fontSize: 12, fontWeight: 900, padding: "5px 8px" }}>{fmtDur(clip?.duration || clip?.duration_sec || 45)}</div>
        <div className="clip-hover-actions" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: 0, transition: "opacity 0.18s ease", background: "rgba(7,7,17,0.42)" }}>
          <GhostButton onClick={onPreview} style={{ padding: "9px 11px", background: "rgba(0,0,0,0.55)" }}>Preview</GhostButton>
          <GhostButton onClick={onQuickPost} style={{ padding: "9px 11px", background: "rgba(0,0,0,0.55)" }}>{clip?.quickPosting ? "Queueing..." : "Quick Post"}</GhostButton>
          <PrimaryButton onClick={onSchedule} style={{ padding: "9px 11px" }}>Schedule</PrimaryButton>
        </div>
      </div>
      <div style={{ padding: 15 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
          <div style={{ color: "#fff", fontWeight: 900, fontSize: compact ? 13 : 15, lineHeight: 1.35, minHeight: compact ? 36 : 42 }}>{clipTitle(clip)}</div>
          <span style={{ border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, borderRadius: 999, padding: "4px 8px", fontSize: 11, fontWeight: 900, flexShrink: 0 }}>{status}</span>
        </div>
        <div style={{ color: THEME.muted, fontSize: 12, marginBottom: 12 }}>{clipCreator(clip)}</div>
        <div style={{ display: "flex", justifyContent: "space-between", color: THEME.muted, fontSize: 12 }}>
          <span>◉ {fmtViews(views)}</span>
          <span>♧ {engagement}</span>
        </div>
      </div>
    </div>
  );
}

function AccountCard({ account, health, autoModeEnabled, onPost, onVerify, onDisconnect, onUpgradeAutoMode }) {
  const platform = PLATFORMS[account.platform] || PLATFORMS.tiktok;
  const followers = account.platform === "youtube" ? "1.1M" : account.platform === "instagram" ? "278K" : "312K";
  const initials = (account.display_name || account.platform || "A").slice(0, 2).toUpperCase();
  return (
    <Panel style={{ padding: 18, minHeight: 236, background: "linear-gradient(180deg,rgba(18,18,31,0.96),rgba(11,11,22,0.98))" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 16 }}>
        <div style={{ width: 28, height: 28, borderRadius: 9, display: "grid", placeItems: "center", color: "#fff", background: `${platform.color}22`, border: `1px solid ${platform.color}55`, fontWeight: 900 }}>{platform.icon}</div>
        <div style={{ color: THEME.dim }}>✣</div>
      </div>
      <div style={{ display: "grid", placeItems: "center", textAlign: "center" }}>
        <div style={{ width: 68, height: 68, borderRadius: "50%", background: `linear-gradient(135deg,${platform.color},#6d28d9)`, display: "grid", placeItems: "center", color: "#fff", fontWeight: 900, fontSize: 20, boxShadow: `0 18px 45px ${platform.color}22` }}>{initials}</div>
        <div style={{ color: "#fff", fontWeight: 900, marginTop: 12 }}>{account.display_name || platform.label}</div>
        <div style={{ color: THEME.muted, fontSize: 12 }}>@{account.platform_account_id || String(account.display_name || "account").replace(/\s+/g, "").toLowerCase()}</div>
        <Tag color="rgba(46,232,139,0.12)" text={THEME.green}>Active</Tag>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18, color: THEME.muted, fontSize: 12 }}>
        <div><div>Followers</div><strong style={{ color: "#fff" }}>{followers}</strong></div>
        <div style={{ textAlign: "right" }}><div>Last Post</div><strong style={{ color: "#fff" }}>{health?.metrics?.nextEligibleAt ? "ready soon" : "2h ago"}</strong></div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16 }}>
        <GhostButton onClick={onPost} style={{ flex: 1 }}>Post</GhostButton>
        <GhostButton onClick={onDisconnect} style={{ color: "#ff8aa0", padding: "9px 11px" }}>Disconnect</GhostButton>
        <button onClick={autoModeEnabled ? onVerify : onUpgradeAutoMode} style={{ background: autoModeEnabled ? "rgba(139,92,246,0.12)" : "rgba(255,255,255,0.05)", border: `1px solid ${autoModeEnabled ? "rgba(139,92,246,0.55)" : THEME.line}`, color: "#fff", borderRadius: 999, padding: "9px 12px", cursor: "pointer", display: "flex", gap: 8, alignItems: "center" }}>
          Auto Mode {autoModeEnabled ? "" : "🔒"} <span style={{ width: 34, height: 18, borderRadius: 999, background: autoModeEnabled ? "linear-gradient(135deg,#8b5cf6,#6d28d9)" : "rgba(255,255,255,0.16)", display: "inline-block", position: "relative" }} />
        </button>
      </div>
    </Panel>
  );
}

function AnalyticsCard({ title, children, style = {} }) {
  return (
    <Panel style={{ padding: 20, ...style }}>
      <div style={{ color: "#fff", fontWeight: 900, marginBottom: 14 }}>{title}</div>
      {children}
    </Panel>
  );
}

function UsageMeter({ label, current, limit }) {
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
        <div style={{ color: THEME.muted, fontWeight: 800 }}>{label}</div>
        <div style={{ color: "#fff", fontWeight: 900 }}>{current}/{limit}</div>
      </div>
      <div style={{ height: 9, background: "rgba(255,255,255,0.06)", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ width: `${usagePercent(current, limit)}%`, height: "100%", background: usagePercent(current, limit) >= 90 ? "linear-gradient(90deg,#f59e0b,#ff5b79)" : "linear-gradient(90deg,#8b5cf6,#2ee88b)" }} />
      </div>
    </div>
  );
}

function ViewsChart({ hasData = true }) {
  return (
    <div style={{ height: 300, position: "relative", borderRadius: 16, background: "linear-gradient(180deg,rgba(139,92,246,0.08),rgba(139,92,246,0.02))", overflow: "hidden", border: `1px solid ${THEME.line}` }}>
      {hasData ? <svg viewBox="0 0 720 260" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
        <defs>
          <linearGradient id="viewsFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.42" />
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[40, 90, 140, 190, 240].map((y) => <line key={y} x1="0" x2="720" y1={y} y2={y} stroke="rgba(255,255,255,0.06)" />)}
        <path d="M0 210 C70 190 85 150 135 165 C185 185 205 110 270 120 C325 128 360 72 420 88 C490 110 515 36 585 58 C645 78 660 128 720 98 L720 260 L0 260 Z" fill="url(#viewsFill)" />
        <path d="M0 210 C70 190 85 150 135 165 C185 185 205 110 270 120 C325 128 360 72 420 88 C490 110 515 36 585 58 C645 78 660 128 720 98" fill="none" stroke="#8b5cf6" strokeWidth="4" />
      </svg> : (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: THEME.muted, textAlign: "center", padding: 24 }}>
          <div>
            <div style={{ color: "#fff", fontWeight: 900, fontSize: 18, marginBottom: 6 }}>No analytics yet</div>
            <div style={{ fontSize: 13 }}>Connect accounts and post clips to start building this chart.</div>
          </div>
        </div>
      )}
    </div>
  );
}

function PostClipModal({ clip, accounts = [], onClose, onPostNow, onQueue, onSchedule }) {
  const [format, setFormat] = useState("9:16");
  const [selectedAccount, setSelectedAccount] = useState(accounts[0]?.id || "");
  const [title, setTitle] = useState(clipTitle(clip));
  const [caption, setCaption] = useState("Lud made some great points here.");
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  if (!clip) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 180, background: "rgba(0,0,0,0.72)", backdropFilter: "blur(16px)", display: "grid", placeItems: "center", padding: 22 }} onClick={(event) => event.target === event.currentTarget && onClose()}>
      <Panel style={{ width: "min(1040px,96vw)", maxHeight: "92vh", overflow: "auto", padding: 22, background: "linear-gradient(145deg,rgba(20,20,34,0.99),rgba(10,10,20,0.99))" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div>
            <div style={{ color: "#fff", fontSize: 22, fontWeight: 900 }}>Post Clip</div>
            <div style={{ color: THEME.muted, fontSize: 13 }}>Format, caption, and queue this clip.</div>
          </div>
          <GhostButton onClick={onClose}>×</GhostButton>
        </div>
        <div className="post-modal-grid" style={{ display: "grid", gridTemplateColumns: "1fr 0.95fr", gap: 22 }}>
          <div>
            <div style={{ position: "relative", borderRadius: 18, overflow: "hidden", border: `1px solid ${THEME.line}`, background: "#080812", aspectRatio: "16 / 10" }}>
              {clipImage(clip) ? <img src={clipImage(clip)} alt={clipTitle(clip)} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(7,7,17,0.75),transparent)" }} />
              <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", width: 64, height: 64, borderRadius: "50%", display: "grid", placeItems: "center", background: "rgba(255,255,255,0.92)", color: "#111", fontSize: 24 }}>▶</div>
              <div style={{ position: "absolute", left: 14, bottom: 14, color: "#fff", fontWeight: 900 }}>{fmtDur(clip?.duration || 45)}</div>
            </div>
          </div>
          <div style={{ display: "grid", gap: 14 }}>
            <Field label="Select Account">
              <Select value={selectedAccount} onChange={(e) => setSelectedAccount(e.target.value)}>
                {accounts.length ? accounts.map((account) => <option key={account.id} value={account.id}>{account.display_name || account.handle || account.platform}</option>) : <option>No connected account</option>}
              </Select>
            </Field>
            <Field label="Title"><TextInput value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
            <Field label="Caption"><TextArea value={caption} onChange={(e) => setCaption(e.target.value)} style={{ minHeight: 92 }} /></Field>
            <Field label="Hashtags"><TextInput defaultValue="#clips #twitch #clipse" /></Field>
            <div>
              <div style={{ color: THEME.muted, fontSize: 12, fontWeight: 800, marginBottom: 8 }}>TikTok Format</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
                {["9:16", "1:1", "16:9"].map((entry) => (
                  <GhostButton key={entry} onClick={() => setFormat(entry)} style={{ background: format === entry ? "rgba(139,92,246,0.22)" : undefined, borderColor: format === entry ? "rgba(139,92,246,0.7)" : THEME.line }}>{entry}</GhostButton>
                ))}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <TextInput type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} />
              <TextInput type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} />
            </div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1.4fr", gap: 12, marginTop: 22 }}>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <GhostButton onClick={() => onPostNow?.({ clip, selectedAccount, title, caption, format })}>Post Now</GhostButton>
          <GhostButton onClick={() => onQueue?.({ clip, selectedAccount, title, caption, format, publishAt: null })}>Add to Queue</GhostButton>
          <PrimaryButton onClick={() => onSchedule?.({ clip, selectedAccount, title, caption, format, publishAt: scheduleDate && scheduleTime ? `${scheduleDate}T${scheduleTime}` : null })}>Schedule Post</PrimaryButton>
        </div>
      </Panel>
    </div>
  );
}


function CreatePostWizard({
  workspaceClips,
  selectedPrepClip,
  selectedPrepClipId,
  setSelectedPrepClipId,
  createPostStep,
  setCreatePostStep,
  uploadDrafts,
  setUploadDraft,
  connectedAccounts,
  postingHealth,
  postReadiness,
  checkPostReadiness,
  scheduleUpload,
  manualVideoFiles,
  setManualVideoFiles,
  uploadManualVideoAsset,
  cliprInputs,
  setCliprInputs,
  cliprRights,
  setCliprRights,
  cliprImports,
  importCliprVideoAsset,
  videoNeedsAttention,
  setTab,
}) {
  const clip = selectedPrepClip || workspaceClips[0] || null;
  const draft = clip ? uploadDrafts[clip.id] || {} : {};
  const availablePostingAccounts = connectedAccounts.filter((account) => account.status === "active" && (account.account_type === "posting" || account.account_type === "both"));
  const selectedAccount = availablePostingAccounts.find((account) => account.id === draft.postingAccountId);
  const hasVideoAsset = Boolean(clip && Number(clip.asset_count || 0) > 0);
  const hasSelectedAccount = Boolean(draft.postingAccountId);
  const readiness = clip ? postReadiness[clip.id] : null;
  const readinessPassed = Boolean(readiness?.allowed);
  const defaultCliprInput = clip ? clip.source_url || clip.source_clip_id || "" : "";
  const cliprInput = clip ? cliprInputs[clip.id] ?? defaultCliprInput : "";
  const autoCaption = clip ? generateQuickPostCaption(clip, selectedAccount?.platform || "tiktok") : "";
  const videoHelp = clip ? videoNeedsAttention[clip.id] || (!hasVideoAsset ? "Pull with Clipr or upload a video file so Clipse can format the clip for short-form posting." : "") : "";
  const canSubmit = Boolean(clip && hasVideoAsset && hasSelectedAccount && ["ready", "scheduled"].includes(clip.state));
  const steps = [
    { id: 1, title: "Choose Clip", body: "Pick one saved clip to prepare.", done: Boolean(clip) },
    { id: 2, title: "Prepare Video", body: "Pull with Clipr or upload MP4.", done: hasVideoAsset },
    { id: 3, title: "Queue Post", body: "Pick account, caption, and time.", done: Boolean(readinessPassed || clip?.state === "scheduled") },
  ];

  if (!workspaceClips.length) {
    return (
      <EmptyState
        eyebrow="CREATE POST"
        title="Save a clip before creating a post"
        body="Browse clips, save one to your workspace, then come back here to pull the video, review the caption, and queue it."
        primaryLabel="Browse Clips"
        onPrimary={() => setTab("clips")}
        secondaryLabel="Connect Account"
        onSecondary={() => setTab("accounts")}
      />
    );
  }

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <Panel style={{ padding: 18 }}>
        <div className="step-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
          {steps.map((step) => {
            const active = createPostStep === step.id;
            return (
              <button
                key={step.id}
                onClick={() => setCreatePostStep(step.id)}
                style={{ textAlign: "left", border: `1px solid ${active ? "rgba(139,92,246,0.8)" : step.done ? "rgba(46,232,139,0.35)" : THEME.line}`, background: active ? "linear-gradient(135deg,rgba(139,92,246,0.24),rgba(255,255,255,0.05))" : step.done ? "rgba(46,232,139,0.08)" : "rgba(255,255,255,0.035)", borderRadius: 16, padding: 15, cursor: "pointer" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 30, height: 30, borderRadius: "50%", display: "grid", placeItems: "center", background: active ? "linear-gradient(135deg,#9f5cff,#6d28d9)" : step.done ? "rgba(46,232,139,0.16)" : "rgba(255,255,255,0.07)", color: step.done && !active ? THEME.green : "#fff", fontWeight: 950 }}>{step.done && !active ? "✓" : step.id}</div>
                  <div style={{ color: "#fff", fontWeight: 900 }}>{step.title}</div>
                </div>
                <div style={{ color: THEME.muted, fontSize: 12, lineHeight: 1.5 }}>{step.body}</div>
              </button>
            );
          })}
        </div>
      </Panel>

      <Panel style={{ padding: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ color: "#fff", fontSize: 22, fontWeight: 950 }}>Create a post</div>
            <div style={{ color: THEME.muted, fontSize: 13, marginTop: 4 }}>One clip, one account, one clear queue action.</div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <GhostButton onClick={() => setTab("clips")}>Browse Clips</GhostButton>
            <GhostButton onClick={() => setTab("queue")}>View Queue</GhostButton>
          </div>
        </div>

        <div className="post-modal-grid" style={{ display: "grid", gridTemplateColumns: "0.9fr 1.1fr", gap: 20, alignItems: "start" }}>
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ color: THEME.muted, fontSize: 12, fontWeight: 900, letterSpacing: 1.5 }}>CHOOSE CLIP</div>
            <div style={{ display: "grid", gap: 10 }}>
              {workspaceClips.map((entry) => {
                const selected = entry.id === (selectedPrepClipId || clip?.id);
                return (
                  <button
                    key={entry.id}
                    onClick={() => { setSelectedPrepClipId(entry.id); setCreatePostStep(2); }}
                    style={{ display: "grid", gridTemplateColumns: "86px 1fr auto", gap: 12, alignItems: "center", textAlign: "left", background: selected ? "rgba(139,92,246,0.18)" : "rgba(255,255,255,0.035)", border: `1px solid ${selected ? "rgba(139,92,246,0.72)" : THEME.line}`, borderRadius: 14, padding: 10, cursor: "pointer" }}
                  >
                    {entry.thumbnail_url ? <img src={entry.thumbnail_url} alt={entry.title} style={{ width: 86, height: 54, objectFit: "cover", borderRadius: 10 }} /> : <div style={{ width: 86, height: 54, borderRadius: 10, background: "rgba(255,255,255,0.06)" }} />}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: "#fff", fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entry.title}</div>
                      <div style={{ color: THEME.muted, fontSize: 12, marginTop: 4 }}>{entry.display_name || entry.login || "Clipse"} • {fmtDur(entry.duration_sec || entry.duration || 30)}</div>
                    </div>
                    <Tag color={Number(entry.asset_count || 0) > 0 ? "#4ade8018" : "#facc1518"} text={Number(entry.asset_count || 0) > 0 ? "#4ade80" : "#facc15"}>{Number(entry.asset_count || 0) > 0 ? "Ready" : "Needs video"}</Tag>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: "grid", gap: 14 }}>
            <div style={{ border: `1px solid ${THEME.line}`, borderRadius: 18, overflow: "hidden", background: "#080812" }}>
              <div style={{ position: "relative", aspectRatio: "16 / 9", background: "rgba(255,255,255,0.04)" }}>
                {clip?.thumbnail_url ? <img src={clip.thumbnail_url} alt={clip.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(7,7,17,0.84),transparent 55%)" }} />
                <div style={{ position: "absolute", left: 16, bottom: 16, right: 16 }}>
                  <div style={{ color: "#fff", fontSize: 18, fontWeight: 950, marginBottom: 4 }}>{clip?.title}</div>
                  <div style={{ color: "#d8d4e5", fontSize: 12 }}>{clip?.display_name || clip?.login || "Clipse"}</div>
                </div>
              </div>
              <ClipLifecycleTimeline clip={clip} hasVideoAsset={hasVideoAsset} hasPostingAccount={hasSelectedAccount} readiness={readiness} />
            </div>

            <NextActionCard
              title={!hasVideoAsset ? "Prepare the video" : !hasSelectedAccount ? "Choose posting account" : readiness?.allowed === false ? "Fix readiness blockers" : readinessPassed ? "Ready to queue" : "Review before queueing"}
              body={!hasVideoAsset ? "Clipse can pull the video with Clipr or you can upload a file manually." : !hasSelectedAccount ? "Select the TikTok, Instagram, or YouTube account that should receive this post." : readiness?.allowed === false ? "Resolve the warnings below, then run the check again." : readinessPassed ? "Everything looks good. Add it to the queue or schedule it for later." : "Run a readiness check to catch token, cooldown, and caption issues before queueing."}
              tone={!hasVideoAsset ? "yellow" : readiness?.allowed === false ? "red" : readinessPassed ? "green" : "purple"}
            >
              {!hasVideoAsset ? <GhostButton onClick={() => setCreatePostStep(2)}>Prepare</GhostButton> : !hasSelectedAccount ? <GhostButton onClick={() => setCreatePostStep(3)}>Choose Account</GhostButton> : readinessPassed ? <PrimaryButton onClick={() => scheduleUpload(clip)}>Add to Queue</PrimaryButton> : <GhostButton onClick={() => checkPostReadiness(clip)} disabled={readiness?.loading}>{readiness?.loading ? "Checking..." : "Check"}</GhostButton>}
            </NextActionCard>

            {createPostStep <= 2 ? (
              <Panel style={{ padding: 16, background: "rgba(255,255,255,0.035)" }}>
                <div style={{ color: "#fff", fontWeight: 950, marginBottom: 10 }}>Video preparation</div>
                <div style={{ display: "grid", gap: 12 }}>
                  <Field label="CLIPR URL OR SLUG">
                    <TextInput value={cliprInput} onChange={(e) => setCliprInputs((prev) => ({ ...prev, [clip.id]: e.target.value }))} placeholder="Clipr slug or Twitch clip URL..." />
                  </Field>
                  <label style={{ display: "flex", gap: 8, alignItems: "flex-start", color: "#aaa", fontSize: 12, lineHeight: 1.55 }}>
                    <input type="checkbox" checked={Boolean(cliprRights[clip.id])} onChange={(e) => setCliprRights((prev) => ({ ...prev, [clip.id]: e.target.checked }))} />
                    I confirm I have rights or permission to import, store, format, and repost this video.
                  </label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <PrimaryButton onClick={() => importCliprVideoAsset(clip)} disabled={cliprImports[clip.id] || !cliprRights[clip.id]} style={{ opacity: cliprImports[clip.id] || !cliprRights[clip.id] ? 0.55 : 1 }}>{cliprImports[clip.id] ? "Pulling..." : hasVideoAsset ? "Pull Again" : "Pull With Clipr"}</PrimaryButton>
                    <GhostButton onClick={() => hasVideoAsset && setCreatePostStep(3)} disabled={!hasVideoAsset} style={{ opacity: hasVideoAsset ? 1 : 0.45 }}>Continue</GhostButton>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "end" }}>
                    <Field label="OR UPLOAD VIDEO FILE">
                      <input type="file" accept="video/mp4,video/quicktime,video/webm" onChange={(e) => setManualVideoFiles((prev) => ({ ...prev, [clip.id]: e.target.files?.[0] || null }))} style={{ width: "100%", background: "#080810", border: "1px solid #202034", borderRadius: 8, color: "#aaa", padding: 10, fontSize: 11 }} />
                    </Field>
                    <GhostButton onClick={() => uploadManualVideoAsset(clip)} disabled={!manualVideoFiles[clip.id]}>Upload</GhostButton>
                  </div>
                  {videoHelp ? <div style={{ background: "#facc1510", border: "1px solid #facc1533", borderRadius: 10, padding: "9px 12px", color: "#facc15", fontSize: 12, lineHeight: 1.55 }}>{videoHelp}</div> : null}
                </div>
              </Panel>
            ) : null}

            {createPostStep >= 3 ? (
              <Panel style={{ padding: 16, background: "rgba(255,255,255,0.035)" }}>
                <div style={{ color: "#fff", fontWeight: 950, marginBottom: 10 }}>Post details</div>
                <div style={{ display: "grid", gap: 12 }}>
                  <Field label="POSTING ACCOUNT">
                    <Select value={draft.postingAccountId || ""} onChange={(e) => setUploadDraft(clip.id, { postingAccountId: e.target.value })}>
                      <option value="">Select account</option>
                      {availablePostingAccounts.map((account) => {
                        const health = postingHealth.find((entry) => entry.account?.id === account.id);
                        const suffix = health ? ` - ${health.metrics.postsLast24h}/${health.policy.dailyPostLimit} today${health.allowed ? "" : " - limited"}` : "";
                        return <option key={account.id} value={account.id}>{account.display_name} ({account.platform}){suffix}</option>;
                      })}
                    </Select>
                  </Field>
                  <Field label="TITLE"><TextInput value={draft.title || clip.title} onChange={(e) => setUploadDraft(clip.id, { title: e.target.value })} /></Field>
                  <Field label="CAPTION"><TextArea value={draft.caption || autoCaption} onChange={(e) => setUploadDraft(clip.id, { caption: e.target.value })} style={{ minHeight: 86 }} /></Field>
                  <Field label="PUBLISH AT (OPTIONAL)"><TextInput type="datetime-local" value={draft.publishAt || ""} onChange={(e) => setUploadDraft(clip.id, { publishAt: e.target.value })} /></Field>
                  {readiness && !readiness.loading ? (
                    <div style={{ border: `1px solid ${readiness.allowed ? "#4ade8033" : "#facc1533"}`, background: readiness.allowed ? "#06140b" : "#161204", borderRadius: 12, padding: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 8 }}>
                        <div style={{ color: "#fff", fontSize: 12, fontWeight: 900 }}>Readiness check</div>
                        <Tag color={readiness.allowed ? "#4ade8018" : "#facc1518"} text={readiness.allowed ? "#4ade80" : "#facc15"}>{readiness.allowed ? "READY" : "NEEDS ATTENTION"}</Tag>
                      </div>
                      {readiness.blockers?.length ? <div style={{ color: "#facc15", fontSize: 11, lineHeight: 1.6 }}>{readiness.blockers.join(" • ")}</div> : null}
                      {readiness.warnings?.length ? <div style={{ color: "#aaa", fontSize: 11, lineHeight: 1.6 }}>{readiness.warnings.join(" • ")}</div> : null}
                    </div>
                  ) : null}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 10 }}>
                    <GhostButton onClick={() => checkPostReadiness(clip)} disabled={!hasSelectedAccount || readiness?.loading}>{readiness?.loading ? "Checking..." : "Check readiness"}</GhostButton>
                    <PrimaryButton onClick={() => scheduleUpload(clip)} disabled={!canSubmit} style={{ opacity: canSubmit ? 1 : 0.5 }}>{draft.publishAt ? "Schedule Post" : "Add to Queue"}</PrimaryButton>
                  </div>
                </div>
              </Panel>
            ) : null}
          </div>
        </div>
      </Panel>
    </div>
  );
}

function QuickPostAccountModal({ clip, accounts = [], loading, onClose, onConfirm }) {
  const [selectedAccountId, setSelectedAccountId] = useState(accounts[0]?.id || "");
  const selectedAccount = accounts.find((account) => account.id === selectedAccountId) || accounts[0];
  const captionPreview = generateQuickPostCaption(clip, selectedAccount?.platform || "tiktok");
  if (!clip) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 190, background: "rgba(0,0,0,0.72)", backdropFilter: "blur(16px)", display: "grid", placeItems: "center", padding: 22 }} onClick={(event) => event.target === event.currentTarget && onClose()}>
      <Panel style={{ width: "min(560px,95vw)", padding: 22, background: "linear-gradient(145deg,rgba(20,20,34,0.99),rgba(10,10,20,0.99))" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", marginBottom: 18 }}>
          <div>
            <div style={{ color: "#fff", fontSize: 22, fontWeight: 900 }}>Quick Post</div>
            <div style={{ color: THEME.muted, fontSize: 13, marginTop: 4 }}>Choose the account. Clipse will pull with Clipr and queue it automatically.</div>
          </div>
          <GhostButton onClick={onClose}>×</GhostButton>
        </div>

        <div style={{ display: "flex", gap: 14, alignItems: "center", background: "rgba(255,255,255,0.04)", border: `1px solid ${THEME.line}`, borderRadius: 16, padding: 12, marginBottom: 18 }}>
          {clipImage(clip) ? <img src={clipImage(clip)} alt={clipTitle(clip)} style={{ width: 88, height: 58, borderRadius: 12, objectFit: "cover" }} /> : <div style={{ width: 88, height: 58, borderRadius: 12, background: THEME.purpleSoft }} />}
          <div style={{ minWidth: 0 }}>
            <div style={{ color: "#fff", fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{clipTitle(clip)}</div>
            <div style={{ color: THEME.muted, fontSize: 12, marginTop: 4 }}>{clipCreator(clip)} • {fmtDur(clip?.duration || clip?.duration_sec || 45)}</div>
          </div>
        </div>

        <div style={{ display: "grid", gap: 10, marginBottom: 18 }}>
          {accounts.map((account) => {
            const platform = PLATFORMS[account.platform] || PLATFORMS.tiktok;
            const selected = selectedAccountId === account.id;
            return (
              <button
                key={account.id}
                onClick={() => setSelectedAccountId(account.id)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  textAlign: "left",
                  background: selected ? "rgba(139,92,246,0.18)" : "rgba(255,255,255,0.04)",
                  border: `1px solid ${selected ? "rgba(139,92,246,0.72)" : THEME.line}`,
                  borderRadius: 14,
                  padding: 13,
                  cursor: "pointer",
                  color: "#fff",
                }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 12, display: "grid", placeItems: "center", background: `${platform.color}22`, border: `1px solid ${platform.color}55`, color: platform.color, fontWeight: 900 }}>{platform.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 900 }}>{account.display_name || platform.label}</div>
                  <div style={{ color: THEME.muted, fontSize: 12 }}>{platform.label} • {account.account_type}</div>
                </div>
                {selected ? <Tag color="rgba(139,92,246,0.2)" text="#c4b5fd">Selected</Tag> : null}
              </button>
            );
          })}
        </div>

        <div style={{ background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.28)", borderRadius: 14, padding: 13, marginBottom: 18 }}>
          <div style={{ color: "#fff", fontWeight: 900, marginBottom: 6, fontSize: 13 }}>Auto-generated caption</div>
          <div style={{ color: THEME.muted, fontSize: 12, lineHeight: 1.6 }}>{captionPreview}</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 12 }}>
          <GhostButton onClick={onClose} disabled={loading}>Cancel</GhostButton>
          <PrimaryButton onClick={() => onConfirm(selectedAccountId, captionPreview)} disabled={!selectedAccountId || loading}>
            {loading ? "Queueing..." : "Pull With Clipr + Queue"}
          </PrimaryButton>
        </div>
      </Panel>
    </div>
  );
}

function DashboardApp({ initialTab = "home", initialAuthMode = "login", authOnly = false }) {
  const [tab, setTab] = useState(initialTab);
  const [clipFilter, setClipFilter] = useState("All");
  const [clipTimeRange, setClipTimeRange] = useState("7d");
  const [accountFilter, setAccountFilter] = useState("All Accounts");
  const [postModalClip, setPostModalClip] = useState(null);
  const [quickPostRequest, setQuickPostRequest] = useState(null);
  const [upgradePrompt, setUpgradePrompt] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [selectedBillingPlan, setSelectedBillingPlan] = useState(null);
  const [settingsSection, setSettingsSection] = useState("billing");
  const [workspaceSettings, setWorkspaceSettings] = useState({
    workspaceName: "Clipse Workspace",
    ownerEmail: "admin@clipse.local",
    timezone: "America/New_York",
    currency: "USD",
    defaultFormat: "9:16",
    captionStyle: "High-energy hook",
    defaultHashtags: "#clips #gaming #streamer",
    scheduleWindow: "7PM - 9PM",
    dailyPostLimit: "3",
    postSpacingMinutes: "90",
    reviewBeforePost: true,
    duplicateCaptionProtection: false,
    watermarkFree: true,
    uploadFailedAlerts: true,
    queueStuckAlerts: true,
    tokenExpiredAlerts: true,
    postReadyAlerts: true,
    memberCanPost: false,
    requireAdminApproval: true,
    dataRetention: "180 days",
  });
  const [searchInput, setSearchInput] = useState("");
  const [streamers, setStreamers] = useState([]);
  const [streams, setStreams] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [clips, setClips] = useState([]);
  const [clipsLoading, setClipsLoading] = useState(false);
  const [streamerLoading, setStreamerLoading] = useState(false);
  const [previewClip, setPreviewClip] = useState(null);
  const [previewStreamer, setPreviewStreamer] = useState(null);
  const [queue, setQueue] = useState([]);
  const [backendQueue, setBackendQueue] = useState([]);
  const [billingUsage, setBillingUsage] = useState(null);
  const [notif, setNotif] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [proxyOk, setProxyOk] = useState(null);

  const [urlInput, setUrlInput] = useState("");
  const [urlLoading, setUrlLoading] = useState(false);
  const [urlProgress, setUrlProgress] = useState(0);
  const [urlStage, setUrlStage] = useState("");
  const [urlClips, setUrlClips] = useState(null);
  const [urlStreamer, setUrlStreamer] = useState(null);

  const [backendOk, setBackendOk] = useState(null);
  const [authMode, setAuthMode] = useState(initialAuthMode);
  const [token, setToken] = useState(() => localStorage.getItem("clipse-backend-token") || localStorage.getItem("cliphive-backend-token") || "");
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem("clipse-backend-refresh-token") || localStorage.getItem("cliphive-backend-refresh-token") || "");
  const [currentUser, setCurrentUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ companyName: "", email: "", password: "" });
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [oauthProviders, setOauthProviders] = useState([]);
  const [postingHealth, setPostingHealth] = useState([]);
  const [backendClips, setBackendClips] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminSummary, setAdminSummary] = useState(null);
  const [platformUsers, setPlatformUsers] = useState([]);
  const [platformTenants, setPlatformTenants] = useState([]);
  const [platformSummary, setPlatformSummary] = useState(null);
  const [productionReadiness, setProductionReadiness] = useState(null);
  const [platformSearch, setPlatformSearch] = useState("");
  const [uploadAttempts, setUploadAttempts] = useState([]);
  const [providerStatus, setProviderStatus] = useState([]);
  const [postReadiness, setPostReadiness] = useState({});
  const [manualVideoFiles, setManualVideoFiles] = useState({});
  const [videoNeedsAttention, setVideoNeedsAttention] = useState({});
  const [cliprInputs, setCliprInputs] = useState({});
  const [cliprRights, setCliprRights] = useState({});
  const [cliprImports, setCliprImports] = useState({});
  const [quickPosting, setQuickPosting] = useState({});

  const [accountForm, setAccountForm] = useState({
    platform: "tiktok",
    platformAccountId: "",
    displayName: "",
    accountType: "posting",
    accessToken: "demo-access-token",
    refreshToken: "demo-refresh-token",
    tokenExpiresAt: "",
  });
  const [adminUserForm, setAdminUserForm] = useState({ email: "", password: "", role: "standard_user" });
  const [uploadDrafts, setUploadDrafts] = useState({});
  const [selectedPrepClipId, setSelectedPrepClipId] = useState("");
  const [createPostStep, setCreatePostStep] = useState(1);

  const notify = (msg, type = "ok") => {
    if (String(msg || "").trim().toLowerCase() === "unauthorized") return;
    setNotif({ msg, type });
    setTimeout(() => setNotif(null), 3200);
  };

  const isPlanLimitReached = (limitName, increment = 1) => {
    if (!billingUsage) return false;
    const current = Number(billingUsage.usage?.[limitName] || 0);
    const limit = Number(billingUsage.limits?.[limitName] || 0);
    return limit > 0 && current + increment > limit;
  };

  const showUpgrade = (reason, limitName) => {
    setUpgradePrompt({ reason, limitName });
    setSettingsSection("billing");
    setTab("settings");
  };

  const hasFeature = (featureName) => Boolean(billingUsage?.plan?.[featureName]);

  const updateWorkspaceSetting = (key, value) => {
    setWorkspaceSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleBillingSelect = (planKey, plan) => {
    setSelectedBillingPlan(planKey);
    if (!currentUser) {
      setAuthMode(planKey === "free" ? "register" : "login");
      setTab("portal");
      notify(`${plan.label} selected. Log in or create an account to continue.`, "ok");
      return;
    }
    const planRank = { free: 0, starter: 1, pro: 2 };
    const currentPlanKey = billingUsage?.plan?.key;
    if (planKey === currentPlanKey) return;
    if ((planRank[planKey] || 0) < (planRank[currentPlanKey] || 0)) {
      notify(`You're already on ${billingUsage?.plan?.label || "a higher plan"}.`, "ok");
      return;
    }
    notify(`Billing checkout for ${plan.label} will connect to Stripe next`, "ok");
  };

  const postingAccountsForPreview = useMemo(() => {
    if (!currentUser) return [];
    const accounts = connectedAccounts
      .filter((acc) => acc.status === "active" && (acc.account_type === "posting" || acc.account_type === "both"))
      .sort((a, b) => platformPriority(a.platform) - platformPriority(b.platform))
      .map((acc) => {
        const health = postingHealth.find((entry) => entry.account?.id === acc.id);
        return {
          health,
          id: acc.id,
          handle: acc.display_name,
          platform: acc.platform,
          followers: "linked",
          postsToday: health?.metrics?.postsLast24h || 0,
          postLimit: health?.policy?.dailyPostLimit || 5,
          status: acc.status !== "active" ? "cooldown" : health?.allowed === false ? "cooldown" : "active",
        };
      });
    return accounts;
  }, [connectedAccounts, currentUser, postingHealth]);

  const isAdmin = ["tenant_admin", "platform_admin"].includes(currentUser?.role);
  const isPlatformAdmin = currentUser?.role === "platform_admin";
  const visibleSettingsSections = isAdmin
    ? [
        ["billing", "Plan & Billing"],
        ["workspace", "Workspace Profile"],
        ["posting", "Posting Defaults"],
        ["safety", "Safety & Automation"],
        ["notifications", "Notifications"],
        ["team", "Team Permissions"],
        ["privacy", "Data & Privacy"],
      ]
    : [
        ["billing", "Plan & Billing"],
        ["posting", "Posting Defaults"],
        ["notifications", "Notifications"],
      ];
  const visibleSettingsKeys = visibleSettingsSections.map(([section]) => section);

  useEffect(() => {
    fetch(`${TWITCH_PROXY.replace(/\/api\/twitch\/?$/, "")}/health`)
      .then((r) => setProxyOk(r.ok))
      .catch(() => setProxyOk(false));

    fetch(`${BACKEND}/health`)
      .then((r) => setBackendOk(r.ok))
      .catch(() => setBackendOk(false));
  }, []);

  useEffect(() => {
    if (tab === "settings" && !visibleSettingsKeys.includes(settingsSection)) {
      setSettingsSection(visibleSettingsKeys[0] || "posting");
    }
  }, [settingsSection, tab, visibleSettingsKeys]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const page = params.get("page");
    if (page === "terms" || page === "privacy") {
      setTab(page);
    }
    const oauthPlatform = params.get("oauth");
    const oauthStatus = params.get("status");
    if (!oauthPlatform || !oauthStatus) return;

    setTab(params.get("tab") || "accounts");
    if (oauthStatus === "connected") {
      notify(`${PLATFORMS[oauthPlatform]?.label || oauthPlatform} connected${params.get("account") ? `: ${params.get("account")}` : ""}`);
      if (token) loadBackendData(currentUser, token);
    } else {
      notify(`${PLATFORMS[oauthPlatform]?.label || oauthPlatform} connect failed: ${params.get("reason") || "Unknown error"}`, "err");
    }
    window.history.replaceState({}, "", window.location.pathname);
    // We only want to process the OAuth callback query once after landing back in the app.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (proxyOk) {
      loadDefaultStreamers();
    }
    // Startup reload when proxy state flips to healthy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proxyOk]);

  useEffect(() => {
    if (!token) {
      setCurrentUser(null);
      return;
    }
    loadBackendSession();
    // Token changes are the only trigger we want for session bootstrap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const saveToken = (nextToken) => {
    setToken(nextToken);
    if (nextToken) localStorage.setItem("clipse-backend-token", nextToken);
    else localStorage.removeItem("clipse-backend-token");
    localStorage.removeItem("cliphive-backend-token");
  };

  const saveRefreshToken = (nextToken) => {
    setRefreshToken(nextToken);
    if (nextToken) localStorage.setItem("clipse-backend-refresh-token", nextToken);
    else localStorage.removeItem("clipse-backend-refresh-token");
    localStorage.removeItem("cliphive-backend-refresh-token");
  };

  const refreshBackendAccessToken = async () => {
    if (!refreshToken) {
      const error = new Error("Your session expired. Please log in again.");
      error.code = "TOKEN_INVALID";
      throw error;
    }
    const refreshed = await backendFetch("/api/auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refreshToken }),
    });
    saveToken(refreshed.accessToken);
    saveRefreshToken(refreshed.refreshToken);
    return refreshed.accessToken;
  };

  const authedBackendFetch = async (path, options = {}, authToken = token) => {
    try {
      return await backendFetch(path, options, authToken);
    } catch (error) {
      if (!isAuthTokenError(error)) throw error;
      try {
        const nextToken = await refreshBackendAccessToken();
        return await backendFetch(path, options, nextToken);
      } catch (refreshError) {
        saveToken("");
        saveRefreshToken("");
        setCurrentUser(null);
        setTab("portal");
        throw new Error(isAuthTokenError(refreshError) ? "Your session expired. Please log in again." : refreshError.message);
      }
    }
  };

  const loadDefaultStreamers = async () => {
    setStreamerLoading(true);
    setApiError(null);
    try {
      const query = DEFAULT_STREAMERS.map((login) => `login=${login}`).join("&");
      const data = await twitchGet(`/users?${query}`);
      const users = data.data || [];
      setStreamers(users);
      if (users.length) {
        const ids = users.map((user) => user.id).join("&user_id=");
        const streamData = await twitchGet(`/streams?user_id=${ids}`);
        const streamMap = {};
        (streamData.data || []).forEach((stream) => {
          streamMap[stream.user_id] = stream;
        });
        setStreams(streamMap);
        selectStreamer(users[0], streamMap);
      }
    } catch (error) {
      setApiError(error.message);
    }
    setStreamerLoading(false);
  };

  const loadBackendSession = async () => {
    try {
      const me = await backendFetch("/api/auth/me", {}, token);
      setCurrentUser(me.user);
      setBackendOk(true);
      await loadBackendData(me.user, token);
    } catch (error) {
      if (refreshToken) {
        try {
          const refreshed = await backendFetch("/api/auth/refresh", {
            method: "POST",
            body: JSON.stringify({ refreshToken }),
          });
          saveToken(refreshed.accessToken);
          saveRefreshToken(refreshed.refreshToken);
          const me = await backendFetch("/api/auth/me", {}, refreshed.accessToken);
          setCurrentUser(me.user);
          setBackendOk(true);
          await loadBackendData(me.user, refreshed.accessToken);
          return;
        } catch (refreshError) {
          notify(isAuthTokenError(refreshError) ? "Your session expired. Please log in again." : refreshError.message, "err");
        }
      } else {
        notify(isAuthTokenError(error) ? "Your session expired. Please log in again." : error.message, "err");
      }
      setBackendOk(false);
      saveToken("");
      saveRefreshToken("");
      setCurrentUser(null);
      setTab("portal");
    }
  };

  const loadBackendData = async (user = currentUser, authToken = token) => {
    if (!authToken) return;
    const requiredRequests = await Promise.allSettled([
      backendFetch("/api/accounts/connected", {}, authToken),
      backendFetch("/api/accounts/posting-health", {}, authToken),
      backendFetch("/api/clips", {}, authToken),
      backendFetch("/api/queue", {}, authToken),
      backendFetch("/api/billing/usage", {}, authToken),
    ]);
    const authFailure = requiredRequests.find((result) => result.status === "rejected" && isAuthTokenError(result.reason));
    if (authFailure) throw authFailure.reason;
    const [accountsRes, healthRes, clipsRes, queueRes, usageRes] = requiredRequests.map((result) => (result.status === "fulfilled" ? result.value : { data: [] }));
    setConnectedAccounts(accountsRes.data || []);
    setPostingHealth(healthRes.data || []);
    setBackendClips(clipsRes.data || []);
    setBackendQueue(queueRes.data || []);
    setBillingUsage(usageRes.data || null);
    backendFetch("/api/oauth/providers", {}, authToken)
      .then((providersRes) => setOauthProviders(providersRes.data || []))
      .catch(() => setOauthProviders([]));

    if (["tenant_admin", "platform_admin"].includes(user?.role)) {
      const adminRequests = await Promise.allSettled([
        backendFetch("/api/admin/users", {}, authToken),
        backendFetch("/api/admin/summary", {}, authToken),
        backendFetch("/api/admin/upload-attempts", {}, authToken),
        backendFetch("/api/admin/provider-status", {}, authToken),
      ]);
      const [usersRes, summaryRes, attemptsRes, providerStatusRes] = adminRequests.map((result) => (result.status === "fulfilled" ? result.value : { data: [] }));
      setAdminUsers(usersRes.data || []);
      setAdminSummary(summaryRes);
      setUploadAttempts(attemptsRes.data || []);
      setProviderStatus(providerStatusRes.data || []);
    } else {
      setAdminUsers([]);
      setAdminSummary(null);
      setUploadAttempts([]);
      setProviderStatus([]);
    }

    if (user?.role === "platform_admin") {
      const platformRequests = await Promise.allSettled([
        backendFetch("/api/platform/users", {}, authToken),
        backendFetch("/api/platform/tenants", {}, authToken),
        backendFetch("/api/platform/summary", {}, authToken),
        backendFetch("/api/platform/production-readiness", {}, authToken),
      ]);
      const [platformUsersRes, platformTenantsRes, platformSummaryRes, productionReadinessRes] = platformRequests.map((result) => (result.status === "fulfilled" ? result.value : { data: [] }));
      setPlatformUsers(platformUsersRes.data || []);
      setPlatformTenants(platformTenantsRes.data || []);
      setPlatformSummary(platformSummaryRes || null);
      setProductionReadiness(productionReadinessRes.data || null);
    } else {
      setPlatformUsers([]);
      setPlatformTenants([]);
      setPlatformSummary(null);
      setProductionReadiness(null);
    }
  };

  const selectStreamer = async (user, rangeKey = clipTimeRange) => {
    setSelectedUser(user);
    setClips([]);
    setClipsLoading(true);
    try {
      const data = await getTopClips(user.id, 12, rangeKey);
      setClips(data);
    } catch (error) {
      notify(`Clips error: ${error.message}`, "err");
    }
    setClipsLoading(false);
  };

  const searchStreamer = async () => {
    if (!searchInput.trim()) return;
    setStreamerLoading(true);
    try {
      const user = await getUser(searchInput.trim());
      if (!user) {
        notify(`"${searchInput}" not found`, "err");
        setStreamerLoading(false);
        return;
      }
      setStreamers((prev) => (prev.find((entry) => entry.id === user.id) ? prev : [user, ...prev]));
      const streamData = await twitchGet(`/streams?user_id=${user.id}`);
      const stream = streamData.data?.[0];
      if (stream) setStreams((prev) => ({ ...prev, [user.id]: stream }));
      selectStreamer(user);
      setSearchInput("");
    } catch (error) {
      notify(error.message, "err");
    }
    setStreamerLoading(false);
  };

  const extractFromUrl = async () => {
    if (!urlInput.trim()) return;
    setUrlLoading(true);
    setUrlClips(null);
    setUrlProgress(0);
    const twitchMatch = urlInput.match(/twitch\.tv\/(?!videos\/)([a-zA-Z0-9_]+)/);

    if (twitchMatch) {
      const login = twitchMatch[1];
      const stages = ["Resolving channel..", "Fetching top clips..", "Scoring by virality..", "Ranking results.."];
      for (let i = 0; i < stages.length; i += 1) {
        setUrlStage(stages[i]);
        for (let progress = (i / stages.length) * 100; progress < ((i + 1) / stages.length) * 100; progress += 5) {
          await new Promise((resolve) => setTimeout(resolve, 45));
          setUrlProgress(Math.round(progress));
        }
      }
      try {
        const user = await getUser(login);
        if (user) {
          const topClips = await getTopClips(user.id, 6, clipTimeRange);
          setUrlClips(topClips);
          setUrlStreamer(user);
          setStreamers((prev) => (prev.find((entry) => entry.id === user.id) ? prev : [user, ...prev]));
        } else {
          setUrlClips("notfound");
        }
      } catch (error) {
        notify(`Failed: ${error.message}`, "err");
        setUrlClips("notfound");
      }
    } else {
      const stages = ["Fetching VOD metadata..", "Analyzing chat spikes..", "Detecting audio peaks..", "Scoring candidates.."];
      for (let i = 0; i < stages.length; i += 1) {
        setUrlStage(stages[i]);
        for (let progress = (i / stages.length) * 100; progress < ((i + 1) / stages.length) * 100; progress += 3) {
          await new Promise((resolve) => setTimeout(resolve, 55));
          setUrlProgress(Math.round(progress));
        }
      }
      setUrlClips("non-twitch");
    }
    setUrlProgress(100);
    setUrlLoading(false);
  };

  const handleQueue = async ({ clip, streamer, accounts, caption, scheduleTime, score }) => {
    if (!currentUser) {
      notify("Log in before queueing clips", "err");
      setTab("portal");
      return;
    }
    try {
      const response = await backendFetch("/api/clips/queue-external", {
        method: "POST",
        body: JSON.stringify({
          clip: {
            id: clip.id,
            title: clip.title,
            url: clip.url,
            thumbnailUrl: clip.thumbnail_url,
            duration: clip.duration,
            viewCount: clip.view_count,
          },
          streamer: {
            id: streamer.id,
            login: streamer.login,
            displayName: streamer.display_name,
            profileImageUrl: streamer.profile_image_url,
          },
          postingAccountIds: accounts.map((account) => account.id),
          caption,
          scheduleTime,
        }),
      }, token);
      setQueue((prev) => [
        {
          id: response.data?.clip?.id || Date.now(),
          title: clip.title,
          streamer: streamer.display_name,
          accounts: accounts.map((account) => account.handle),
          caption,
          scheduled: scheduleTime === "now" ? "NOW" : `+${scheduleTime}`,
          status: "backend queued",
          score,
          thumb: clip.thumbnail_url,
        },
        ...prev,
      ]);
      notify(`Backend queued ${accounts.length} upload job${accounts.length > 1 ? "s" : ""}`);
      await loadBackendData();
    } catch (error) {
      notify(error.blockers?.[0] || error.message, "err");
      throw error;
    }
  };

  const importClipForOperations = async ({ clip, streamer }) => {
    if (!currentUser) {
      notify("Log in before importing clips", "err");
      setTab("portal");
      return;
    }
    let importedClipId = null;
    try {
      const response = await authedBackendFetch("/api/clips/import-external", {
        method: "POST",
        body: JSON.stringify({
          clip: {
            id: clip.id,
            title: clip.title,
            url: clip.url,
            thumbnailUrl: clip.thumbnail_url,
            duration: clip.duration,
            viewCount: clip.view_count,
          },
          streamer: {
            id: streamer.id,
            login: streamer.login,
            displayName: streamer.display_name,
            profileImageUrl: streamer.profile_image_url,
          },
        }),
      });
      importedClipId = response.data?.clip?.id;
      notify("Clip metadata saved. Preparing video...");
      if (importedClipId) {
        setUploadDraft(importedClipId, { title: clip.title, publishAt: "" });
        try {
          await authedBackendFetch(`/api/clips/${importedClipId}/assets/import-clipr`, {
            method: "POST",
            body: JSON.stringify({
              cliprInput: clip.url || clip.id,
              confirmRights: true,
            }),
          });
          notify("Clip saved with Clipr and marked ready");
        } catch (prepareError) {
          setVideoNeedsAttention((prev) => ({
            ...prev,
            [importedClipId]: "Clipr could not prepare this clip automatically. Upload the clip file below to finish preparing it.",
          }));
          notify("Clip saved. Upload a video file to finish preparing it.", "err");
        }
      } else {
        notify("Clip saved to Workspace");
      }
      await loadBackendData();
      setTab("ops");
    } catch (error) {
      notify(error.message || "Clip could not be imported", "err");
      throw error;
    }
  };

  const activePostingAccounts = () => connectedAccounts
    .filter((account) => account.status === "active" && (account.account_type === "posting" || account.account_type === "both"))
    .sort((a, b) => platformPriority(a.platform) - platformPriority(b.platform));

  const startQuickPost = ({ clip, streamer }) => {
    if (!currentUser) {
      notify("Log in before using Quick Post", "err");
      setTab("portal");
      return;
    }

    const accounts = activePostingAccounts();
    if (!accounts.length) {
      notify("Connect a posting account before using Quick Post", "err");
      setTab("accounts");
      return;
    }
    if (isPlanLimitReached("monthlyUploadJobs", 1)) {
      showUpgrade("You’ve used all Quick Posts included in your current plan.", "monthlyUploadJobs");
      return;
    }

    setQuickPostRequest({ clip, streamer });
  };

  const quickPostClip = async ({ clip, streamer, postingAccountId, caption }) => {
    if (!currentUser) {
      notify("Log in before using Quick Post", "err");
      setTab("portal");
      return;
    }

    const postingAccount = activePostingAccounts().find((account) => account.id === postingAccountId);
    if (!postingAccount) {
      notify("Choose an active posting account first", "err");
      return;
    }

    const quickKey = clip.id || clip.source_clip_id || clip.title;
    if (quickPosting[quickKey]) return;

    setQuickPosting((prev) => ({ ...prev, [quickKey]: true }));
    try {
      let preparedClip = clip;
      let preparedClipId = clip.source_clip_id ? clip.id : null;

      if (!preparedClipId) {
        const sourceStreamer = streamer || selectedUser || {
          id: clip.broadcaster_id || "manual",
          login: clipCreator(clip),
          display_name: clipCreator(clip),
          profile_image_url: "",
        };
        notify("Quick Post: saving clip metadata...");
        const response = await authedBackendFetch("/api/clips/import-external", {
          method: "POST",
          body: JSON.stringify({
            clip: {
              id: clip.id,
              title: clipTitle(clip),
              url: clip.url || clip.source_url,
              thumbnailUrl: clipImage(clip),
              duration: clip.duration || clip.duration_sec,
              viewCount: clip.view_count || clip.viewCount,
            },
            streamer: {
              id: sourceStreamer.id,
              login: sourceStreamer.login || clipCreator(clip),
              displayName: sourceStreamer.display_name || sourceStreamer.displayName || clipCreator(clip),
              profileImageUrl: sourceStreamer.profile_image_url || sourceStreamer.profileImageUrl || "",
            },
          }),
        });
        preparedClip = response.data?.clip || clip;
        preparedClipId = preparedClip.id;
      }

      if (!preparedClipId) throw new Error("Could not save this clip before queueing.");

      if (!Number(preparedClip.asset_count || clip.asset_count || 0)) {
        notify("Quick Post: pulling video with Clipr...");
        await authedBackendFetch(`/api/clips/${preparedClipId}/assets/import-clipr`, {
          method: "POST",
          body: JSON.stringify({
            cliprInput: preparedClip.source_url || clip.source_url || clip.url || preparedClip.source_clip_id || clip.id,
            confirmRights: true,
          }),
        });
      }

      notify("Quick Post: adding to queue...");
      const generatedCaption = caption || generateQuickPostCaption(preparedClip, postingAccount.platform || "tiktok");
      await authedBackendFetch(`/api/clips/${preparedClipId}/schedule-upload`, {
        method: "POST",
        body: JSON.stringify({
          postingAccountId: postingAccount.id,
          platform: postingAccount.platform || "tiktok",
          title: clipTitle(preparedClip),
          caption: generatedCaption,
          publishAt: null,
        }),
      });

      notify(`Quick Post queued with an auto caption for ${postingAccount.display_name || postingAccount.platform}`);
      setQuickPostRequest(null);
      setTab("queue");
      await loadBackendData();
    } catch (error) {
      notify(error.blockers?.[0] || error.message || "Quick Post failed", "err");
    } finally {
      setQuickPosting((prev) => {
        const next = { ...prev };
        delete next[quickKey];
        return next;
      });
    }
  };

  const requireLoginForQueue = () => {
    notify("Log in before queueing clips", "err");
    setPreviewClip(null);
    setPreviewStreamer(null);
    setTab("portal");
  };

  const onLogin = async (event) => {
    event.preventDefault();
    try {
      const data = await backendFetch("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(loginForm),
      });
      saveToken(data.accessToken);
      saveRefreshToken(data.refreshToken);
      notify("Logged into backend portal");
      setTab("portal");
    } catch (error) {
      notify(error.blockers?.[0] || error.message, "err");
    }
  };

  const onRegister = async (event) => {
    event.preventDefault();
    try {
      await backendFetch("/api/auth/register", {
        method: "POST",
        body: JSON.stringify(registerForm),
      });
      notify("Tenant created. You can log in now.");
      setAuthMode("login");
      setLoginForm({ email: registerForm.email, password: registerForm.password });
    } catch (error) {
      notify(error.message, "err");
    }
  };

  const onLogout = async () => {
    try {
      if (refreshToken) {
        await backendFetch("/api/auth/logout", {
          method: "POST",
          body: JSON.stringify({ refreshToken }),
        });
      }
    } catch (error) {
      // Keep local logout even if server-side revoke fails.
    }
    saveToken("");
    saveRefreshToken("");
    setCurrentUser(null);
    setBackendQueue([]);
    setBillingUsage(null);
    notify("Logged out");
  };

  const addConnectedAccount = async (event) => {
    event.preventDefault();
    try {
      await backendFetch("/api/accounts/connected", {
        method: "POST",
        body: JSON.stringify({
          ...accountForm,
          scopes: [],
          tokenExpiresAt: accountForm.tokenExpiresAt || null,
        }),
      }, token);
      notify("Connected account added");
      setAccountForm({
        platform: "tiktok",
        platformAccountId: "",
        displayName: "",
        accountType: "posting",
        accessToken: "demo-access-token",
        refreshToken: "demo-refresh-token",
        tokenExpiresAt: "",
      });
      await loadBackendData();
    } catch (error) {
      notify(error.message, "err");
    }
  };

  const startOAuth = async (platform, accountType = "posting", access = "login") => {
    if ((accountType === "posting" || accountType === "both") && isPlanLimitReached("postingAccounts", 1)) {
      showUpgrade("Your current plan has reached its connected posting account limit.", "postingAccounts");
      return;
    }
    try {
      const result = await backendFetch(`/api/oauth/${platform}/start`, {
        method: "POST",
        body: JSON.stringify({ accountType, access }),
      }, token);
      if (!result.configured) {
        notify(result.message || `${platform} OAuth is not configured`, "err");
        return;
      }
      window.location.href = result.authorizationUrl;
    } catch (error) {
      notify(error.message, "err");
    }
  };

  const connectLocalTestAccount = async (platform, accountType = "posting") => {
    if ((accountType === "posting" || accountType === "both") && isPlanLimitReached("postingAccounts", 1)) {
      showUpgrade("Your current plan has reached its connected posting account limit.", "postingAccounts");
      return;
    }
    try {
      await backendFetch("/api/accounts/local-test", {
        method: "POST",
        body: JSON.stringify({ platform, accountType }),
      }, token);
      notify(`${PLATFORMS[platform]?.label || platform} test account connected`);
      await loadBackendData();
    } catch (error) {
      notify(error.message, "err");
    }
  };

  const verifyConnectedAccount = async (accountId) => {
    try {
      const result = await backendFetch(`/api/accounts/${accountId}/verify`, { method: "POST" }, token);
      notify(result.data?.message || "Account verified");
      await loadBackendData();
    } catch (error) {
      notify(error.message || "Account verification failed", "err");
      await loadBackendData();
    }
  };

  const disconnectConnectedAccount = async (accountId) => {
    try {
      await backendFetch(`/api/accounts/${accountId}`, { method: "DELETE" }, token);
      setConnectedAccounts((prev) => prev.filter((account) => account.id !== accountId));
      setPostingHealth((prev) => prev.filter((entry) => entry.account?.id !== accountId));
      setUploadDrafts((prev) => {
        const next = {};
        Object.entries(prev).forEach(([clipId, draft]) => {
          next[clipId] = draft.postingAccountId === accountId ? { ...draft, postingAccountId: "" } : draft;
        });
        return next;
      });
      notify("Account disconnected");
      await loadBackendData();
    } catch (error) {
      notify(error.message || "Account could not be disconnected", "err");
    }
  };

  const setUploadDraft = (clipId, patch) => {
    setPostReadiness((prev) => {
      if (!prev[clipId]) return prev;
      const next = { ...prev };
      delete next[clipId];
      return next;
    });
    setUploadDrafts((prev) => ({
      ...prev,
      [clipId]: {
        postingAccountId: prev[clipId]?.postingAccountId || "",
        title: prev[clipId]?.title || "",
        caption: prev[clipId]?.caption || "",
        publishAt: prev[clipId]?.publishAt || "",
        ...patch,
      },
    }));
  };

  const scheduleUpload = async (clip) => {
    const draft = uploadDrafts[clip.id] || {};
    if (!draft.postingAccountId) {
      notify("Choose a posting account first", "err");
      return;
    }
    if (!Number(clip.asset_count || 0)) {
      const prepared = await ensureCliprVideoAsset(clip, { notifyLabel: "Schedule Post" });
      if (!prepared) return;
    }
    const account = connectedAccounts.find((entry) => entry.id === draft.postingAccountId);
    const publishAtIso = localDateTimeToIso(draft.publishAt);
    let readiness = postReadiness[clip.id];
    if (!readiness || readiness.allowed === false) {
      readiness = await checkPostReadiness(clip);
      if (!readiness?.allowed) return;
    }
    try {
      await backendFetch(`/api/clips/${clip.id}/schedule-upload`, {
        method: "POST",
        body: JSON.stringify({
          postingAccountId: draft.postingAccountId,
          platform: account?.platform || "tiktok",
          title: draft.title || clip.title,
          caption: draft.caption || generateQuickPostCaption(clip, account?.platform || "tiktok"),
          publishAt: publishAtIso,
        }),
      }, token);
      notify("Upload job scheduled");
      await loadBackendData();
    } catch (error) {
      notify(error.message, "err");
    }
  };

  const checkPostReadiness = async (clip) => {
    const draft = uploadDrafts[clip.id] || {};
    if (!draft.postingAccountId) {
      notify("Choose a posting account first", "err");
      return null;
    }
    setPostReadiness((prev) => ({ ...prev, [clip.id]: { loading: true } }));
    try {
      const result = await backendFetch(`/api/clips/${clip.id}/post-readiness`, {
        method: "POST",
        body: JSON.stringify({
          postingAccountId: draft.postingAccountId,
          title: draft.title || clip.title,
          caption: draft.caption || generateQuickPostCaption(clip, connectedAccounts.find((entry) => entry.id === draft.postingAccountId)?.platform || "tiktok"),
          publishAt: localDateTimeToIso(draft.publishAt),
        }),
      }, token);
      setPostReadiness((prev) => ({ ...prev, [clip.id]: result.data }));
      notify(result.data.allowed ? "Post readiness passed" : "Post readiness has blockers", result.data.allowed ? "ok" : "err");
      return result.data;
    } catch (error) {
      setPostReadiness((prev) => ({ ...prev, [clip.id]: { allowed: false, blockers: [error.message], warnings: [], checklist: [] } }));
      notify(error.message, "err");
      return null;
    }
  };

  const uploadManualVideoAsset = async (clip) => {
    const file = manualVideoFiles[clip.id];
    if (!file) {
      notify("Choose an MP4/MOV/WebM file first", "err");
      return;
    }
    try {
      await backendFetch(`/api/clips/${clip.id}/assets/manual-video`, {
        method: "POST",
        headers: {
          "Content-Type": file.type || "video/mp4",
          "X-File-Name": file.name,
        },
        body: file,
      }, token);
      notify("Video uploaded and formatted for TikTok");
      setManualVideoFiles((prev) => {
        const next = { ...prev };
        delete next[clip.id];
        return next;
      });
      setVideoNeedsAttention((prev) => {
        const next = { ...prev };
        delete next[clip.id];
        return next;
      });
      setPostReadiness((prev) => {
        const next = { ...prev };
        delete next[clip.id];
        return next;
      });
      await loadBackendData();
    } catch (error) {
      notify(error.message || "Video upload failed", "err");
    }
  };

  const importCliprVideoAsset = async (clip) => {
    if (!cliprRights[clip.id]) {
      notify("Confirm you have rights to import this video first", "err");
      return;
    }
    setCliprImports((prev) => ({ ...prev, [clip.id]: true }));
    try {
      await authedBackendFetch(`/api/clips/${clip.id}/assets/import-clipr`, {
        method: "POST",
        body: JSON.stringify({
          cliprInput: (cliprInputs[clip.id] ?? clip.source_url) || clip.source_clip_id,
          confirmRights: true,
        }),
      });
      notify("Clipr video imported and formatted");
      setVideoNeedsAttention((prev) => {
        const next = { ...prev };
        delete next[clip.id];
        return next;
      });
      setPostReadiness((prev) => {
        const next = { ...prev };
        delete next[clip.id];
        return next;
      });
      await loadBackendData();
    } catch (error) {
      setVideoNeedsAttention((prev) => ({
        ...prev,
        [clip.id]: error.message || "Clipr import failed. Upload the video file instead.",
      }));
      notify(error.message || "Clipr import failed", "err");
    } finally {
      setCliprImports((prev) => {
        const next = { ...prev };
        delete next[clip.id];
        return next;
      });
    }
  };

  const ensureCliprVideoAsset = async (clip, { sourceInput, notifyLabel = "Clipr" } = {}) => {
    if (!clip?.id || Number(clip.asset_count || 0)) return true;
    try {
      notify(`${notifyLabel}: pulling video with Clipr...`);
      await authedBackendFetch(`/api/clips/${clip.id}/assets/import-clipr`, {
        method: "POST",
        body: JSON.stringify({
          cliprInput: sourceInput || (cliprInputs[clip.id] ?? clip.source_url ?? clip.url) || clip.source_clip_id || clip.id,
          confirmRights: true,
        }),
      });
      setVideoNeedsAttention((prev) => {
        const next = { ...prev };
        delete next[clip.id];
        return next;
      });
      setPostReadiness((prev) => {
        const next = { ...prev };
        delete next[clip.id];
        return next;
      });
      return true;
    } catch (error) {
      setVideoNeedsAttention((prev) => ({
        ...prev,
        [clip.id]: error.message || "Clipr import failed. Upload the video file instead.",
      }));
      notify(error.message || "Clipr import failed", "err");
      return false;
    }
  };

  const removeQueueItem = async (item) => {
    if (!item?.upload_target_id) return;
    try {
      await backendFetch(`/api/queue/${item.upload_target_id}`, { method: "DELETE" }, token);
      notify("Post removed from queue");
      await loadBackendData();
    } catch (error) {
      notify(error.message || "Could not remove post", "err");
    }
  };

  const retryQueueItem = async (item) => {
    if (!item?.upload_target_id) return;
    try {
      await backendFetch(`/api/queue/${item.upload_target_id}/retry`, { method: "POST" }, token);
      notify("Retry queued");
      await loadBackendData();
    } catch (error) {
      notify(error.message || "Could not retry post", "err");
    }
  };

  const createTenantUser = async (event) => {
    event.preventDefault();
    try {
      await backendFetch("/api/admin/users", {
        method: "POST",
        body: JSON.stringify(adminUserForm),
      }, token);
      notify("Tenant user created");
      setAdminUserForm({ email: "", password: "", role: "standard_user" });
      await loadBackendData();
    } catch (error) {
      notify(error.message, "err");
    }
  };

  const updateTenantUser = async (user, patch) => {
    try {
      await backendFetch(`/api/admin/users/${user.id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }, token);
      notify("User updated");
      await loadBackendData();
    } catch (error) {
      notify(error.message || "Could not update user", "err");
    }
  };

  const deleteTenantUser = async (user) => {
    if (!window.confirm(`Delete ${user.email}? This removes their sessions, connected accounts, clips, jobs, and queue history.`)) return;
    try {
      await backendFetch(`/api/admin/users/${user.id}`, { method: "DELETE" }, token);
      notify("User deleted");
      await loadBackendData();
    } catch (error) {
      notify(error.message || "Could not delete user", "err");
    }
  };

  const loadPlatformUsers = async () => {
    if (!token || !isPlatformAdmin) return;
    try {
      const usersRes = await backendFetch(`/api/platform/users?search=${encodeURIComponent(platformSearch)}`, {}, token);
      setPlatformUsers(usersRes.data || []);
    } catch (error) {
      notify(error.message || "Could not load platform users", "err");
    }
  };

  const refreshProductionReadiness = async () => {
    if (!token || !isPlatformAdmin) return;
    try {
      const readinessRes = await backendFetch("/api/platform/production-readiness", {}, token);
      setProductionReadiness(readinessRes.data || null);
      notify("Production readiness refreshed");
    } catch (error) {
      notify(error.message || "Could not refresh production readiness", "err");
    }
  };

  const updatePlatformUser = async (user, patch) => {
    try {
      await backendFetch(`/api/platform/users/${user.id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }, token);
      notify("Platform user updated");
      await loadBackendData();
    } catch (error) {
      notify(error.message || "Could not update platform user", "err");
    }
  };

  const updatePlatformTenant = async (tenant, patch) => {
    try {
      await backendFetch(`/api/platform/tenants/${tenant.id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }, token);
      notify("Tenant updated");
      await loadBackendData();
    } catch (error) {
      notify(error.message || "Could not update tenant", "err");
    }
  };

  const tabs = [
    { id: "home", label: "Home" },
    { id: "clips", label: "Clips" },
    { id: "queue", label: "Queue" },
    { id: "accounts", label: "Accounts" },
    { id: "analytics", label: "Analytics" },
    { id: "templates", label: "Templates" },
    { id: "settings", label: "Settings" },
    ...(isAdmin ? [{ id: "admin", label: "Admin" }] : []),
  ];

  const liveCount = Object.keys(streams).length;
  const queueCount = currentUser ? backendQueue.length : queue.length;
  const activeUserName = currentUser ? userDisplayName(currentUser) : "Log in";
  const greetingName = currentUser ? activeUserName : "there";
  const activeUserInitials = currentUser ? userInitials(currentUser) : "LI";
  const activeRoleLabel = currentUser ? roleLabel(currentUser?.role) : "Guest";
  const billingDisplay = billingUsage || {
    plan: null,
    plans: PUBLIC_PLANS,
    usage: { monthlyUploadJobs: 0, postingAccounts: 0 },
    limits: { monthlyUploadJobs: PUBLIC_PLANS.free.monthlyUploadJobs, postingAccounts: PUBLIC_PLANS.free.postingAccounts },
  };
  const workspaceClips = backendClips
    .filter((clip) => !["scheduled", "posted"].includes(clip.state))
    .slice(0, 5);
  const selectedPrepClip = workspaceClips.find((clip) => clip.id === selectedPrepClipId) || workspaceClips[0] || null;
  const accountClips = currentUser ? backendClips : [];
  const displayClips = (backendClips.length ? backendClips : clips).slice(0, 12);
  const homeDisplayClips = accountClips.slice(0, 12);
  const filteredHomeClips = homeDisplayClips.filter((clip) => clipFilter === "All" || clipState(clip) === clipFilter || (clipFilter === "Drafts" && clipState(clip) === "Draft"));
  const filteredAccounts = connectedAccounts
    .filter((account) => account.status !== "disconnected")
    .filter((account) => accountFilter === "All Accounts" || (PLATFORMS[account.platform]?.label || account.platform) === accountFilter);
  const selectedClipTimeRange = CLIP_TIME_RANGES.find((entry) => entry.key === clipTimeRange) || CLIP_TIME_RANGES[2];
  const postedClips = accountClips.filter((clip) => clipState(clip) === "Posted");
  const totalViews = accountClips.reduce((sum, clip) => sum + Number(clip.view_count || clip.viewCount || 0), 0);
  const clipsPostedCount = postedClips.length;
  const avgViewsPerClip = accountClips.length ? Math.round(totalViews / accountClips.length) : 0;
  const engagementRate = totalViews > 0 ? Math.min(12, postedClips.length * 0.8 + connectedAccounts.length * 0.4) : 0;
  const totalLikes = 0;
  const totalComments = 0;
  const totalShares = 0;
  const hasAnalyticsData = accountClips.length > 0 || connectedAccounts.length > 0 || backendQueue.length > 0;
  const hasPostingAccount = connectedAccounts.some((account) => account.status === "active" && (account.account_type === "posting" || account.account_type === "both"));
  const hasReadyClip = accountClips.some((clip) => ["Ready", "Scheduled", "Posted"].includes(clipState(clip)));
  const onboardingSteps = [
    {
      id: "account",
      title: currentUser ? "Account created" : "Create account",
      body: currentUser ? "You are signed in and ready to build." : "Sign up or log in to save clips and connect accounts.",
      done: Boolean(currentUser),
      onClick: () => setTab("portal"),
    },
    {
      id: "connect",
      title: "Connect posting",
      body: hasPostingAccount ? "A posting account is ready." : "Connect TikTok first, then add more platforms later.",
      done: hasPostingAccount,
      onClick: () => setTab("accounts"),
    },
    {
      id: "browse",
      title: "Browse clips",
      body: accountClips.length ? "You have saved clips in this account." : "Search a streamer and choose a clip worth posting.",
      done: accountClips.length > 0,
      onClick: () => setTab("clips"),
    },
    {
      id: "prepare",
      title: "Prepare video",
      body: hasReadyClip ? "At least one clip is ready for posting." : "Use Clipr or manual upload to format a vertical MP4.",
      done: hasReadyClip,
      onClick: () => setTab("ops"),
    },
    {
      id: "queue",
      title: "Queue post",
      body: backendQueue.length ? "Your queue has activity." : "Schedule or quick-post your first prepared clip.",
      done: backendQueue.length > 0 || clipsPostedCount > 0,
      onClick: () => setTab("ops"),
    },
  ];
  const shouldShowOnboarding = !currentUser || onboardingSteps.some((step) => !step.done);

  const pageTitle = tab === "home" ? `Good evening, ${greetingName} 👋` : tab === "ops" ? "Create Post" : tabs.find((entry) => entry.id === tab)?.label || "Clipse";
  const pageSubtitle = tab === "home" ? "Here’s what’s happening with your content." : tab === "analytics" ? "Track performance across all your accounts and content." : tab === "accounts" ? "Manage and monitor all your connected accounts." : tab === "clips" ? "Manage all your clips in one place." : tab === "queue" ? "Track scheduled posts, retries, failures, and posting receipts." : "Manage your Clipse workspace.";

  const openPostModal = (clip) => {
    setPostModalClip(clip || displayClips[0] || backendClips[0] || clips[0] || null);
  };

  const handlePostModalSchedule = async ({ clip, selectedAccount, title, caption, publishAt }) => {
    if (!clip?.id) {
      notify("Save this clip before scheduling it", "err");
      return;
    }
    if (!selectedAccount) {
      notify("Choose a posting account first", "err");
      return;
    }
    if (!Number(clip.asset_count || 0)) {
      const prepared = await ensureCliprVideoAsset(clip, { notifyLabel: publishAt ? "Schedule Post" : "Queue Post" });
      if (!prepared) return;
    }
    const account = connectedAccounts.find((entry) => entry.id === selectedAccount);
    try {
      await backendFetch(`/api/clips/${clip.id}/schedule-upload`, {
        method: "POST",
        body: JSON.stringify({
          postingAccountId: selectedAccount,
          platform: account?.platform || "tiktok",
          title: title || clip.title,
          caption: caption || generateQuickPostCaption(clip, account?.platform || "tiktok"),
          publishAt: localDateTimeToIso(publishAt),
        }),
      }, token);
      notify(publishAt ? "Scheduled post added to queue" : "Post added to queue");
      setPostModalClip(null);
      setTab("queue");
      await loadBackendData();
    } catch (error) {
      notify(error.blockers?.[0] || error.message || "Could not schedule post", "err");
    }
  };

  if (authOnly && currentUser) {
    if (typeof window !== "undefined") {
      const target = IS_LOCALHOST
        ? "/"
        : `${appHref("/")}${buildAuthHandoffHash(token, refreshToken)}`;
      window.location.replace(target);
    }
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: THEME.bg, color: THEME.text }}>
        Opening Clipse...
      </div>
    );
  }

  if (authOnly) {
    return (
      <div style={{ minHeight: "100vh", background: "radial-gradient(circle at 20% 0%,rgba(139,92,246,0.24),transparent 34%),radial-gradient(circle at 82% 18%,rgba(46,232,139,0.08),transparent 28%),#070711", color: THEME.text, fontFamily: "'DM Sans', 'Inter', sans-serif", padding: "32px 20px" }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800&family=Space+Grotesk:wght@600;700&display=swap');
          *{box-sizing:border-box}
          input::placeholder,textarea::placeholder{color:#686174}
        `}</style>
        {notif && String(notif.msg || "").trim().toLowerCase() !== "unauthorized" ? <div style={{ position: "fixed", top: 18, right: 18, background: notif.type === "err" ? "#2a0d18" : "#0f211a", border: `1px solid ${notif.type === "err" ? "#ff5b7944" : "#2ee88b44"}`, color: notif.type === "err" ? "#ff8aa0" : "#7dffb8", padding: "12px 14px", borderRadius: 14, zIndex: 10, maxWidth: 420 }}>{notif.msg}</div> : null}
        <div style={{ maxWidth: 1020, margin: "0 auto" }}>
          <header style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", marginBottom: 54 }}>
            <BrandLockup href={APP_HOSTNAME === "localhost" || APP_HOSTNAME === "127.0.0.1" ? "/?page=landing" : "https://getclipse.com"} variant="full" markSize={32} />
            <GhostButton onClick={() => { window.location.href = APP_HOSTNAME === "localhost" || APP_HOSTNAME === "127.0.0.1" ? "/?page=landing" : "https://getclipse.com"; }}>Back to Home</GhostButton>
          </header>
          <div style={{ display: "grid", gridTemplateColumns: "0.95fr 1.05fr", gap: 24, alignItems: "center" }}>
            <div>
              <Tag color="rgba(139,92,246,0.18)" text="#c4b5fd">CLIPSE ACCOUNT</Tag>
              <h1 style={{ color: "#fff", fontFamily: "'Space Grotesk',sans-serif", fontSize: 54, lineHeight: 1, letterSpacing: -1.8, margin: "18px 0 14px" }}>
                {authMode === "register" ? "Create your clipping workspace." : "Log in to your clipping workspace."}
              </h1>
              <p style={{ color: THEME.muted, fontSize: 16, lineHeight: 1.75, maxWidth: 520, margin: 0 }}>
                Connect posting accounts, prepare clips, queue uploads, and track receipts from one focused creator operations dashboard.
              </p>
              <div style={{ display: "grid", gap: 10, marginTop: 24, color: "#ddd6fe", fontSize: 13 }}>
                <span>✓ Account-specific clips and connected accounts</span>
                <span>✓ R2-backed video storage</span>
                <span>✓ Queue receipts and retry flow</span>
              </div>
            </div>
            <AuthPortal
              authMode={authMode}
              setAuthMode={setAuthMode}
              loginForm={loginForm}
              setLoginForm={setLoginForm}
              registerForm={registerForm}
              setRegisterForm={setRegisterForm}
              onLogin={onLogin}
              onRegister={onRegister}
              backendOk={backendOk}
              currentUser={currentUser}
              onLogout={onLogout}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: `radial-gradient(circle at 72% 0%, rgba(139,92,246,0.16), transparent 34%), radial-gradient(circle at 2% 24%, rgba(109,40,217,0.16), transparent 30%), ${THEME.bg}`, color: "#d8d4e5", fontFamily: "'DM Sans', 'Inter', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800&family=Space+Grotesk:wght@600;700&display=swap');
        @keyframes livepulse{0%{box-shadow:0 0 0 0 #ef444488}70%{box-shadow:0 0 0 8px #ef444400}100%{box-shadow:0 0 0 0 #ef444400}}
        @keyframes slidein{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}}
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        *{box-sizing:border-box}
        ::-webkit-scrollbar{width:5px}
        ::-webkit-scrollbar-thumb{background:#8b5cf655;border-radius:99px}
        input::placeholder,textarea::placeholder{color:#686174}
        button:disabled{cursor:not-allowed}
        .clip-card:hover{transform:translateY(-3px);border-color:rgba(139,92,246,0.75);box-shadow:0 20px 55px rgba(0,0,0,0.35),0 0 0 1px rgba(139,92,246,0.18)}
        .saas-clip-card{transition:transform .18s ease,border-color .18s ease,box-shadow .18s ease}
        .saas-clip-card:hover{transform:translateY(-4px);border-color:rgba(139,92,246,0.55);box-shadow:0 24px 70px rgba(0,0,0,0.38),0 0 0 1px rgba(139,92,246,0.18)}
        .saas-clip-card:hover .clip-hover-actions{opacity:1!important}
        .nav-button:hover{background:rgba(255,255,255,0.06)!important;color:#fff!important}
        .quick-card:hover{transform:translateY(-2px);border-color:rgba(139,92,246,0.5)!important}
        @media(max-width:1100px){.app-sidebar{position:relative!important;width:auto!important;min-height:auto!important}.app-main{margin-left:0!important;padding:18px!important}.browse-grid,.accounts-grid,.admin-grid,.analytics-grid,.post-modal-grid{grid-template-columns:1fr!important}.step-grid{grid-template-columns:1fr!important}.clip-grid,.stat-grid,.account-card-grid{grid-template-columns:repeat(2,1fr)!important}}
        @media(max-width:720px){.clip-grid,.stat-grid,.account-card-grid{grid-template-columns:1fr!important}.topbar{align-items:flex-start!important;flex-direction:column!important}.workspace-row{grid-template-columns:1fr!important}.sidebar-nav{display:grid;grid-template-columns:repeat(2,1fr);gap:8px}.sidebar-nav button{margin-bottom:0!important}.hero-steps{grid-template-columns:1fr!important}.sidebar-upsell,.sidebar-status{display:none!important}.sidebar-user{border-top:0!important;padding-top:4px!important}.app-main{padding-top:12px!important}}
      `}</style>

      {notif && String(notif.msg || "").trim().toLowerCase() !== "unauthorized" ? (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 200, background: notif.type === "err" ? "#ef444418" : "#4ade8018", border: `1px solid ${notif.type === "err" ? "#ef444466" : "#4ade8066"}`, color: notif.type === "err" ? "#ef4444" : "#4ade80", padding: "10px 18px", borderRadius: 8, fontSize: 12, animation: "slidein 0.25s ease" }}>
          {notif.msg}
        </div>
        ) : null}

      {upgradePrompt ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 210, background: "rgba(0,0,0,0.68)", backdropFilter: "blur(14px)", display: "grid", placeItems: "center", padding: 22 }} onClick={(event) => event.target === event.currentTarget && setUpgradePrompt(null)}>
          <Panel style={{ width: "min(520px,95vw)", padding: 24, background: "linear-gradient(145deg,rgba(20,20,34,0.99),rgba(10,10,20,0.99))" }}>
            <div style={{ color: "#fff", fontSize: 24, fontWeight: 900, marginBottom: 8 }}>Upgrade to keep growing</div>
            <div style={{ color: THEME.muted, lineHeight: 1.7, marginBottom: 18 }}>{upgradePrompt.reason}</div>
            {billingUsage ? (
              <div style={{ display: "grid", gap: 12, marginBottom: 18 }}>
                <UsageMeter label="Quick Posts this month" current={billingUsage.usage.monthlyUploadJobs} limit={billingUsage.limits.monthlyUploadJobs} />
                <UsageMeter label="Posting accounts" current={billingUsage.usage.postingAccounts} limit={billingUsage.limits.postingAccounts} />
              </div>
            ) : null}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: 12 }}>
              <GhostButton onClick={() => setUpgradePrompt(null)}>Not now</GhostButton>
              <PrimaryButton onClick={() => { setUpgradePrompt(null); setSettingsSection("billing"); setTab("settings"); }}>View Plans</PrimaryButton>
            </div>
          </Panel>
        </div>
      ) : null}

      {previewClip && previewStreamer ? (
        <PreviewModal
          clip={previewClip}
          streamer={previewStreamer}
          accounts={postingAccountsForPreview}
          currentUser={currentUser}
          onClose={() => {
            setPreviewClip(null);
            setPreviewStreamer(null);
          }}
          onQueue={handleQueue}
          onImport={importClipForOperations}
          onRequireLogin={requireLoginForQueue}
        />
      ) : null}

      {postModalClip ? (
        <PostClipModal
          clip={postModalClip}
          accounts={connectedAccounts.filter((account) => account.status === "active" && (account.account_type === "posting" || account.account_type === "both"))}
          onClose={() => setPostModalClip(null)}
          onPostNow={handlePostModalSchedule}
          onQueue={handlePostModalSchedule}
          onSchedule={handlePostModalSchedule}
        />
      ) : null}

      {quickPostRequest ? (
        <QuickPostAccountModal
          clip={quickPostRequest.clip}
          accounts={activePostingAccounts()}
          loading={Boolean(quickPosting[quickPostRequest.clip?.id || quickPostRequest.clip?.source_clip_id || quickPostRequest.clip?.title])}
          onClose={() => setQuickPostRequest(null)}
          onConfirm={(postingAccountId, caption) => quickPostClip({ ...quickPostRequest, postingAccountId, caption })}
        />
      ) : null}

      <div className="app-sidebar" style={{ position: "fixed", left: 0, top: 0, bottom: 0, width: 232, background: "linear-gradient(180deg,rgba(10,10,19,0.96),rgba(8,8,16,0.92))", borderRight: `1px solid ${THEME.line}`, display: "flex", flexDirection: "column", zIndex: 10, boxShadow: "30px 0 80px rgba(0,0,0,0.28)" }}>
        <div style={{ padding: "22px 18px 14px" }}>
          <BrandLockup markSize={34} textSize={20} gap={10} />
        </div>

        <div style={{ padding: "0 16px 13px" }}>
          <PrimaryButton onClick={() => setTab("ops")} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 14, padding: "11px 12px" }}>
            <span style={{ fontSize: 20, lineHeight: 0 }}>+</span> New Post
          </PrimaryButton>
        </div>

        <nav className="sidebar-nav" style={{ flex: 1, padding: "0 12px" }}>
          {tabs.map((entry) => (
            <button
              className="nav-button"
              key={entry.id}
              onClick={() => setTab(entry.id)}
              style={{ width: "100%", textAlign: "left", background: tab === entry.id ? "linear-gradient(90deg,rgba(139,92,246,0.22),rgba(255,255,255,0.05))" : "transparent", border: `1px solid ${tab === entry.id ? "rgba(139,92,246,0.22)" : "transparent"}`, borderRadius: 12, padding: "10px 12px", marginBottom: 5, color: tab === entry.id ? "#fff" : "#b7b1c2", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, transition: "all 0.18s ease" }}
            >
              <span style={{ color: tab === entry.id ? THEME.purple : THEME.muted, fontSize: 18, width: 22, textAlign: "center" }}>{NAV_ICONS[entry.id] || "•"}</span>
              {entry.label}
            </button>
          ))}
        </nav>

        <div className="sidebar-upsell" style={{ margin: "0 12px 10px", background: "linear-gradient(180deg,rgba(139,92,246,0.12),rgba(255,255,255,0.045))", border: `1px solid ${THEME.line}`, borderRadius: 14, padding: 12 }}>
          <div style={{ color: THEME.purple, fontSize: 18, marginBottom: 8 }}>⌁</div>
          <div style={{ color: "#fff", fontWeight: 900, fontSize: 13, marginBottom: 6 }}>Unlock more power</div>
          <div style={{ color: THEME.muted, fontSize: 11.5, lineHeight: 1.45, marginBottom: 10 }}>Upgrade your plan for more accounts, auto-posting, AI tools, and analytics.</div>
          <PrimaryButton onClick={() => { setSettingsSection("billing"); setTab("settings"); }} style={{ width: "100%", padding: "10px 11px", fontSize: 12 }}>Upgrade Now</PrimaryButton>
        </div>

        <div className="sidebar-status" style={{ margin: "0 12px 10px", background: "rgba(255,255,255,0.055)", border: `1px solid ${THEME.line}`, borderRadius: 14, padding: "10px 12px", display: "grid", gap: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: backendOk ? THEME.green : THEME.danger }} />
            <span style={{ fontSize: 11.5, color: THEME.muted }}>{backendOk ? "All Systems Operational" : "Backend offline"}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7, color: proxyOk ? THEME.dim : THEME.danger, fontSize: 11.5 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: proxyOk ? "#4ade80" : "#ef4444" }} />
            {proxyOk ? "Proxy online" : "Proxy offline"}
          </div>
        </div>

        <div className="sidebar-user" style={{ padding: "10px 12px 12px", borderTop: `1px solid ${THEME.line}` }}>
          {liveCount > 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
              <LiveDot />
              <span style={{ fontSize: 11.5, color: "#ff8aa0" }}>{liveCount} live now</span>
            </div>
          ) : null}
          <div onClick={() => setTab("portal")} style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(255,255,255,0.04)", border: `1px solid ${THEME.line}`, borderRadius: 14, padding: 10, cursor: "pointer" }}>
            <div style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#9f5cff,#6d28d9)", display: "grid", placeItems: "center", color: "#fff", fontWeight: 800, fontSize: 12 }}>{activeUserInitials}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{activeUserName}</div>
              <div style={{ color: THEME.muted, fontSize: 11.5 }}>{activeRoleLabel} • {queueCount} posts</div>
            </div>
          </div>
        </div>
      </div>

      <div className="app-main" style={{ marginLeft: 232, padding: tab === "settings" ? "20px 32px 28px" : "28px 32px 40px", minHeight: "100vh" }}>
        {tab !== "settings" ? (
          <div className="topbar" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 18, marginBottom: 24 }}>
            <div>
              <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 30, letterSpacing: -0.8, color: THEME.text, margin: 0 }}>{pageTitle}</h1>
              <div style={{ color: THEME.muted, fontSize: 15, marginTop: 6 }}>{pageSubtitle}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ background: "rgba(255,255,255,0.055)", border: `1px solid ${THEME.line}`, borderRadius: 12, padding: "12px 14px", minWidth: 230, color: THEME.dim, fontSize: 14 }}>⌕ Search clips...</div>
              <div style={{ width: 38, height: 38, borderRadius: "50%", background: "rgba(255,255,255,0.055)", border: `1px solid ${THEME.line}`, display: "grid", placeItems: "center", color: "#fff" }}>◦</div>
            </div>
          </div>
        ) : null}
        {apiError && String(apiError || "").trim().toLowerCase() !== "unauthorized" ? (
          <div style={{ background: "#ef444412", border: "1px solid #ef444433", borderRadius: 10, padding: "12px 18px", marginBottom: 20, fontSize: 12, color: "#ef4444" }}>
            {apiError}
          </div>
        ) : null}

        {tab === "home" ? (
          <div style={{ display: "grid", gap: 22 }}>
            {shouldShowOnboarding ? <OnboardingChecklist steps={onboardingSteps} /> : null}

            <div className="stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 18 }}>
              <StatCard label="Total Views" value={fmtViews(totalViews)} accent="#8b5cf6" />
              <StatCard label="Clips Posted" value={String(clipsPostedCount)} accent="#3b82f6" />
              <StatCard label="Avg Views / Clip" value={fmtViews(avgViewsPerClip)} accent="#22c55e" />
              <StatCard label="Engagement Rate" value={fmtPercent(engagementRate)} accent="#f59e0b" />
            </div>

            <Panel style={{ padding: 22 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", marginBottom: 20 }}>
                <div>
                  <div style={{ color: "#fff", fontSize: 18, fontWeight: 900 }}>Recent Clips</div>
                  <div style={{ color: THEME.muted, fontSize: 13, marginTop: 4 }}>Latest ready, scheduled, and posted content.</div>
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <GhostButton style={{ padding: "9px 12px", background: "rgba(139,92,246,0.16)", color: "#fff" }}>▦ Grid</GhostButton>
                  <GhostButton style={{ padding: "9px 12px" }}>☷ List</GhostButton>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", marginBottom: 18, flexWrap: "wrap" }}>
                <FilterBar active={clipFilter} onChange={setClipFilter} />
                <GhostButton onClick={() => setTab("clips")}>View all</GhostButton>
              </div>
              <div className="clip-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16 }}>
                {(filteredHomeClips.length ? filteredHomeClips : homeDisplayClips).slice(0, 8).map((clip) => (
                  <SaaSClipCard
                    key={clip.id || clip.source_clip_id || clip.title}
                    clip={{ ...clip, quickPosting: Boolean(quickPosting[clip.id || clip.source_clip_id || clip.title]) }}
                    compact
                    onPreview={() => openPostModal(clip)}
                    onQuickPost={() => startQuickPost({ clip })}
                    onSchedule={() => openPostModal(clip)}
                  />
                ))}
                {!homeDisplayClips.length ? (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <EmptyState
                      eyebrow="NO CLIPS YET"
                      title={currentUser ? "Your clip library is empty" : "Create an account to start clipping"}
                      body={currentUser ? "Browse Twitch clips, save one to your workspace, then prepare it for TikTok, Reels, or Shorts." : "You can explore plans without logging in, but saving clips and queueing posts needs an account."}
                      primaryLabel={currentUser ? "Browse Clips" : "Create Account"}
                      onPrimary={() => currentUser ? setTab("clips") : (setAuthMode("register"), setTab("portal"))}
                      secondaryLabel={currentUser ? "Connect Account" : "View Plans"}
                      onSecondary={() => currentUser ? setTab("accounts") : (setSettingsSection("billing"), setTab("settings"))}
                    />
                  </div>
                ) : null}
              </div>
            </Panel>
          </div>
        ) : null}

        {tab === "clips" ? (
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 24 }}>
              <h2 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 22, color: "#fff", margin: 0 }}>Your Clips</h2>
              <span style={{ fontSize: 13, color: THEME.dim }}>search, import, preview, and schedule clips</span>
            </div>
            <Panel style={{ padding: 16, marginBottom: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <TextInput value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchStreamer()} placeholder="Search clips or add a streamer..." />
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <GhostButton onClick={searchStreamer} disabled={streamerLoading || !searchInput.trim()}>Import Clips</GhostButton>
                  <PrimaryButton onClick={() => setTab("ops")}>+ New Clip</PrimaryButton>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <div style={{ color: "#fff", fontWeight: 900, fontSize: 13 }}>Clip time range</div>
                  <div style={{ color: THEME.muted, fontSize: 12, marginTop: 3 }}>{selectedClipTimeRange.description}</div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {CLIP_TIME_RANGES.map((range) => (
                    <button
                      key={range.key}
                      onClick={() => {
                        setClipTimeRange(range.key);
                        if (selectedUser) selectStreamer({ ...selectedUser }, range.key);
                      }}
                      style={{ border: `1px solid ${clipTimeRange === range.key ? "rgba(139,92,246,0.8)" : THEME.line}`, background: clipTimeRange === range.key ? "linear-gradient(135deg,rgba(139,92,246,0.32),rgba(109,40,217,0.18))" : "rgba(255,255,255,0.04)", color: clipTimeRange === range.key ? "#fff" : THEME.muted, borderRadius: 10, padding: "9px 12px", fontWeight: 900, cursor: "pointer" }}
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
              </div>
            </Panel>
            <Panel style={{ padding: 18, marginBottom: 18 }}>
            <div className="hero-steps" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
                {[
                  ["1", "Browse", "Find a Twitch clip."],
                  ["2", "Import", "Send it to Workspace."],
                  ["3", "Prepare", "Attach or format video."],
                  ["4", "Post", "Schedule to TikTok."],
                ].map((step) => (
                  <div key={step[0]} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#7c3aed22", border: "1px solid #7c3aed55", color: "#a78bfa", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 900, flexShrink: 0 }}>{step[0]}</div>
                    <div>
                      <div style={{ color: "#fff", fontSize: 12, fontWeight: 800 }}>{step[1]}</div>
                      <div style={{ color: "#666", fontSize: 10, marginTop: 3, lineHeight: 1.5 }}>{step[2]}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
            <div className="browse-grid" style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 20 }}>
              <Panel style={{ padding: 14 }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <TextInput value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchStreamer()} placeholder="Add streamer..." />
                  <GhostButton onClick={searchStreamer} disabled={streamerLoading || !searchInput.trim()} style={{ padding: "8px 10px" }}>
                    {streamerLoading ? <Spinner size={12} /> : "+"}
                  </GhostButton>
                </div>
                <div style={{ overflowY: "auto", maxHeight: "calc(100vh - 220px)" }}>
                  {streamers.length === 0 && streamerLoading ? <div style={{ textAlign: "center", padding: 30 }}><Spinner /></div> : null}
                  {streamers.map((user) => (
                    <StreamerRow key={user.id} user={user} stream={streams[user.id]} selected={selectedUser?.id === user.id} loading={clipsLoading && selectedUser?.id === user.id} onClick={() => selectStreamer(user)} />
                  ))}
                </div>
              </Panel>

              <div>
                {selectedUser ? (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                      <div>
                        <div style={{ fontSize: 10, color: "#444", letterSpacing: 2 }}>SOURCE</div>
                        <div style={{ fontSize: 24, color: "#fff", fontWeight: 800 }}>{selectedUser.display_name}</div>
                      </div>
                    </div>
                    {clipsLoading ? <Spinner /> : (
                      <div className="clip-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
                        {clips.map((clip) => (
                          <SaaSClipCard
                            key={clip.id}
                            clip={{ ...clip, quickPosting: Boolean(quickPosting[clip.id || clip.source_clip_id || clip.title]) }}
                            onPreview={() => { setPreviewClip(clip); setPreviewStreamer(selectedUser); }}
                            onQuickPost={() => startQuickPost({ clip, streamer: selectedUser })}
                            onSchedule={() => openPostModal(clip)}
                          />
                        ))}
                        {!clips.length ? (
                          <div style={{ gridColumn: "1 / -1" }}>
                            <EmptyState
                              eyebrow="NO CLIPS FOUND"
                              title={`No clips in ${selectedClipTimeRange.label}`}
                              body="Try a wider time range like Month or All time, or search a different streamer."
                              primaryLabel="Use All time"
                              onPrimary={() => { setClipTimeRange("all"); selectStreamer(selectedUser, "all"); }}
                              secondaryLabel="Try Month"
                              onSecondary={() => { setClipTimeRange("30d"); selectStreamer(selectedUser, "30d"); }}
                            />
                          </div>
                        ) : null}
                      </div>
                    )}
                  </>
                ) : (
                  <EmptyState
                    eyebrow="BROWSE CLIPS"
                    title="Pick a streamer to start"
                    body="Search a Twitch streamer, choose a time range, then save or quick-post clips into your account."
                    primaryLabel="Try ludwig"
                    onPrimary={() => setSearchInput("ludwig")}
                    secondaryLabel="Go to Workspace"
                    onSecondary={() => setTab("ops")}
                  />
                )}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginTop: 18 }}>
              {[
                ["↧", "Auto Import", "Clipr-first saving from Twitch clip URLs."],
                ["✦", "AI Captions", "Draft captions and posting metadata."],
                ["▤", "Templates", "Reusable posting formats for creators."],
                ["▮", "Analytics", "Track posting health and failures."],
              ].map((card) => (
                <Panel className="quick-card" key={card[1]} style={{ padding: 18, transition: "all 0.18s ease", background: "rgba(255,255,255,0.04)" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: THEME.purpleSoft, color: "#c4b5fd", display: "grid", placeItems: "center", fontSize: 20, marginBottom: 12 }}>{card[0]}</div>
                  <div style={{ color: "#fff", fontWeight: 800, marginBottom: 6 }}>{card[1]}</div>
                  <div style={{ color: THEME.muted, fontSize: 12, lineHeight: 1.5 }}>{card[2]}</div>
                </Panel>
              ))}
            </div>
          </div>
        ) : null}

        {tab === "extract" ? (
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 24 }}>
              <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 34, letterSpacing: 4, color: "#fff", margin: 0 }}>PASTE URL</h1>
              <span style={{ fontSize: 11, color: "#333" }}>channel extraction</span>
            </div>
            <Panel style={{ padding: 20 }}>
              <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
                <TextInput value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="Paste twitch.tv/username or a VOD URL..." />
                <PrimaryButton onClick={extractFromUrl} disabled={urlLoading}>{urlLoading ? "SCANNING" : "EXTRACT"}</PrimaryButton>
              </div>
              {urlLoading ? (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 11, color: "#7c3aed" }}>{urlStage}</span>
                    <span style={{ fontSize: 11, color: "#444" }}>{urlProgress}%</span>
                  </div>
                  <div style={{ height: 4, background: "#1a1a28", borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${urlProgress}%`, background: "linear-gradient(90deg,#7c3aed,#a855f7)", borderRadius: 2 }} />
                  </div>
                </div>
              ) : null}

              {urlClips && Array.isArray(urlClips) ? (
                <div>
                  <div style={{ fontSize: 10, color: "#4ade80", letterSpacing: 2, marginBottom: 14 }}>FOUND {urlClips.length} CLIPS FOR {urlStreamer?.display_name?.toUpperCase()}</div>
                  <div className="clip-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
                    {urlClips.map((clip) => (
                      <ClipCard key={clip.id} clip={clip} onSelect={() => { setPreviewClip(clip); setPreviewStreamer(urlStreamer); }} />
                    ))}
                  </div>
                </div>
              ) : null}

              {urlClips === "notfound" ? <div style={{ color: "#facc15" }}>Streamer not found.</div> : null}
              {urlClips === "non-twitch" ? <div style={{ color: "#facc15" }}>Non-Twitch extraction still needs a backend video processor flow.</div> : null}
            </Panel>
          </div>
        ) : null}

        {tab === "queue" ? (
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 24 }}>
              <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 34, letterSpacing: 4, color: "#fff", margin: 0 }}>POSTS</h1>
              <span style={{ fontSize: 11, color: "#333" }}>{queueCount} scheduled or attempted posts</span>
            </div>

            {billingUsage ? (
              <Panel style={{ padding: 18, marginBottom: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 10, color: "#555", letterSpacing: 2, marginBottom: 4 }}>PLAN</div>
                    <div style={{ color: "#fff", fontSize: 18, fontWeight: 800 }}>{billingUsage.plan.label}</div>
                  </div>
                  <Tag color="#7c3aed18" text="#a78bfa">{billingUsage.usage.monthlyUploadJobs}/{billingUsage.limits.monthlyUploadJobs} MONTHLY JOBS</Tag>
                </div>
                <div style={{ height: 5, background: "#0b0b16", borderRadius: 999, overflow: "hidden" }}>
                  <div style={{ width: `${usagePercent(billingUsage.usage.monthlyUploadJobs, billingUsage.limits.monthlyUploadJobs)}%`, height: "100%", background: "linear-gradient(90deg,#4ade80,#a78bfa)" }} />
                </div>
              </Panel>
            ) : null}

            {currentUser ? (
              backendQueue.length === 0 ? (
                <EmptyState
                  eyebrow="QUEUE EMPTY"
                  title="No posts are queued yet"
                  body="Once you prepare a clip and choose a posting account, scheduled posts and upload attempts will appear here."
                  primaryLabel="Prepare a Clip"
                  onPrimary={() => setTab("ops")}
                  secondaryLabel="Browse Clips"
                  onSecondary={() => setTab("clips")}
                />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {backendQueue.map((item) => {
                    const tone = queueReceiptTone(item);
                    const receiptTime = item.posted_at || item.latest_attempt_at || item.scheduled_at || item.job_created_at;
                    return (
                      <Panel key={item.upload_target_id} style={{ padding: "16px 20px", display: "flex", gap: 16, alignItems: "flex-start" }}>
                        {item.thumbnail_url ? <img src={item.thumbnail_url} alt={item.title} style={{ width: 96, height: 56, objectFit: "cover", borderRadius: 10, border: `1px solid ${THEME.line}` }} /> : null}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", marginBottom: 8 }}>
                            <div>
                              <div style={{ fontSize: 14, fontWeight: 800, color: "#e8e8f0", marginBottom: 6 }}>{item.title}</div>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                <span style={{ fontSize: 10, color: THEME.dim }}>from {item.streamer_name || "unknown"}</span>
                                <Tag color="#7c3aed22" text="#a78bfa">{item.account_name || "Posting account"}</Tag>
                                <Tag color="#ffffff10" text="#aaa">{String(item.platform || "").toUpperCase()}</Tag>
                              </div>
                            </div>
                            <Tag color={tone.color} text={tone.text}>{queueReceiptLabel(item)}</Tag>
                          </div>

                          <div style={{ background: "rgba(255,255,255,0.035)", border: `1px solid ${tone.border}`, borderRadius: 12, padding: 12, display: "grid", gap: 10 }}>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10 }}>
                              <div>
                                <div style={{ color: THEME.dim, fontSize: 10, letterSpacing: 1.2 }}>TIME</div>
                                <div style={{ color: "#dcd8e8", fontSize: 11, marginTop: 4 }}>{formatDateTime(receiptTime) || "Pending"}</div>
                              </div>
                              <div>
                                <div style={{ color: THEME.dim, fontSize: 10, letterSpacing: 1.2 }}>JOB</div>
                                <div style={{ color: "#dcd8e8", fontSize: 11, marginTop: 4 }}>{shortId(item.job_id) || shortId(item.upload_target_id) || "Queued"}</div>
                              </div>
                              <div>
                                <div style={{ color: THEME.dim, fontSize: 10, letterSpacing: 1.2 }}>MODE</div>
                                <div style={{ color: "#dcd8e8", fontSize: 11, marginTop: 4 }}>{item.provider_mode || item.provider_source || "Worker queue"}</div>
                              </div>
                              <div>
                                <div style={{ color: THEME.dim, fontSize: 10, letterSpacing: 1.2 }}>PUBLISH ID</div>
                                <div style={{ color: "#dcd8e8", fontSize: 11, marginTop: 4 }}>{shortId(item.platform_publish_id) || "Not returned"}</div>
                              </div>
                            </div>
                            <div style={{ color: tone.text, fontSize: 11, lineHeight: 1.6 }}>
                              {queueReceiptDetail(item)}
                            </div>
                            {item.asset_public_url ? (
                              <a href={item.asset_public_url} target="_blank" rel="noreferrer" style={{ color: "#a78bfa", fontSize: 11, textDecoration: "none", fontWeight: 800 }}>
                                View stored MP4
                              </a>
                            ) : null}
                            {(item.last_error_message || item.attempt_error_message) ? (
                              <div style={{ background: "#ef444414", border: "1px solid #ef444433", borderRadius: 8, padding: "8px 10px", fontSize: 11, color: "#ef4444", lineHeight: 1.6 }}>
                                {item.last_error_code || item.attempt_error_code}: {item.last_error_message || item.attempt_error_message}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div style={{ textAlign: "right", display: "grid", gap: 8, justifyItems: "end", minWidth: 120 }}>
                          {item.needs_reconnect ? (
                            <PrimaryButton onClick={() => setTab("accounts")} style={{ padding: "9px 12px", fontSize: 12 }}>
                              Reconnect
                            </PrimaryButton>
                          ) : null}
                          {item.receipt_status === "failed" ? (
                            <PrimaryButton onClick={() => retryQueueItem(item)} style={{ padding: "9px 12px", fontSize: 12 }}>
                              Retry
                            </PrimaryButton>
                          ) : null}
                          {item.upload_status !== "posted" ? (
                            <GhostButton onClick={() => removeQueueItem(item)} style={{ color: "#ef4444", borderColor: "#ef444433" }}>
                              REMOVE
                            </GhostButton>
                          ) : null}
                        </div>
                      </Panel>
                    );
                  })}
                </div>
              )
            ) : queue.length === 0 ? (
              <EmptyState
                eyebrow="LOGIN REQUIRED"
                title="Log in to queue posts"
                body="Clipse keeps queues tied to each account, so sign in before scheduling or posting clips."
                primaryLabel="Log In"
                onPrimary={() => { setAuthMode("login"); setTab("portal"); }}
                secondaryLabel="View Plans"
                onSecondary={() => { setSettingsSection("billing"); setTab("settings"); }}
              />
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {queue.map((item) => (
                  <Panel key={item.id} style={{ padding: "16px 20px", display: "flex", gap: 16, alignItems: "flex-start" }}>
                    {item.thumb ? <img src={item.thumb} alt={item.title} style={{ width: 90, height: 50, objectFit: "cover", borderRadius: 6 }} /> : null}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#e8e8f0", marginBottom: 6 }}>{item.title}</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                        <span style={{ fontSize: 10, color: "#444" }}>from {item.streamer}</span>
                        {item.accounts.map((account) => <Tag key={account} color="#7c3aed22" text="#a78bfa">{account}</Tag>)}
                      </div>
                      {item.caption ? <div style={{ background: "#080810", border: "1px solid #1a1a28", borderRadius: 8, padding: "8px 12px", fontSize: 11, color: "#888", lineHeight: 1.6 }}>{item.caption}</div> : null}
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 18, fontWeight: 900, color: item.score >= 93 ? "#4ade80" : item.score >= 85 ? "#facc15" : "#f97316" }}>{item.score}</div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: item.status === "posting" ? "#4ade80" : "#555" }}>{item.status === "posting" ? "POSTING" : item.scheduled}</div>
                      <GhostButton onClick={() => setQueue((prev) => prev.filter((entry) => entry.id !== item.id))} style={{ marginTop: 8, color: "#ef4444", borderColor: "#ef444433" }}>
                        REMOVE
                      </GhostButton>
                    </div>
                  </Panel>
                ))}
              </div>
            )}
          </div>
        ) : null}

        {tab === "portal" ? (
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 24 }}>
              <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 34, letterSpacing: 4, color: "#fff", margin: 0 }}>{currentUser ? "PROFILE" : "LOGIN"}</h1>
              <span style={{ fontSize: 11, color: "#333" }}>{currentUser ? "account access" : "sign in to queue and post"}</span>
            </div>
            <AuthPortal
              authMode={authMode}
              setAuthMode={setAuthMode}
              loginForm={loginForm}
              setLoginForm={setLoginForm}
              registerForm={registerForm}
              setRegisterForm={setRegisterForm}
              onLogin={onLogin}
              onRegister={onRegister}
              backendOk={backendOk}
              currentUser={currentUser}
              onLogout={onLogout}
            />
          </div>
        ) : null}

        {tab === "accounts" ? (
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 24 }}>
              <h2 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 22, color: "#fff", margin: 0 }}>Accounts</h2>
              <span style={{ fontSize: 13, color: THEME.dim }}>{connectedAccounts.length} connected posting/source accounts</span>
            </div>
            {!currentUser ? (
              <EmptyState
                eyebrow="ACCOUNTS"
                title="Log in to connect posting accounts"
                body="Connected TikTok, YouTube, and Instagram accounts are private to each Clipse user."
                primaryLabel="Log In"
                onPrimary={() => { setAuthMode("login"); setTab("portal"); }}
                secondaryLabel="Create Account"
                onSecondary={() => { setAuthMode("register"); setTab("portal"); }}
              />
            ) : (
              <div style={{ display: "grid", gap: 18 }}>
                <Panel style={{ padding: 18 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                    <FilterBar filters={["All Accounts", "TikTok", "Instagram", "YouTube"]} active={accountFilter} onChange={setAccountFilter} />
                    <PrimaryButton onClick={() => startOAuth("tiktok", "posting")}>+ Add Account</PrimaryButton>
                  </div>
                </Panel>

                <div className="account-card-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
                  {filteredAccounts.map((account) => {
                      const health = postingHealth.find((entry) => entry.account?.id === account.id);
                      return (
                        <AccountCard
                          key={account.id}
                          account={account}
                          health={health}
                          autoModeEnabled={hasFeature("autoMode")}
                          onPost={() => openPostModal(displayClips[0])}
                          onVerify={() => verifyConnectedAccount(account.id)}
                          onDisconnect={() => disconnectConnectedAccount(account.id)}
                          onUpgradeAutoMode={() => showUpgrade("Auto Mode is included on Growth and Studio. It will automatically clip, caption, and post across multiple accounts.", "autoMode")}
                        />
                      );
                    })}
                  {!filteredAccounts.length ? (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <EmptyState
                        eyebrow={accountFilter === "All Accounts" ? "NO ACCOUNTS" : "NO MATCHES"}
                        title={accountFilter === "All Accounts" ? "Connect your first posting account" : `No ${accountFilter} accounts yet`}
                        body="Start with TikTok for the MVP flow. You can add more posting destinations as platform access is approved."
                        primaryLabel="+ Add TikTok"
                        onPrimary={() => startOAuth("tiktok", "posting")}
                        secondaryLabel={isAdmin ? "Add Local Test" : undefined}
                        onSecondary={isAdmin ? () => connectLocalTestAccount("tiktok", "posting") : undefined}
                      />
                    </div>
                  ) : null}
                </div>

                <Panel style={{ padding: 22 }}>
                  <div style={{ fontSize: 10, color: "#555", letterSpacing: 2, marginBottom: 12 }}>CONNECT ACCOUNTS</div>
                  <div style={{ display: "grid", gap: 10, marginBottom: 18 }}>
                    {(oauthProviders.length ? oauthProviders : Object.keys(PLATFORMS).map((provider) => ({ provider, configured: false, scopes: [] })))
                      .slice()
                      .sort((a, b) => platformPriority(a.provider) - platformPriority(b.provider))
                      .map((provider) => {
                      const platform = PLATFORMS[provider.provider] || PLATFORMS.twitch;
                      const accountType = provider.provider === "twitch" ? "source" : "posting";
                      return (
                        <div key={provider.provider} style={{ background: provider.provider === "tiktok" ? "#190b14" : "#0b0b16", border: `1px solid ${provider.provider === "tiktok" ? "#ff5b7944" : "#202034"}`, borderRadius: 12, padding: 14 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
                            <div>
                              <div style={{ color: "#fff", fontWeight: 800 }}>{platform.label} {provider.provider === "tiktok" ? "PRIMARY" : ""}</div>
                              <div style={{ color: "#666", fontSize: 10, marginTop: 4 }}>{accountType === "source" ? "Clip source account" : "Posting account"}</div>
                              {isAdmin && provider.provider === "tiktok" ? (
                                <div style={{ color: "#facc15", fontSize: 10, marginTop: 8, lineHeight: 1.5 }}>
                                  TikTok client_key fixes: use the Developer Portal value named Client key, enable Login Kit for Desktop for localhost, add your TikTok account as a Test User, and register {provider.redirectUri || "the callback URL"}.
                                </div>
                              ) : null}
                            </div>
                            <Tag color={provider.configured ? "#4ade8018" : "#facc1518"} text={provider.configured ? "#4ade80" : "#facc15"}>{provider.configured ? "OAUTH READY" : isAdmin ? "LOCAL TEST" : "COMING SOON"}</Tag>
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {isAdmin && provider.provider === "tiktok" ? <Tag color="#ffffff10" text="#aaa">SCOPE {provider.scopes?.join(",") || "user.info.basic"}</Tag> : null}
                            {isAdmin && provider.provider === "tiktok" ? <Tag color="#ffffff10" text="#aaa">{String(provider.diagnostics?.loginKit || "desktop").toUpperCase()}</Tag> : null}
                            {isAdmin && provider.provider === "tiktok" ? <Tag color="#ffffff10" text="#aaa">PKCE {provider.diagnostics?.pkce ? "ON" : "OFF"}</Tag> : null}
                            <PrimaryButton
                              onClick={() => provider.configured ? startOAuth(provider.provider, accountType) : isAdmin ? connectLocalTestAccount(provider.provider, accountType) : notify(`${platform.label} connection is coming soon`, "ok")}
                              disabled={!provider.configured && !isAdmin}
                              style={{ flex: 1, minWidth: 170, opacity: !provider.configured && !isAdmin ? 0.55 : 1 }}
                            >
                              {provider.configured ? `CONNECT ${platform.label.toUpperCase()}` : isAdmin ? `ADD ${platform.label.toUpperCase()} TEST` : `${platform.label.toUpperCase()} COMING SOON`}
                            </PrimaryButton>
                            {provider.provider === "tiktok" && provider.configured ? (
                              <GhostButton onClick={() => startOAuth(provider.provider, accountType, "posting")} style={{ flex: 1, minWidth: 190 }}>
                                REQUEST POSTING ACCESS
                              </GhostButton>
                            ) : null}
                            {isAdmin && !provider.configured ? <GhostButton onClick={() => setAccountForm((prev) => ({ ...prev, platform: provider.provider, accountType }))}>MANUAL</GhostButton> : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {isAdmin ? (
                    <>
                      <div style={{ fontSize: 10, color: "#555", letterSpacing: 2, marginBottom: 12 }}>ADVANCED TOKEN LINK</div>
                      <form onSubmit={addConnectedAccount} style={{ display: "grid", gap: 14 }}>
                        <Field label="PLATFORM">
                          <Select value={accountForm.platform} onChange={(e) => setAccountForm((prev) => ({ ...prev, platform: e.target.value }))}>
                            <option value="tiktok">TikTok</option>
                            <option value="instagram">Instagram</option>
                            <option value="youtube">YouTube</option>
                            <option value="twitch">Twitch</option>
                          </Select>
                        </Field>
                        <Field label="PLATFORM ACCOUNT ID">
                          <TextInput value={accountForm.platformAccountId} onChange={(e) => setAccountForm((prev) => ({ ...prev, platformAccountId: e.target.value }))} />
                        </Field>
                        <Field label="DISPLAY NAME">
                          <TextInput value={accountForm.displayName} onChange={(e) => setAccountForm((prev) => ({ ...prev, displayName: e.target.value }))} />
                        </Field>
                        <Field label="ACCOUNT TYPE">
                          <Select value={accountForm.accountType} onChange={(e) => setAccountForm((prev) => ({ ...prev, accountType: e.target.value }))}>
                            <option value="posting">Posting</option>
                            <option value="source">Source</option>
                            <option value="both">Both</option>
                          </Select>
                        </Field>
                        <Field label="ACCESS TOKEN">
                          <TextInput value={accountForm.accessToken} onChange={(e) => setAccountForm((prev) => ({ ...prev, accessToken: e.target.value }))} />
                        </Field>
                        <Field label="REFRESH TOKEN">
                          <TextInput value={accountForm.refreshToken} onChange={(e) => setAccountForm((prev) => ({ ...prev, refreshToken: e.target.value }))} />
                        </Field>
                        <Field label="TOKEN EXPIRES AT">
                          <TextInput type="datetime-local" value={accountForm.tokenExpiresAt} onChange={(e) => setAccountForm((prev) => ({ ...prev, tokenExpiresAt: e.target.value }))} />
                        </Field>
                        <PrimaryButton type="submit">CONNECT ACCOUNT</PrimaryButton>
                      </form>
                    </>
                  ) : null}
                </Panel>
              </div>
            )}
          </div>
        ) : null}

        {tab === "analytics" ? (
          !hasFeature("analytics") ? (
            <Panel style={{ padding: 34, textAlign: "center" }}>
              <div style={{ color: "#fff", fontSize: 26, fontWeight: 900, marginBottom: 10 }}>Analytics is a Growth feature</div>
              <div style={{ color: THEME.muted, lineHeight: 1.7, maxWidth: 620, margin: "0 auto 18px" }}>
                Upgrade to Growth or Studio to unlock performance tracking, top clip insights, best posting time, and engagement analytics.
              </div>
              <PrimaryButton onClick={() => showUpgrade("Analytics is included on Growth and Studio plans.", "analytics")}>View Growth Plan</PrimaryButton>
            </Panel>
          ) : (
          <div style={{ display: "grid", gap: 18 }}>
            <div className="stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 18 }}>
              <StatCard label="Total Views" value={fmtViews(totalViews)} accent="#8b5cf6" />
              <StatCard label="Total Likes" value={fmtViews(totalLikes)} accent="#22c55e" />
              <StatCard label="Total Comments" value={fmtViews(totalComments)} accent="#3b82f6" />
              <StatCard label="Shares" value={fmtViews(totalShares)} accent="#f59e0b" />
            </div>
            <div className="analytics-grid" style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 18 }}>
              <AnalyticsCard title="Views Over Time">
                <ViewsChart hasData={hasAnalyticsData} />
              </AnalyticsCard>
              <AnalyticsCard title="Top Performing Clips">
                <div style={{ display: "grid", gap: 12 }}>
                  {accountClips.slice(0, 5).map((clip, index) => (
                    <div key={clip.id || index} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      {clipImage(clip) ? <img src={clipImage(clip)} alt={clipTitle(clip)} style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 10 }} /> : <div style={{ width: 44, height: 44, borderRadius: 10, background: THEME.purpleSoft }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: "#fff", fontSize: 12, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{clipTitle(clip)}</div>
                        <div style={{ color: THEME.muted, fontSize: 11 }}>{fmtViews(clip.view_count || 0)} views</div>
                      </div>
                    </div>
                  ))}
                  {!accountClips.length ? <div style={{ color: THEME.muted, fontSize: 13, lineHeight: 1.6 }}>No posted clips yet. Top clips will appear here after this account starts posting.</div> : null}
                  <GhostButton onClick={() => setTab("clips")}>View all</GhostButton>
                </div>
              </AnalyticsCard>
            </div>
            <div className="analytics-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 18 }}>
              <AnalyticsCard title="Traffic Sources">
                <div style={{ display: "flex", gap: 18, alignItems: "center" }}>
                  <div style={{ width: 104, height: 104, borderRadius: "50%", background: hasAnalyticsData ? "conic-gradient(#8b5cf6 0 65%,#3b82f6 65% 85%,#22c55e 85% 95%,rgba(255,255,255,0.12) 95%)" : "rgba(255,255,255,0.08)", boxShadow: "inset 0 0 0 24px #0f1020" }} />
                  <div style={{ color: THEME.muted, fontSize: 13, lineHeight: 1.9 }}>
                    <div><span style={{ color: "#8b5cf6" }}>●</span> For You Page {hasAnalyticsData ? "65%" : "0%"}</div>
                    <div><span style={{ color: "#3b82f6" }}>●</span> Following {hasAnalyticsData ? "20%" : "0%"}</div>
                    <div><span style={{ color: "#22c55e" }}>●</span> Profile {hasAnalyticsData ? "10%" : "0%"}</div>
                  </div>
                </div>
              </AnalyticsCard>
              <AnalyticsCard title="Best Posting Time">
                <div style={{ color: "#fff", fontSize: 28, fontWeight: 900 }}>{hasAnalyticsData ? "7PM - 9PM" : "Not enough data"}</div>
                <div style={{ color: THEME.muted, marginTop: 6 }}>{hasAnalyticsData ? "Engagement is 23% higher in this window." : "Posting-time recommendations appear after enough posts."}</div>
                <div style={{ display: "flex", gap: 5, alignItems: "end", height: 78, marginTop: 18 }}>
                  {(hasAnalyticsData ? [16, 22, 28, 34, 44, 56, 72, 64] : [8, 8, 8, 8, 8, 8, 8, 8]).map((height, index) => <div key={index} style={{ flex: 1, height, background: "linear-gradient(180deg,#8b5cf6,rgba(139,92,246,0.12))", borderRadius: 6, opacity: hasAnalyticsData ? 1 : 0.35 }} />)}
                </div>
              </AnalyticsCard>
              <AnalyticsCard title="Engagement Rate">
                <div style={{ color: "#fff", fontSize: 36, fontWeight: 900 }}>{fmtPercent(engagementRate)}</div>
                <div style={{ color: hasAnalyticsData ? THEME.green : THEME.muted, fontWeight: 800 }}>{hasAnalyticsData ? "Based on current workspace data" : "No posts yet"}</div>
                <div style={{ marginTop: 16, height: 70, borderRadius: 14, background: "linear-gradient(135deg,rgba(139,92,246,0.2),rgba(46,232,139,0.08))" }} />
              </AnalyticsCard>
            </div>
          </div>
          )
        ) : null}

        {tab === "templates" ? (
          <Panel style={{ padding: 32 }}>
            <div style={{ color: "#fff", fontSize: 22, fontWeight: 900, marginBottom: 8 }}>Templates</div>
            <div style={{ color: THEME.muted, lineHeight: 1.7 }}>
              Templates are a strong paid feature later: reusable captions, format presets, and account-specific posting styles.
            </div>
          </Panel>
        ) : null}

        {tab === "settings" ? (
          <div style={{ display: "grid", gap: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center" }}>
              <div>
                <h1 style={{ color: "#fff", fontSize: 32, lineHeight: 1.1, margin: 0, fontFamily: "'Space Grotesk',sans-serif" }}>Settings</h1>
                <div style={{ color: THEME.muted, fontSize: 14, marginTop: 6 }}>{isAdmin ? "Workspace rules, defaults, billing, and permissions." : "Plan details, posting defaults, and notification preferences."}</div>
              </div>
              <GhostButton onClick={() => setTab("home")} style={{ padding: "9px 13px" }}>← Back to Dashboard</GhostButton>
            </div>

            <Panel style={{ padding: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {visibleSettingsSections.map(([section, label]) => (
                <button
                  key={section}
                  onClick={() => setSettingsSection(section)}
                  style={{ border: `1px solid ${settingsSection === section ? "rgba(139,92,246,0.65)" : THEME.line}`, background: settingsSection === section ? "linear-gradient(135deg,rgba(139,92,246,0.3),rgba(109,40,217,0.18))" : "rgba(255,255,255,0.035)", color: settingsSection === section ? "#fff" : THEME.muted, borderRadius: 12, padding: "10px 13px", fontWeight: 850, cursor: "pointer" }}
                >
                  {label}
                </button>
              ))}
            </Panel>

            {settingsSection === "billing" ? (
              <>
                <div style={{ textAlign: "center", maxWidth: 780, margin: "0 auto 2px" }}>
                  <h1 style={{ color: "#fff", fontSize: 34, lineHeight: 1.08, margin: "0 0 9px", fontFamily: "'Space Grotesk',sans-serif" }}>Simple pricing for scaling clip accounts</h1>
                  <div style={{ color: THEME.muted, fontSize: 15.5 }}>Start free. Upgrade when you’re ready.</div>
                  <div style={{ margin: "16px auto 0", display: "inline-flex", background: "rgba(255,255,255,0.04)", border: `1px solid ${THEME.line}`, borderRadius: 12, padding: 4 }}>
                    {["monthly", "yearly"].map((cycle) => (
                      <button key={cycle} onClick={() => setBillingCycle(cycle)} style={{ minWidth: 132, border: "none", borderRadius: 9, padding: "9px 16px", color: billingCycle === cycle ? "#fff" : THEME.muted, background: billingCycle === cycle ? "linear-gradient(135deg,#8b5cf6,#6d28d9)" : "transparent", fontWeight: 900, cursor: "pointer" }}>
                        {cycle === "monthly" ? "Monthly" : <>Yearly <span style={{ color: THEME.green, fontSize: 11, marginLeft: 8 }}>20% OFF</span></>}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="account-card-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 18, maxWidth: 1080, margin: "0 auto", width: "100%" }}>
                  {Object.entries(billingDisplay.plans || PUBLIC_PLANS).filter(([planKey]) => ["free", "starter", "pro"].includes(planKey)).map(([planKey, plan]) => {
                    const price = billingCycle === "yearly" ? Math.round((plan.priceMonthlyUsd || 0) * 0.8) : plan.priceMonthlyUsd || 0;
                    const isCurrent = Boolean(currentUser && billingDisplay?.plan?.key === planKey);
                    const planRank = { free: 0, starter: 1, pro: 2 };
                    const currentPlanKey = billingDisplay?.plan?.key || "free";
                    const isLowerThanCurrent = Boolean(currentUser && (planRank[planKey] || 0) < (planRank[currentPlanKey] || 0));
                    const selectedPlanKey = selectedBillingPlan || billingDisplay?.plan?.key || "starter";
                    const isSelected = selectedPlanKey === planKey;
                    const buttonDisabled = isCurrent || isLowerThanCurrent;
                    const buttonLabel = !currentUser ? (planKey === "free" ? "Get Started" : `Choose ${plan.label}`) : isCurrent ? "Current Plan" : isLowerThanCurrent ? `Included in ${billingDisplay?.plan?.label || "Current"}` : planKey === "free" ? "Get Started" : `Upgrade to ${plan.label}`;
                    return (
                      <Panel key={planKey} onClick={() => setSelectedBillingPlan(planKey)} style={{ padding: 20, minHeight: 352, borderColor: isSelected ? "rgba(139,92,246,0.95)" : THEME.line, boxShadow: isSelected ? "0 0 70px rgba(139,92,246,0.16), inset 0 1px 0 rgba(255,255,255,0.04)" : undefined, position: "relative", display: "flex", flexDirection: "column", cursor: "pointer" }}>
                        {plan.featured ? <div style={{ position: "absolute", top: -13, left: "50%", transform: "translateX(-50%)", background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", color: "#fff", borderRadius: 8, padding: "6px 15px", fontWeight: 900, fontSize: 11 }}>MOST POPULAR</div> : null}
                        {isCurrent ? <div style={{ position: "absolute", right: 14, top: 14 }}><Tag color="rgba(46,232,139,0.16)" text={THEME.green}>Current</Tag></div> : null}
                        <div style={{ color: THEME.purple, textTransform: "uppercase", fontWeight: 900, fontSize: 15, marginBottom: 10 }}>{plan.label}</div>
                        <div style={{ marginBottom: 8 }}><span style={{ color: "#fff", fontSize: 36, fontWeight: 900 }}>${price}</span><span style={{ color: THEME.muted, fontSize: 13 }}> / month</span></div>
                        <div style={{ color: "#d8d4e5", lineHeight: 1.45, minHeight: 42, fontSize: 13 }}>{plan.tagline}</div>
                        <div style={{ height: 1, background: THEME.line, margin: "14px 0" }} />
                        <div style={{ display: "grid", gap: 9, minHeight: 108, flex: 1, alignContent: "start" }}>
                          {(plan.features || []).map((feature) => <div key={feature} style={{ color: "#fff", fontSize: 13 }}>✓ {feature}</div>)}
                          {!plan.autoMode ? <div style={{ color: THEME.muted, fontSize: 13 }}>× Auto-posting</div> : null}
                        </div>
                        <PrimaryButton onClick={(event) => { event.stopPropagation(); handleBillingSelect(planKey, plan); }} disabled={buttonDisabled} style={{ width: "100%", marginTop: "auto", padding: "11px 14px", background: isSelected && !buttonDisabled ? "linear-gradient(135deg,#8b5cf6,#6d28d9)" : "transparent", border: `1px solid ${THEME.purple}`, color: isSelected && !buttonDisabled ? "#fff" : "#c084fc", boxShadow: isSelected && !buttonDisabled ? undefined : "none", opacity: buttonDisabled ? 0.55 : 1 }}>
                          {buttonLabel}
                        </PrimaryButton>
                      </Panel>
                    );
                  })}
                </div>

                <Panel style={{ padding: 14, maxWidth: 1080, margin: "0 auto", width: "100%" }}>
                  <div style={{ color: "#fff", fontSize: 16, fontWeight: 900, marginBottom: 10 }}>Compare plans</div>
                  {[
                    ["Accounts", "1", "5", "15"],
                    ["Clips per month", "10", "100", "500"],
                    ["Scheduling", "✓", "✓", "✓"],
                    ["Auto-posting", "×", "×", "✓"],
                    ["AI captions & hashtags", "×", "✓", "✓"],
                    ["Analytics", "×", "×", "Advanced"],
                  ].map((row, index) => (
                    <div key={row[0]} style={{ display: "grid", gridTemplateColumns: "1.5fr repeat(3,1fr)", borderTop: index === 0 ? `1px solid ${THEME.line}` : "none", borderBottom: `1px solid ${THEME.line}`, color: "#fff" }}>
                      {row.map((cell, cellIndex) => <div key={cellIndex} style={{ padding: "9px 14px", color: cell === "×" ? THEME.muted : cell === "✓" ? THEME.purple : "#fff", textAlign: cellIndex === 0 ? "left" : "center", fontWeight: cellIndex === 0 ? 800 : 600, fontSize: 13 }}>{cell}</div>)}
                    </div>
                  ))}
                </Panel>
              </>
            ) : null}

            {isAdmin && settingsSection === "workspace" ? (
              <Panel style={{ padding: 22 }}>
                <div style={{ color: "#fff", fontSize: 20, fontWeight: 900, marginBottom: 6 }}>Workspace Profile</div>
                <div style={{ color: THEME.muted, marginBottom: 20 }}>Defaults that affect the whole Clipse workspace.</div>
                <div className="account-card-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 16 }}>
                  <Field label="WORKSPACE NAME"><TextInput value={workspaceSettings.workspaceName} onChange={(e) => updateWorkspaceSetting("workspaceName", e.target.value)} /></Field>
                  <Field label="OWNER EMAIL"><TextInput value={currentUser?.email || workspaceSettings.ownerEmail} onChange={(e) => updateWorkspaceSetting("ownerEmail", e.target.value)} /></Field>
                  <Field label="TIMEZONE"><Select value={workspaceSettings.timezone} onChange={(e) => updateWorkspaceSetting("timezone", e.target.value)}><option>America/New_York</option><option>America/Chicago</option><option>America/Los_Angeles</option><option>UTC</option></Select></Field>
                  <Field label="DEFAULT CURRENCY"><Select value={workspaceSettings.currency} onChange={(e) => updateWorkspaceSetting("currency", e.target.value)}><option>USD</option><option>CAD</option><option>EUR</option><option>GBP</option></Select></Field>
                </div>
                <PrimaryButton onClick={() => notify("Workspace settings saved") } style={{ marginTop: 18 }}>Save Workspace</PrimaryButton>
              </Panel>
            ) : null}

            {settingsSection === "posting" ? (
              <Panel style={{ padding: 22 }}>
                <div style={{ color: "#fff", fontSize: 20, fontWeight: 900, marginBottom: 6 }}>Posting Defaults</div>
                <div style={{ color: THEME.muted, marginBottom: 20 }}>Reusable defaults for captions, formats, and schedules.</div>
                <div className="account-card-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 16 }}>
                  <Field label="DEFAULT FORMAT"><Select value={workspaceSettings.defaultFormat} onChange={(e) => updateWorkspaceSetting("defaultFormat", e.target.value)}><option>9:16</option><option>1:1</option><option>16:9</option></Select></Field>
                  <Field label="CAPTION STYLE"><Select value={workspaceSettings.captionStyle} onChange={(e) => updateWorkspaceSetting("captionStyle", e.target.value)}><option>High-energy hook</option><option>Clean and simple</option><option>Meme-forward</option><option>Creator brand voice</option></Select></Field>
                  <Field label="DEFAULT HASHTAGS"><TextInput value={workspaceSettings.defaultHashtags} onChange={(e) => updateWorkspaceSetting("defaultHashtags", e.target.value)} /></Field>
                  <Field label="BEST POSTING WINDOW"><Select value={workspaceSettings.scheduleWindow} onChange={(e) => updateWorkspaceSetting("scheduleWindow", e.target.value)}><option>7PM - 9PM</option><option>12PM - 2PM</option><option>4PM - 6PM</option><option>Auto choose later</option></Select></Field>
                </div>
                <PrimaryButton onClick={() => notify("Posting defaults saved") } style={{ marginTop: 18 }}>Save Defaults</PrimaryButton>
              </Panel>
            ) : null}

            {isAdmin && settingsSection === "safety" ? (
              <Panel style={{ padding: 22 }}>
                <div style={{ color: "#fff", fontSize: 20, fontWeight: 900, marginBottom: 6 }}>Safety & Automation</div>
                <div style={{ color: THEME.muted, marginBottom: 20 }}>Guardrails that help protect posting accounts while Auto Mode grows.</div>
                <div className="account-card-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 16, marginBottom: 18 }}>
                  <Field label="MAX POSTS PER ACCOUNT / DAY"><TextInput value={workspaceSettings.dailyPostLimit} onChange={(e) => updateWorkspaceSetting("dailyPostLimit", e.target.value)} /></Field>
                  <Field label="MINUTES BETWEEN POSTS"><TextInput value={workspaceSettings.postSpacingMinutes} onChange={(e) => updateWorkspaceSetting("postSpacingMinutes", e.target.value)} /></Field>
                </div>
                {[
                  ["reviewBeforePost", "Review before posting", "Keep manual approval on before uploads go live."],
                  ["duplicateCaptionProtection", "Duplicate caption protection", "Block repeated captions once testing is finished."],
                  ["watermarkFree", "Watermark free-plan exports", "Reserve no-watermark exports for paid plans."],
                ].map(([key, title, description]) => (
                  <div key={key} style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", padding: "14px 0", borderTop: `1px solid ${THEME.line}` }}>
                    <div><div style={{ color: "#fff", fontWeight: 850 }}>{title}</div><div style={{ color: THEME.muted, fontSize: 13, marginTop: 3 }}>{description}</div></div>
                    <ToggleSwitch checked={workspaceSettings[key]} onChange={(value) => updateWorkspaceSetting(key, value)} />
                  </div>
                ))}
                <PrimaryButton onClick={() => notify("Safety rules saved") } style={{ marginTop: 8 }}>Save Safety Rules</PrimaryButton>
              </Panel>
            ) : null}

            {settingsSection === "notifications" ? (
              <Panel style={{ padding: 22 }}>
                <div style={{ color: "#fff", fontSize: 20, fontWeight: 900, marginBottom: 6 }}>Notifications</div>
                <div style={{ color: THEME.muted, marginBottom: 12 }}>Choose the operational alerts that matter.</div>
                {[
                  ["uploadFailedAlerts", "Upload failed", "Know when TikTok, YouTube, or Instagram rejects a post."],
                  ["queueStuckAlerts", "Queue stuck", "Get alerted when a post sits too long in scheduled or processing."],
                  ["tokenExpiredAlerts", "Account token expired", "Prompt reconnects before posts start failing."],
                  ["postReadyAlerts", "Post ready for review", "Notify admins when sandbox or review-mode posts need approval."],
                ].map(([key, title, description]) => (
                  <div key={key} style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", padding: "14px 0", borderTop: `1px solid ${THEME.line}` }}>
                    <div><div style={{ color: "#fff", fontWeight: 850 }}>{title}</div><div style={{ color: THEME.muted, fontSize: 13, marginTop: 3 }}>{description}</div></div>
                    <ToggleSwitch checked={workspaceSettings[key]} onChange={(value) => updateWorkspaceSetting(key, value)} />
                  </div>
                ))}
              </Panel>
            ) : null}

            {isAdmin && settingsSection === "team" ? (
              <Panel style={{ padding: 22 }}>
                <div style={{ color: "#fff", fontSize: 20, fontWeight: 900, marginBottom: 6 }}>Team Permissions</div>
                <div style={{ color: THEME.muted, marginBottom: 18 }}>Set default permissions before team invites become a paid feature.</div>
                {[
                  ["memberCanPost", "Members can publish posts", "If off, non-admins can only draft and queue for review."],
                  ["requireAdminApproval", "Require admin approval", "A safer default for agencies and shared clip accounts."],
                ].map(([key, title, description]) => (
                  <div key={key} style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", padding: "14px 0", borderTop: `1px solid ${THEME.line}` }}>
                    <div><div style={{ color: "#fff", fontWeight: 850 }}>{title}</div><div style={{ color: THEME.muted, fontSize: 13, marginTop: 3 }}>{description}</div></div>
                    <ToggleSwitch checked={workspaceSettings[key]} onChange={(value) => updateWorkspaceSetting(key, value)} />
                  </div>
                ))}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginTop: 16 }}>
                  {["Owner", "Admin", "Member"].map((role) => <Panel key={role} style={{ padding: 16, background: "rgba(255,255,255,0.035)" }}><div style={{ color: "#fff", fontWeight: 900 }}>{role}</div><div style={{ color: THEME.muted, fontSize: 12, marginTop: 6 }}>{role === "Owner" ? "Billing and all workspace controls." : role === "Admin" ? "Can manage posts, settings, and users." : "Can prepare clips and drafts."}</div></Panel>)}
                </div>
              </Panel>
            ) : null}

            {isAdmin && settingsSection === "privacy" ? (
              <Panel style={{ padding: 22 }}>
                <div style={{ color: "#fff", fontSize: 20, fontWeight: 900, marginBottom: 6 }}>Data & Privacy</div>
                <div style={{ color: THEME.muted, marginBottom: 20 }}>Export, retention, and workspace cleanup controls.</div>
                <div className="account-card-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 16, marginBottom: 18 }}>
                  <Field label="QUEUE HISTORY RETENTION"><Select value={workspaceSettings.dataRetention} onChange={(e) => updateWorkspaceSetting("dataRetention", e.target.value)}><option>30 days</option><option>90 days</option><option>180 days</option><option>1 year</option></Select></Field>
                  <Field label="EXPORT FORMAT"><Select defaultValue="JSON"><option>JSON</option><option>CSV</option></Select></Field>
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <GhostButton onClick={() => notify("Export will be wired to backend storage next")}>Export Workspace Data</GhostButton>
                  <GhostButton onClick={() => notify("Queue history cleanup will be wired next")}>Clear Queue History</GhostButton>
                  <GhostButton onClick={() => notify("Delete workspace requires a confirmation flow") } style={{ color: THEME.danger, borderColor: "rgba(255,91,121,0.35)" }}>Delete Workspace</GhostButton>
                </div>
              </Panel>
            ) : null}
          </div>
        ) : null}

        {tab === "ops" ? (
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 24 }}>
              <h2 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 22, color: "#fff", margin: 0 }}>Create Post</h2>
              <span style={{ fontSize: 13, color: THEME.dim }}>choose clip, prepare video, and queue</span>
            </div>
            {!currentUser ? (
              <EmptyState
                eyebrow="CREATE POST"
                title="Log in to prepare clips"
                body="Preparing and scheduling clips is account-specific, so Clipse needs to know which user owns the clip and posting account."
                primaryLabel="Log In"
                onPrimary={() => { setAuthMode("login"); setTab("portal"); }}
                secondaryLabel="Create Account"
                onSecondary={() => { setAuthMode("register"); setTab("portal"); }}
              />
            ) : (
              <CreatePostWizard
                workspaceClips={workspaceClips}
                selectedPrepClip={selectedPrepClip}
                selectedPrepClipId={selectedPrepClipId}
                setSelectedPrepClipId={setSelectedPrepClipId}
                createPostStep={createPostStep}
                setCreatePostStep={setCreatePostStep}
                uploadDrafts={uploadDrafts}
                setUploadDraft={setUploadDraft}
                connectedAccounts={connectedAccounts}
                postingHealth={postingHealth}
                postReadiness={postReadiness}
                checkPostReadiness={checkPostReadiness}
                scheduleUpload={scheduleUpload}
                manualVideoFiles={manualVideoFiles}
                setManualVideoFiles={setManualVideoFiles}
                uploadManualVideoAsset={uploadManualVideoAsset}
                cliprInputs={cliprInputs}
                setCliprInputs={setCliprInputs}
                cliprRights={cliprRights}
                setCliprRights={setCliprRights}
                cliprImports={cliprImports}
                importCliprVideoAsset={importCliprVideoAsset}
                videoNeedsAttention={videoNeedsAttention}
                setTab={setTab}
              />
            )}
          </div>
        ) : null}

        {tab === "admin" ? (
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 24 }}>
              <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 34, letterSpacing: 4, color: "#fff", margin: 0 }}>ADMIN TOOLS</h1>
              <span style={{ fontSize: 11, color: "#333" }}>team and backend controls</span>
            </div>
            {isPlatformAdmin ? (
              <div style={{ display: "grid", gap: 18, marginBottom: 18 }}>
                <Panel style={{ padding: 22, background: "linear-gradient(135deg,rgba(139,92,246,0.14),rgba(255,255,255,0.035))" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: 10, color: THEME.purple, letterSpacing: 2, fontWeight: 900 }}>PLATFORM ADMIN</div>
                      <div style={{ color: "#fff", fontSize: 22, fontWeight: 950, marginTop: 6 }}>Global Clipse Control Center</div>
                    </div>
                    <Tag color="#7c3aed18" text="#c4b5fd">ALL TENANTS</Tag>
                  </div>
                  {platformSummary ? (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
                      <Panel style={{ padding: 14, background: "#0b0b16" }}><div style={{ color: "#666", fontSize: 10 }}>USERS</div><div style={{ color: "#fff", fontSize: 24, fontWeight: 800 }}>{platformSummary.users}</div></Panel>
                      <Panel style={{ padding: 14, background: "#0b0b16" }}><div style={{ color: "#666", fontSize: 10 }}>TENANTS</div><div style={{ color: "#fff", fontSize: 24, fontWeight: 800 }}>{platformSummary.tenants}</div></Panel>
                      <Panel style={{ padding: 14, background: "#0b0b16" }}><div style={{ color: "#666", fontSize: 10 }}>ACCOUNTS</div><div style={{ color: "#fff", fontSize: 24, fontWeight: 800 }}>{platformSummary.accounts}</div></Panel>
                      <Panel style={{ padding: 14, background: "#0b0b16" }}><div style={{ color: "#666", fontSize: 10 }}>CLIPS</div><div style={{ color: "#fff", fontSize: 24, fontWeight: 800 }}>{platformSummary.clips}</div></Panel>
                    </div>
                  ) : null}
                </Panel>

                <Panel style={{ padding: 22, borderColor: productionReadiness?.overallStatus === "ready" ? "#4ade8044" : "#facc1544" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: 10, color: THEME.purple, letterSpacing: 2, fontWeight: 900 }}>PRODUCTION READINESS</div>
                      <div style={{ color: "#fff", fontSize: 20, fontWeight: 950, marginTop: 6 }}>Launch & TikTok Review Checklist</div>
                      <div style={{ color: THEME.muted, fontSize: 12, marginTop: 5 }}>Visible only to platform admins.</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      {productionReadiness ? (
                        <Tag
                          color={productionReadiness.overallStatus === "ready" ? "#4ade8018" : productionReadiness.overallStatus === "needs_review" ? "#facc1518" : "#ef444418"}
                          text={productionReadiness.overallStatus === "ready" ? "#4ade80" : productionReadiness.overallStatus === "needs_review" ? "#facc15" : "#ef4444"}
                        >
                          {productionReadiness.okCount}/{productionReadiness.totalChecks} READY
                        </Tag>
                      ) : null}
                      <GhostButton onClick={refreshProductionReadiness}>Refresh</GhostButton>
                    </div>
                  </div>

                  {productionReadiness ? (
                    <div style={{ display: "grid", gap: 14 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
                        <Panel style={{ padding: 14, background: "#0b0b16" }}><div style={{ color: "#666", fontSize: 10 }}>STATUS</div><div style={{ color: "#fff", fontSize: 18, fontWeight: 900 }}>{productionReadiness.overallStatus.replace(/_/g, " ").toUpperCase()}</div></Panel>
                        <Panel style={{ padding: 14, background: "#0b0b16" }}><div style={{ color: "#666", fontSize: 10 }}>ASSETS</div><div style={{ color: "#fff", fontSize: 18, fontWeight: 900 }}>{productionReadiness.processedAssets}</div></Panel>
                        <Panel style={{ padding: 14, background: "#0b0b16" }}><div style={{ color: "#666", fontSize: 10 }}>UPDATED</div><div style={{ color: "#fff", fontSize: 12, fontWeight: 800 }}>{formatDateTime(productionReadiness.generatedAt)}</div></Panel>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
                        {productionReadiness.checks.map((check) => (
                          <div key={check.key} style={{ background: "#0b0b16", border: `1px solid ${check.ok ? "#4ade8033" : "#facc1533"}`, borderRadius: 12, padding: 14 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", marginBottom: 8 }}>
                              <div style={{ color: "#fff", fontWeight: 850 }}>{check.label}</div>
                              <Tag color={check.ok ? "#4ade8018" : "#facc1518"} text={check.ok ? "#4ade80" : "#facc15"}>{check.ok ? "READY" : "CHECK"}</Tag>
                            </div>
                            <div style={{ color: "#b7adc9", fontSize: 11, lineHeight: 1.5, wordBreak: "break-word" }}>{check.value}</div>
                            {!check.ok ? <div style={{ color: "#facc15", fontSize: 10, lineHeight: 1.5, marginTop: 8 }}>{check.action}</div> : null}
                          </div>
                        ))}
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <Panel style={{ padding: 14, background: "#0b0b16" }}>
                          <div style={{ color: "#666", fontSize: 10, letterSpacing: 1.5, marginBottom: 10 }}>QUEUE DEPTH</div>
                          <div style={{ display: "grid", gap: 6 }}>
                            {productionReadiness.queueDepth.map((row) => <div key={`${row.queue_name}-${row.status}`} style={{ display: "flex", justifyContent: "space-between", color: "#dcd8e8", fontSize: 11 }}><span>{row.queue_name} • {row.status}</span><strong>{row.total}</strong></div>)}
                            {!productionReadiness.queueDepth.length ? <div style={{ color: "#666", fontSize: 11 }}>No jobs recorded.</div> : null}
                          </div>
                        </Panel>
                        <Panel style={{ padding: 14, background: "#0b0b16" }}>
                          <div style={{ color: "#666", fontSize: 10, letterSpacing: 1.5, marginBottom: 10 }}>UPLOADS LAST 24H</div>
                          <div style={{ display: "grid", gap: 6 }}>
                            {productionReadiness.recentUploadAttempts.map((row) => <div key={row.status} style={{ display: "flex", justifyContent: "space-between", color: "#dcd8e8", fontSize: 11 }}><span>{row.status}</span><strong>{row.total}</strong></div>)}
                            {!productionReadiness.recentUploadAttempts.length ? <div style={{ color: "#666", fontSize: 11 }}>No upload attempts in the last 24 hours.</div> : null}
                          </div>
                        </Panel>
                      </div>
                    </div>
                  ) : <div style={{ color: "#555" }}>Production readiness not loaded yet.</div>}
                </Panel>

                <Panel style={{ padding: 22 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: 10, color: "#555", letterSpacing: 2, marginBottom: 6 }}>GLOBAL USERS</div>
                      <div style={{ color: THEME.muted, fontSize: 12 }}>Search and manage users across every Clipse tenant.</div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <TextInput value={platformSearch} onChange={(e) => setPlatformSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadPlatformUsers()} placeholder="Search email or tenant..." style={{ width: 260, padding: "10px 12px" }} />
                      <GhostButton onClick={loadPlatformUsers}>Search</GhostButton>
                    </div>
                  </div>
                  <div style={{ display: "grid", gap: 10, maxHeight: 420, overflow: "auto" }}>
                    {platformUsers.map((user) => (
                      <div key={user.id} style={{ background: "#0b0b16", border: "1px solid #202034", borderRadius: 12, padding: 14, display: "grid", gridTemplateColumns: "1.4fr 1fr auto", gap: 10, alignItems: "center" }}>
                        <div>
                          <div style={{ color: "#fff", fontWeight: 800 }}>{user.email}</div>
                          <div style={{ color: "#666", fontSize: 11, marginTop: 4 }}>{user.tenant_name} • {user.tenant_plan} • {new Date(user.created_at).toLocaleString()}</div>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <Tag color="#7c3aed18" text="#a78bfa">{user.role.toUpperCase()}</Tag>
                          <Tag color={user.status === "active" ? "#4ade8018" : "#ef444418"} text={user.status === "active" ? "#4ade80" : "#ef4444"}>{user.status.toUpperCase()}</Tag>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                          <Select value={user.role} disabled={user.id === currentUser?.id} onChange={(e) => updatePlatformUser(user, { role: e.target.value })} style={{ width: 155, padding: "8px 10px", fontSize: 12 }}>
                            <option value="standard_user">Standard</option>
                            <option value="tenant_operator">Operator</option>
                            <option value="tenant_viewer">Viewer</option>
                            <option value="tenant_admin">Tenant Admin</option>
                            <option value="platform_admin">Platform Admin</option>
                          </Select>
                          {user.status === "disabled" ? (
                            <GhostButton onClick={() => updatePlatformUser(user, { status: "active" })} style={{ padding: "8px 10px" }}>Reactivate</GhostButton>
                          ) : (
                            <GhostButton onClick={() => updatePlatformUser(user, { status: "disabled" })} disabled={user.id === currentUser?.id} style={{ padding: "8px 10px" }}>Disable</GhostButton>
                          )}
                        </div>
                      </div>
                    ))}
                    {!platformUsers.length ? <div style={{ color: "#555" }}>No platform users loaded yet.</div> : null}
                  </div>
                </Panel>

                <Panel style={{ padding: 22 }}>
                  <div style={{ fontSize: 10, color: "#555", letterSpacing: 2, marginBottom: 12 }}>TENANTS</div>
                  <div style={{ display: "grid", gap: 10, maxHeight: 360, overflow: "auto" }}>
                    {platformTenants.map((tenant) => (
                      <div key={tenant.id} style={{ background: "#0b0b16", border: "1px solid #202034", borderRadius: 12, padding: 14, display: "grid", gridTemplateColumns: "1.3fr 1fr auto", gap: 10, alignItems: "center" }}>
                        <div>
                          <div style={{ color: "#fff", fontWeight: 800 }}>{tenant.name}</div>
                          <div style={{ color: "#666", fontSize: 11, marginTop: 4 }}>{tenant.users} users • {tenant.accounts} accounts • {tenant.clips} clips</div>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <Tag color="#7c3aed18" text="#a78bfa">{tenant.plan.toUpperCase()}</Tag>
                          <Tag color={tenant.status === "active" ? "#4ade8018" : "#ef444418"} text={tenant.status === "active" ? "#4ade80" : "#ef4444"}>{tenant.status.toUpperCase()}</Tag>
                        </div>
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                          <Select value={tenant.plan} onChange={(e) => updatePlatformTenant(tenant, { plan: e.target.value })} style={{ width: 110, padding: "8px 10px", fontSize: 12 }}>
                            <option value="free">Free</option>
                            <option value="starter">Starter</option>
                            <option value="pro">Pro</option>
                            <option value="studio">Studio</option>
                          </Select>
                          {tenant.status === "disabled" ? (
                            <GhostButton onClick={() => updatePlatformTenant(tenant, { status: "active" })} style={{ padding: "8px 10px" }}>Enable</GhostButton>
                          ) : (
                            <GhostButton onClick={() => updatePlatformTenant(tenant, { status: "disabled" })} style={{ padding: "8px 10px", color: "#ef4444", borderColor: "#ef444433" }}>Disable</GhostButton>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Panel>
              </div>
            ) : null}
            <div className="admin-grid" style={{ display: "grid", gridTemplateColumns: "0.9fr 1.1fr", gap: 18 }}>
              <Panel style={{ padding: 22 }}>
                <div style={{ fontSize: 10, color: "#555", letterSpacing: 2, marginBottom: 12 }}>CREATE TENANT USER</div>
                <form onSubmit={createTenantUser} style={{ display: "grid", gap: 12 }}>
                  <Field label="EMAIL">
                    <TextInput value={adminUserForm.email} onChange={(e) => setAdminUserForm((prev) => ({ ...prev, email: e.target.value }))} />
                  </Field>
                  <Field label="PASSWORD">
                    <TextInput type="password" value={adminUserForm.password} onChange={(e) => setAdminUserForm((prev) => ({ ...prev, password: e.target.value }))} />
                  </Field>
                  <Field label="ROLE">
                    <Select value={adminUserForm.role} onChange={(e) => setAdminUserForm((prev) => ({ ...prev, role: e.target.value }))}>
                      <option value="standard_user">Standard user</option>
                      <option value="tenant_operator">Tenant operator</option>
                      <option value="tenant_viewer">Tenant viewer</option>
                      <option value="tenant_admin">Tenant admin</option>
                    </Select>
                  </Field>
                  <PrimaryButton type="submit">CREATE USER</PrimaryButton>
                </form>
              </Panel>

              <div style={{ display: "grid", gap: 18 }}>
                <Panel style={{ padding: 22 }}>
                  <div style={{ fontSize: 10, color: "#555", letterSpacing: 2, marginBottom: 12 }}>TENANT SUMMARY</div>
                  {adminSummary ? (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
                      <Panel style={{ padding: 14, background: "#0b0b16" }}><div style={{ color: "#666", fontSize: 10 }}>USERS</div><div style={{ color: "#fff", fontSize: 24, fontWeight: 800 }}>{adminSummary.users}</div></Panel>
                      <Panel style={{ padding: 14, background: "#0b0b16" }}><div style={{ color: "#666", fontSize: 10 }}>ACCOUNTS</div><div style={{ color: "#fff", fontSize: 24, fontWeight: 800 }}>{adminSummary.accounts}</div></Panel>
                      <Panel style={{ padding: 14, background: "#0b0b16" }}><div style={{ color: "#666", fontSize: 10 }}>STREAMERS</div><div style={{ color: "#fff", fontSize: 24, fontWeight: 800 }}>{adminSummary.streamers}</div></Panel>
                      <Panel style={{ padding: 14, background: "#0b0b16" }}><div style={{ color: "#666", fontSize: 10 }}>CLIPS</div><div style={{ color: "#fff", fontSize: 24, fontWeight: 800 }}>{adminSummary.clips}</div></Panel>
                    </div>
                  ) : <div style={{ color: "#555" }}>No admin summary yet.</div>}
                </Panel>

                <Panel style={{ padding: 22 }}>
                  <div style={{ fontSize: 10, color: "#555", letterSpacing: 2, marginBottom: 12 }}>PROVIDER READINESS</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
                    {providerStatus.slice().sort((a, b) => platformPriority(a.platform) - platformPriority(b.platform)).map((provider) => {
                      const platform = PLATFORMS[provider.platform] || PLATFORMS.tiktok;
                      return (
                        <div key={provider.platform} style={{ background: provider.platform === "tiktok" ? "#190b14" : "#0b0b16", border: `1px solid ${provider.platform === "tiktok" ? "#ff5b7944" : "#202034"}`, borderRadius: 12, padding: 14 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", marginBottom: 10 }}>
                            <div style={{ color: "#fff", fontWeight: 800 }}>{platform.label}{provider.platform === "tiktok" ? " PRIMARY" : ""}</div>
                            <Tag color={`${platform.color}18`} text={platform.color}>{provider.uploadMode.toUpperCase()}</Tag>
                          </div>
                          <div style={{ display: "grid", gap: 6 }}>
                            <Tag color={provider.oauthConfigured ? "#4ade8018" : "#facc1518"} text={provider.oauthConfigured ? "#4ade80" : "#facc15"}>{provider.oauthConfigured ? "OAUTH READY" : "OAUTH SETUP"}</Tag>
                            <Tag color={provider.liveUploadImplemented ? "#4ade8018" : "#ffffff10"} text={provider.liveUploadImplemented ? "#4ade80" : "#aaa"}>{provider.liveUploadImplemented ? "LIVE UPLOAD" : "SIMULATED UPLOAD"}</Tag>
                            {provider.postMode ? <Tag color="#ffffff10" text="#aaa">{String(provider.postMode).toUpperCase()}</Tag> : null}
                          </div>
                          {provider.missing?.length ? <div style={{ color: "#666", fontSize: 10, marginTop: 10, lineHeight: 1.6 }}>{provider.missing.join(", ")}</div> : null}
                          {provider.notes?.length ? <div style={{ color: "#888", fontSize: 10, marginTop: 10, lineHeight: 1.6 }}>{provider.notes[0]}</div> : null}
                        </div>
                      );
                    })}
                    {!providerStatus.length ? <div style={{ color: "#555" }}>Provider status not loaded yet.</div> : null}
                  </div>
                </Panel>

                <Panel style={{ padding: 22 }}>
                  <div style={{ fontSize: 10, color: "#555", letterSpacing: 2, marginBottom: 12 }}>TENANT USERS</div>
                  <div style={{ display: "grid", gap: 10 }}>
                    {adminUsers.map((user) => (
                      <div key={user.id} style={{ background: "#0b0b16", border: "1px solid #202034", borderRadius: 12, padding: 14, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                        <div>
                          <div style={{ color: "#fff", fontWeight: 700 }}>{user.email}</div>
                          <div style={{ color: "#666", fontSize: 11 }}>{new Date(user.created_at).toLocaleString()}</div>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <Tag color="#7c3aed18" text="#a78bfa">{user.role.toUpperCase()}</Tag>
                          <Tag color="#4ade8018" text="#4ade80">{user.status.toUpperCase()}</Tag>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <Select value={user.role} disabled={user.id === currentUser?.id} onChange={(e) => updateTenantUser(user, { role: e.target.value })} style={{ width: 150, padding: "8px 10px", fontSize: 12 }}>
                            <option value="standard_user">Standard</option>
                            <option value="tenant_operator">Operator</option>
                            <option value="tenant_viewer">Viewer</option>
                            <option value="tenant_admin">Admin</option>
                          </Select>
                          {user.status === "disabled" ? (
                            <GhostButton onClick={() => updateTenantUser(user, { status: "active" })} style={{ padding: "8px 10px" }}>Reactivate</GhostButton>
                          ) : (
                            <GhostButton onClick={() => updateTenantUser(user, { status: "disabled" })} disabled={user.id === currentUser?.id} style={{ padding: "8px 10px" }}>Disable</GhostButton>
                          )}
                          <GhostButton onClick={() => deleteTenantUser(user)} disabled={user.id === currentUser?.id} style={{ padding: "8px 10px", color: "#ef4444", borderColor: "#ef444433" }}>Delete</GhostButton>
                        </div>
                      </div>
                    ))}
                  </div>
                </Panel>

                <Panel style={{ padding: 22 }}>
                  <div style={{ fontSize: 10, color: "#555", letterSpacing: 2, marginBottom: 12 }}>UPLOAD ATTEMPTS</div>
                  <div style={{ display: "grid", gap: 10 }}>
                    {uploadAttempts.map((attempt) => (
                      <div key={attempt.id} style={{ background: "#0b0b16", border: "1px solid #202034", borderRadius: 12, padding: 14, display: "flex", justifyContent: "space-between", gap: 10 }}>
                        <div>
                          <div style={{ color: "#fff", fontWeight: 700 }}>{attempt.account_name || attempt.platform}</div>
                          <div style={{ color: "#666", fontSize: 11 }}>{attempt.platform} • attempt {attempt.attempt_no} • {new Date(attempt.created_at).toLocaleString()}</div>
                          {attempt.error_message ? <div style={{ color: "#ef4444", fontSize: 11, marginTop: 5 }}>{attempt.error_code}: {attempt.error_message}</div> : null}
                        </div>
                        <Tag color={attempt.status === "posted" ? "#4ade8018" : "#ef444418"} text={attempt.status === "posted" ? "#4ade80" : "#ef4444"}>{attempt.status.toUpperCase()}</Tag>
                      </div>
                    ))}
                    {!uploadAttempts.length ? <div style={{ color: "#555" }}>No upload attempts recorded yet.</div> : null}
                  </div>
                </Panel>
              </div>
            </div>
          </div>
        ) : null}

        {["legal", "terms", "privacy", "rights", "data-deletion", "contact"].includes(tab) ? (
          <div style={{ display: "grid", gap: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 34, letterSpacing: 4, color: "#fff", margin: 0 }}>LEGAL</h1>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <GhostButton onClick={() => setTab("terms")}>TERMS</GhostButton>
                <GhostButton onClick={() => setTab("privacy")}>PRIVACY</GhostButton>
                <GhostButton onClick={() => setTab("rights")}>RIGHTS</GhostButton>
                <GhostButton onClick={() => setTab("data-deletion")}>DATA DELETION</GhostButton>
                <GhostButton onClick={() => setTab("contact")}>CONTACT</GhostButton>
              </div>
            </div>
            <Panel style={{ padding: 16 }}>
              <div style={{ color: "#777", fontSize: 11, lineHeight: 1.8 }}>
                Public review URLs: <span style={{ color: "#fff" }}>https://getclipse.com/terms</span>, <span style={{ color: "#fff" }}>https://getclipse.com/privacy</span>, <span style={{ color: "#fff" }}>https://getclipse.com/rights</span>, and <span style={{ color: "#fff" }}>https://getclipse.com/data-deletion</span>.
              </div>
            </Panel>
            <LegalPage page={tab === "legal" ? "terms" : tab} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function App() {
  consumeAuthHandoffFromHash();
  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const page = params.get("page");
  const pathname = typeof window !== "undefined" ? window.location.pathname.replace(/^\/+|\/+$/g, "") : "";
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const isMarketingHost = ["getclipse.com", "www.getclipse.com"].includes(hostname);
  const legalPages = ["privacy", "terms", "rights", "data-deletion", "contact"];
  const requestedLegalPage = legalPages.includes(page) ? page : legalPages.includes(pathname) ? pathname : "";
  const requestedAuthPage = ["login", "register", "signup"].includes(page) ? page : ["login", "register", "signup"].includes(pathname) ? pathname : "";

  if (requestedLegalPage) {
    return (
      <div style={{ minHeight: "100vh", background: "radial-gradient(circle at 20% 0%,rgba(139,92,246,0.18),transparent 32%),#070711", padding: "34px 20px", color: THEME.text }}>
        <div style={{ maxWidth: 980, margin: "0 auto 24px", display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
          <BrandLockup href={marketingHref("/")} variant="full" markSize={28} />
          <div style={{ display: "flex", gap: 10 }}>
            <GhostButton onClick={() => { window.location.href = marketingHref("/"); }}>Home</GhostButton>
            <PrimaryButton onClick={() => { window.location.href = IS_LOCALHOST ? "/?page=app" : appHref("/"); }}>Open App</PrimaryButton>
          </div>
        </div>
        <LegalPage page={requestedLegalPage} />
      </div>
    );
  }

  if (requestedAuthPage) {
    return (
      <DashboardApp
        initialTab="portal"
        initialAuthMode={requestedAuthPage === "login" ? "login" : "register"}
        authOnly
      />
    );
  }

  if (page === "landing" || (isMarketingHost && page !== "app")) {
    return <PublicLandingPage />;
  }

  const hasStoredAuth = typeof window !== "undefined" && Boolean(localStorage.getItem("clipse-backend-token") || localStorage.getItem("cliphive-backend-token") || localStorage.getItem("clipse-backend-refresh-token") || localStorage.getItem("cliphive-backend-refresh-token"));

  if (page === "app") {
    return hasStoredAuth ? <DashboardApp /> : <DashboardApp initialTab="portal" initialAuthMode="login" authOnly />;
  }

  if (page === "login") {
    return <DashboardApp initialTab="portal" initialAuthMode="login" authOnly />;
  }

  if (page === "register" || page === "signup") {
    return <DashboardApp initialTab="portal" initialAuthMode="register" authOnly />;
  }

  return <DashboardApp />;
}
